import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { curveMonotoneX } from '@visx/curve';
import { LineChart } from '../components/charts/line-chart';
import { ComposedChart } from '../components/charts/composed-chart';
import { BarChart } from '../components/charts/bar-chart';
import { Line } from '../components/charts/line';
import { Bar } from '../components/charts/bar';
import { SeriesBar } from '../components/charts/series-bar';
import { Grid } from '../components/charts/grid';
import { XAxis } from '../components/charts/x-axis';
import { YAxis } from '../components/charts/y-axis';
import { BarXAxis } from '../components/charts/bar-x-axis';
import { ChartTooltip } from '../components/charts/tooltip';
import { ChartLegend } from '../components/ReportsNative/charts/ChartLegend';
import {
  Calendar,
  RefreshCw,
  DollarSign,
  Hotel,
  TrendingUp,
  Percent,
  Users,
  Clock,
  Activity,
  TrendingDown,
  Download,
  Save,
  X,
  Maximize2,
  MapPin,
  ChevronDown,
  ChevronRight,
  Check,
  Sparkles
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useAllowedReportGroups } from '../hooks/useReportAccess';
import { KpiTile } from '../components/KpiTile/KpiTile';
import { CHART_COLORS } from '../styles/chartColors';
import { Card } from '../components/Card/Card';
import { DataTable } from '../components/DataTable/DataTable';
import PerformanceSummary from '../components/PerformanceSummary/PerformanceSummary';
import TonightPickup from '../components/Forecast/TonightPickup';
import ForecastBreakdown from '../components/Forecast/ForecastBreakdown';
import { DashboardSkeleton, MobileDashboardSkeleton } from '../components/SkeletonLoader/SkeletonLoader';
import { AlertModal } from '../components/AlertModal/AlertModal';
import { DateRangePicker } from '../components/DateRangePicker/DateRangePicker';
import { useUserSettingsContext } from '../context/UserSettingsContext';
import { useLoading } from '../context/LoadingContext';
import { useTheme } from '../context/ThemeContext';
import { api, saveDashboardPreferences, getDashboardPreferences } from '../utils/api';
import { MobileDashboard } from '../components/MobileDashboard/MobileDashboard';
// CLASSIC SHELL COPY of DashboardNew as of main (2026-09-16). Rendered when the
// user's layout preference is 'classic' so the old UI has no differences.
// Fix bugs in BOTH files until the classic shell is retired.
import { NightAuditAlerts } from '../components/NightAuditAlerts/NightAuditAlerts';
import DashboardDailyBriefing from '../components/DashboardDailyBriefing/DashboardDailyBriefing';
import NewLookPrompt, { NewLookChip } from '../components/NewLookPrompt/NewLookPrompt';
import useReportingCurrency from '../hooks/useReportingCurrency';
import {
  formatCurrency as sharedFormatCurrency,
  formatCurrencyRounded as sharedFormatCurrencyRounded,
  currencySymbol as sharedCurrencySymbol,
} from '../utils/currency';

const DASHBOARD_DATA_SOURCE = 'sql';
const DASHBOARD_SECTION_KEYS = ['revenue', 'operations', 'financial', 'forecast'];
const DASHBOARD_SECTION_PATHS = {
  revenue: '/dashboard/revenue',
  operations: '/dashboard/operations',
  financial: '/dashboard/financial',
  forecast: '/dashboard/forecast'
};
const DASHBOARD_QUICK_DATE_KEYS = ['yesterday', 'last7', 'last30', 'last60', 'last90', 'thisMonth', 'lastMonth'];
const DASHBOARD_AGGREGATION_KEYS = ['day', 'month', 'year'];
const FINANCIAL_QUICK_DATE_KEYS = ['lastMonth', 'ytd', 'trailing12'];
const FINANCIAL_COMPARISON_KEYS = ['budget', 'ly'];
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const getDashboardSectionFromPath = (pathname = '') => {
  const section = pathname.split('/').filter(Boolean)[1];
  return DASHBOARD_SECTION_KEYS.includes(section) ? section : 'revenue';
};

const isDashboardSectionRoute = (pathname = '') => {
  if (pathname === '/dashboard') return true;
  return DASHBOARD_SECTION_KEYS.some(section => pathname === DASHBOARD_SECTION_PATHS[section]);
};

const isIsoDateParam = (value) => typeof value === 'string' && ISO_DATE_PATTERN.test(value);

const rangesMatch = (left, right) => (
  left?.start === right?.start && left?.end === right?.end
);

const locationsMatch = (left = [], right = []) => (
  left.length === right.length && left.every((value, index) => String(value) === String(right[index]))
);

const parseLocationsParam = (value) => {
  if (value === 'none') return [];
  if (!value || value === 'all') return ['all'];
  const locationIds = value
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);
  return locationIds.length > 0 ? Array.from(new Set(locationIds)) : ['all'];
};

const serializeLocationsParam = (locations = []) => {
  if (locations.length === 0) return 'none';
  if (locations.length === 1 && locations[0] === 'all') return null;
  return locations.join(',');
};

// Export Performance Summary data to Excel
const exportToExcel = (data, filename = 'Performance_Summary', comparisonLabel = 'Benchmark') => {
  // Prepare data for Excel export
  const excelData = data.map(row => ({
    'Location': row.locationName,
    'Occupied Rooms': row.occupiedRooms,
    [`${comparisonLabel} Occupied Rooms`]: row.comparison?.occupiedRooms ?? '',
    'Comp Rooms': row.compRooms,
    [`${comparisonLabel} Comp Rooms`]: row.comparison?.compRooms ?? '',
    'Occupancy %': typeof row.occupancyRate === 'number' ? row.occupancyRate.toFixed(1) + '%' : row.occupancyRate,
    [`${comparisonLabel} Occupancy %`]: typeof row.comparison?.occupancyRate === 'number' ? row.comparison.occupancyRate.toFixed(1) + '%' : '',
    'ADR': typeof row.adr === 'number' ? row.adr.toFixed(2) : row.adr,
    [`${comparisonLabel} ADR`]: typeof row.comparison?.adr === 'number' ? row.comparison.adr.toFixed(2) : '',
    'RevPAR': typeof row.revPAR === 'number' ? row.revPAR.toFixed(2) : row.revPAR,
    [`${comparisonLabel} RevPAR`]: typeof row.comparison?.revPAR === 'number' ? row.comparison.revPAR.toFixed(2) : '',
    'Room Revenue': typeof row.roomRevenue === 'number' ? row.roomRevenue.toFixed(2) : row.roomRevenue,
    [`${comparisonLabel} Room Revenue`]: typeof row.comparison?.roomRevenue === 'number' ? row.comparison.roomRevenue.toFixed(2) : '',
    'Total Revenue': typeof row.totalRevenue === 'number' ? row.totalRevenue.toFixed(2) : row.totalRevenue,
    [`${comparisonLabel} Total Revenue`]: typeof row.comparison?.totalRevenue === 'number' ? row.comparison.totalRevenue.toFixed(2) : ''
  }));

  // Create workbook and worksheet
  const ws = XLSX.utils.json_to_sheet(excelData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Performance Summary');

  // Set column widths
  const colWidths = [
    { wch: 25 }, // Location
    { wch: 15 }, // Occupied Rooms
    { wch: 18 }, // Comparison Occupied Rooms
    { wch: 12 }, // Comp Rooms
    { wch: 18 }, // Comparison Comp Rooms
    { wch: 12 }, // Occupancy %
    { wch: 18 }, // Comparison Occupancy %
    { wch: 10 }, // ADR
    { wch: 14 }, // Comparison ADR
    { wch: 10 }, // RevPAR
    { wch: 14 }, // Comparison RevPAR
    { wch: 15 }, // Room Revenue
    { wch: 18 }, // Comparison Room Revenue
    { wch: 15 }, // Total Revenue
    { wch: 18 }  // Comparison Total Revenue
  ];
  ws['!cols'] = colWidths;

  // Generate filename with current date
  const today = new Date();
  const dateStr = today.toISOString().split('T')[0];
  const finalFilename = `${filename}_${dateStr}.xlsx`;

  // Download the file
  XLSX.writeFile(wb, finalFilename);
};

// Export Operations Performance Summary data to Excel
const exportOperationsToExcel = (data, filename = 'Operations_Performance_Summary') => {
  // Prepare data for Excel export
  const excelData = data.map(row => ({
    'Location': row.locationName,
    'Hours Worked': row.hoursWorked,
    'Budget Hours': row.budgetedHours,
    'HPOR': typeof row.hpor === 'number' ? row.hpor.toFixed(2) : row.hpor,
    'OT Hours': row.otHours,
    'OT %': typeof row.otPercentage === 'number' ? row.otPercentage.toFixed(1) + '%' : row.otPercentage,
    'Wages': typeof row.wages === 'number' ? row.wages.toFixed(2) : row.wages,
    '$POR': typeof row.dpor === 'number' ? row.dpor.toFixed(2) : row.dpor
  }));

  // Create workbook and worksheet
  const ws = XLSX.utils.json_to_sheet(excelData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Operations Performance');

  // Set column widths
  const colWidths = [
    { wch: 25 }, // Location
    { wch: 15 }, // Hours Worked
    { wch: 15 }, // Budget Hours
    { wch: 18 }, // HPOR
    { wch: 12 }, // OT Hours
    { wch: 10 }, // OT %
    { wch: 12 }, // Wages
    { wch: 15 }  // $POR
  ];
  ws['!cols'] = colWidths;

  // Generate filename with current date
  const today = new Date();
  const dateStr = today.toISOString().split('T')[0];
  const finalFilename = `${filename}_${dateStr}.xlsx`;

  // Download the file
  XLSX.writeFile(wb, finalFilename);
};

// Export Financial Performance Summary data to Excel
const exportFinancialToExcel = (data, filename = 'Financial_Performance_Summary', comparisonLabel = 'Budget') => {
  // Prepare data for Excel export
  const excelData = data.map(row => ({
    'Location': row.locationName,
    'Revenue': typeof row.revenue === 'number' ? row.revenue.toFixed(2) : row.revenue,
    [`${comparisonLabel} Revenue`]: typeof row.budgetRevenue === 'number' ? row.budgetRevenue.toFixed(2) : row.budgetRevenue,
    'Rev Variance': typeof row.revenueVariance === 'number' ? row.revenueVariance.toFixed(2) : row.revenueVariance,
    'Expenses': typeof row.expense === 'number' ? row.expense.toFixed(2) : row.expense,
    [`${comparisonLabel} Expenses`]: typeof row.budgetExpense === 'number' ? row.budgetExpense.toFixed(2) : row.budgetExpense,
    'Expense Variance': typeof row.expenseVariance === 'number' ? row.expenseVariance.toFixed(2) : row.expenseVariance,
    'Net Income': typeof row.netIncome === 'number' ? row.netIncome.toFixed(2) : row.netIncome,
    [`${comparisonLabel} Net Income`]: typeof row.budgetNetIncome === 'number' ? row.budgetNetIncome.toFixed(2) : row.budgetNetIncome,
    'Net Income Variance': typeof row.netIncomeVariance === 'number' ? row.netIncomeVariance.toFixed(2) : row.netIncomeVariance
  }));

  // Create workbook and worksheet
  const ws = XLSX.utils.json_to_sheet(excelData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Financial Performance');

  // Set column widths
  const colWidths = [
    { wch: 25 }, // Location
    { wch: 15 }, // Revenue
    { wch: 18 }, // Budget Revenue
    { wch: 15 }, // Rev Variance
    { wch: 15 }, // Expenses
    { wch: 18 }, // Budget Expenses
    { wch: 18 }, // Expense Variance
    { wch: 15 }, // Net Income
    { wch: 20 }, // Budget Net Income
    { wch: 20 }  // Net Income Variance
  ];
  ws['!cols'] = colWidths;

  // Generate filename with current date
  const today = new Date();
  const dateStr = today.toISOString().split('T')[0];
  const finalFilename = `${filename}_${dateStr}.xlsx`;

  // Download the file
  XLSX.writeFile(wb, finalFilename);
};

export default function DashboardClassic() {
  const { currentOrganization, userSettings, isLoading: isLoadingUserSettings } = useUserSettingsContext();
  const { startLoading, stopLoading } = useLoading();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dashboardAlertsEnabled = useFeatureFlagEnabled('dashboard-alerts');
  const nightAuditRiskEnabled = useFeatureFlagEnabled('night-audit-risk');
  const aiAssistantEnabled = useFeatureFlagEnabled('ai-assistant');
  const currentOrgIdRef = useRef(null);
  const selectedLocationsKeyRef = useRef('');
  const dateRangeKeyRef = useRef('');
  const comparisonModeRef = useRef('prior');
  const activeCurrencyRef = useRef('USD');
  const financialDateRangeKeyRef = useRef('');
  const financialComparisonModeRef = useRef('budget');
  const previousOrgIdRef = useRef(null);

  // DEBUG: Log currentOrganization whenever it changes
  useEffect(() => {

  }, [currentOrganization, isLoadingUserSettings]);

  // Navigation handlers for each section — tiles link to the native report pages.
  const handleNavigateToRevenue = () => navigate('/reports/revenue/overview');
  const handleNavigateToOperations = () => navigate('/reports/operations/labor');
  const handleNavigateToFinancial = () => navigate('/reports/financial/pnl');

  // Add CSS animations for pulse effects
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
      }
      @keyframes drawerIn {
        from { transform: translateX(40px); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes filterSpin { to { transform: rotate(360deg); } }

      /* ============================================================
         FILTER BAR — period · date · location · compare · refresh
         ============================================================ */
      .dashboard-filter-bar {
        background: var(--lodge-white);
        border: 1px solid var(--lodge-onyx-100);
        border-radius: 14px;
        padding: 10px 12px;
        margin-bottom: 18px;
        box-shadow: var(--shadow-xs);
      }
      .dashboard-filter-row {
        display: flex;
        align-items: center;
        gap: 12px;
        flex-wrap: wrap;
      }
      .dashboard-filter-row + .dashboard-filter-row {
        margin-top: 8px;
        padding-top: 10px;
        border-top: 1px dashed var(--lodge-onyx-100);
      }
      .dashboard-filter-spacer { flex: 1; min-width: 8px; }

      .dashboard-filter-save {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        border-radius: 8px;
        border: none;
        background: #ECFDF3;
        color: #2E7D32;
        cursor: pointer;
        transition: background 120ms, opacity 200ms;
        flex-shrink: 0;
      }
      .dashboard-filter-save:hover:not(:disabled) { background: #D1FADF; }
      .dashboard-filter-save:disabled { cursor: default; }

      .dashboard-filter-periods {
        display: inline-flex;
        background: var(--lodge-onyx-50);
        border-radius: 999px;
        padding: 3px;
        gap: 1px;
        flex-wrap: nowrap;
        max-width: 100%;
        overflow-x: auto;
      }
      .dashboard-filter-period {
        padding: 7px 14px;
        border: none;
        background: transparent;
        border-radius: 999px;
        font-family: var(--font-sans);
        font-size: 12.5px;
        font-weight: 600;
        color: var(--lodge-onyx-600);
        cursor: pointer;
        white-space: nowrap;
        transition: all 120ms;
      }
      .dashboard-filter-period:hover { color: var(--lodge-onyx-900); }
      .dashboard-filter-period.active {
        background: var(--lodge-orange-400);
        color: #fff;
        box-shadow: 0 1px 3px rgba(255, 122, 0, 0.30);
      }

      .dashboard-filter-date-pill {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        padding: 7px 12px;
        border-radius: 10px;
        background: var(--lodge-white);
        border: 1px solid var(--lodge-onyx-100);
        font-family: var(--font-sans);
        font-size: 12.5px;
        font-weight: 600;
        color: var(--lodge-onyx-900);
        cursor: pointer;
        transition: border-color 120ms;
      }
      .dashboard-filter-date-pill:hover { border-color: var(--lodge-onyx-200); }

      .dashboard-filter-location { position: relative; }
      .dashboard-filter-location-btn {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        padding: 7px 12px;
        border-radius: 10px;
        background: var(--lodge-white);
        border: 1px solid var(--lodge-onyx-100);
        font-family: var(--font-sans);
        font-size: 12.5px;
        font-weight: 600;
        color: var(--lodge-onyx-900);
        cursor: pointer;
        min-width: 160px;
        transition: border-color 120ms;
      }
      .dashboard-filter-location-btn:hover { border-color: var(--lodge-onyx-200); }
      .dashboard-filter-location-btn.open { border-color: var(--lodge-orange-400); }
      .dashboard-filter-location-btn > span.label {
        flex: 1;
        text-align: left;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .dashboard-filter-compare-label {
        font-family: var(--font-sans);
        font-size: 10.5px;
        font-weight: 700;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--lodge-onyx-500);
        padding-left: 4px;
      }
      .dashboard-filter-compare {
        display: inline-flex;
        border: 1px solid var(--lodge-onyx-100);
        border-radius: 10px;
        padding: 3px;
        background: var(--lodge-white);
        gap: 1px;
        flex-wrap: nowrap;
        overflow-x: auto;
      }
      .dashboard-filter-compare-btn {
        padding: 6px 14px;
        border: 1px solid transparent;
        background: transparent;
        border-radius: 7px;
        font-family: var(--font-sans);
        font-size: 12.5px;
        font-weight: 600;
        color: var(--lodge-onyx-600);
        cursor: pointer;
        white-space: nowrap;
        transition: all 120ms;
      }
      .dashboard-filter-compare-btn:hover:not(:disabled) { color: var(--lodge-onyx-900); }
      .dashboard-filter-compare-btn.active {
        background: rgba(255, 122, 0, 0.10);
        color: var(--lodge-orange-500);
        border: 1px solid rgba(255, 122, 0, 0.30);
        padding: 5px 13px;
      }
      .dashboard-filter-compare-btn:disabled {
        color: var(--lodge-onyx-300);
        cursor: not-allowed;
      }

      .dashboard-filter-context {
        font-family: var(--font-sans);
        font-size: 12px;
        color: var(--lodge-onyx-500);
        display: inline-flex;
        align-items: center;
        gap: 4px;
        min-width: 0;
        overflow: hidden;
        white-space: nowrap;
      }
      .dashboard-filter-context > .summary {
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .dashboard-filter-context b { color: var(--lodge-onyx-900); font-weight: 600; }
      .dashboard-filter-default-link {
        margin-left: 4px;
        font-family: var(--font-sans);
        font-size: 12px;
        color: var(--lodge-orange-500);
        font-weight: 600;
        cursor: pointer;
        background: none;
        border: none;
        padding: 0;
        white-space: nowrap;
      }
      .dashboard-filter-default-link:hover:not(:disabled) { text-decoration: underline; }
      .dashboard-filter-default-link:disabled { cursor: default; opacity: 0.7; }

      .dashboard-filter-refresh {
        background: var(--lodge-orange-400);
        color: #fff;
        border: none;
        padding: 8px 16px;
        border-radius: 10px;
        font-family: var(--font-sans);
        font-weight: 600;
        font-size: 12.5px;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        cursor: pointer;
        transition: all 120ms;
        box-shadow: 0 1px 3px rgba(255, 122, 0, 0.30);
        white-space: nowrap;
      }
      .dashboard-filter-refresh:hover { background: var(--lodge-orange-500); transform: translateY(-1px); }
      .dashboard-filter-refresh.is-loading svg { animation: filterSpin 700ms linear infinite; }

      /* Date range picker integration — keep visually consistent with date pill */
      .dashboard-filter-date-wrap {
        display: inline-flex;
        align-items: center;
      }
      .dashboard-filter-date-wrap .date-range-picker-input {
        padding: 7px 12px;
        border-radius: 10px;
        border: 1px solid var(--lodge-onyx-100);
        background: var(--lodge-white);
        font-family: var(--font-sans);
        font-size: 12.5px;
        font-weight: 600;
        color: var(--lodge-onyx-900);
        gap: 7px;
        min-height: 0;
        line-height: 1.2;
        min-width: 0;
        width: auto;
      }
      .dashboard-filter-date-wrap .date-range-picker-input:hover {
        border-color: var(--lodge-onyx-200);
      }
      .dashboard-filter-date-wrap .date-range-picker-input.open {
        border-color: var(--lodge-orange-400);
      }
      .dashboard-filter-date-wrap .date-range-picker-input .has-value,
      .dashboard-filter-date-wrap .date-range-picker-input .placeholder {
        font-family: var(--font-sans);
        font-size: 12.5px;
        font-weight: 600;
        color: var(--lodge-onyx-900);
      }
      .dashboard-filter-date-wrap .date-range-picker-input .placeholder {
        color: var(--lodge-onyx-500);
      }
      .dashboard-filter-date-wrap .date-range-picker-input .dropdown-arrow {
        font-size: 8px;
        color: var(--lodge-onyx-500);
      }

      /* Location dropdown menu (anchored to btn) */
      .dashboard-filter-location-menu {
        position: absolute;
        right: 0;
        top: calc(100% + 4px);
        background: var(--lodge-white);
        border: 1px solid var(--lodge-onyx-100);
        border-radius: 10px;
        box-shadow: 0 10px 30px -10px rgba(15, 15, 17, 0.20);
        min-width: 240px;
        max-height: 320px;
        overflow-y: auto;
        padding: 4px;
        z-index: 50;
      }
      .dashboard-filter-location-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 8px 10px;
        font-family: var(--font-sans);
        font-size: 12.5px;
        color: var(--lodge-onyx-800);
        cursor: pointer;
        border-radius: 6px;
        width: 100%;
        text-align: left;
        background: transparent;
        border: none;
        gap: 12px;
      }
      .dashboard-filter-location-item:hover { background: var(--lodge-onyx-50); }
      .dashboard-filter-location-item.active { color: var(--lodge-orange-500); font-weight: 600; }
      .dashboard-filter-location-item .label {
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        text-align: left;
      }
      .dashboard-filter-location-divider {
        height: 1px;
        background: var(--lodge-onyx-100);
        margin: 4px 0;
      }

      /* ---------- Floating filter bar (on scroll) ---------- */
      .dashboard-float-bar {
        position: fixed;
        left: 50%;
        bottom: 24px;
        transform: translate(-50%, 16px);
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 6px;
        background: var(--lodge-white);
        border: 1px solid var(--lodge-onyx-100);
        border-radius: 999px;
        box-shadow: 0 12px 36px -8px rgba(15, 15, 17, 0.20), 0 2px 6px rgba(15, 15, 17, 0.06);
        z-index: 1200;
        opacity: 0;
        pointer-events: none;
        transition: opacity 180ms ease, transform 200ms ease;
        max-width: calc(100vw - 48px);
      }
      .dashboard-float-bar.visible {
        opacity: 1;
        pointer-events: auto;
        transform: translate(-50%, 0);
      }
      .dashboard-float-save {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        border-radius: 999px;
        background: #ECFDF3;
        color: #2E7D32;
        cursor: pointer;
        transition: background 120ms, opacity 200ms;
        flex-shrink: 0;
        border: none;
      }
      .dashboard-float-save:hover:not(:disabled) { background: #D1FADF; }
      .dashboard-float-save:disabled { cursor: default; }
      .dashboard-float-sep {
        width: 1px;
        height: 18px;
        background: var(--lodge-onyx-100);
        display: inline-block;
        flex-shrink: 0;
      }
      .dashboard-float-menu { position: relative; }
      .dashboard-float-menu-btn {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        padding: 7px 12px;
        border-radius: 999px;
        font-family: var(--font-sans);
        font-size: 12.5px;
        font-weight: 600;
        color: var(--lodge-onyx-900);
        cursor: pointer;
        background: transparent;
        border: none;
        transition: background 120ms;
        white-space: nowrap;
      }
      .dashboard-float-menu-btn:hover { background: var(--lodge-onyx-50); }
      .dashboard-float-menu-btn.open { background: var(--lodge-onyx-50); }
      .dashboard-float-menu-btn.accent {
        background: rgba(255, 122, 0, 0.10);
        color: var(--lodge-orange-500);
      }
      .dashboard-float-menu-btn.accent:hover { background: rgba(255, 122, 0, 0.16); }
      .dashboard-float-menu-label {
        max-width: 200px;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .dashboard-float-menu-pop {
        position: absolute;
        left: 50%;
        bottom: calc(100% + 8px);
        transform: translateX(-50%);
        background: var(--lodge-white);
        border: 1px solid var(--lodge-onyx-100);
        border-radius: 12px;
        box-shadow: 0 12px 30px -10px rgba(15, 15, 17, 0.25);
        min-width: 220px;
        max-height: 320px;
        overflow-y: auto;
        padding: 4px;
        z-index: 1300;
      }
      .dashboard-float-menu-pop.align-right {
        left: auto;
        right: 0;
        transform: none;
      }
      .dashboard-float-menu-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        padding: 8px 10px;
        font-family: var(--font-sans);
        font-size: 12.5px;
        color: var(--lodge-onyx-800);
        cursor: pointer;
        border-radius: 6px;
        text-align: left;
        background: transparent;
        border: none;
        gap: 12px;
      }
      .dashboard-float-menu-item:hover:not(:disabled) { background: var(--lodge-onyx-50); }
      .dashboard-float-menu-item.active { color: var(--lodge-orange-500); font-weight: 600; }
      .dashboard-float-menu-item:disabled {
        color: var(--lodge-onyx-300);
        cursor: not-allowed;
        opacity: 0.7;
      }
      .dashboard-float-menu-item .label {
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        text-align: left;
      }
      .dashboard-float-refresh {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 36px;
        height: 36px;
        border-radius: 999px;
        background: var(--lodge-orange-400);
        color: #fff;
        border: none;
        cursor: pointer;
        margin-left: 2px;
        flex-shrink: 0;
        box-shadow: 0 1px 3px rgba(255, 122, 0, 0.35);
        transition: background 120ms, transform 120ms;
      }
      .dashboard-float-refresh:hover { background: var(--lodge-orange-500); transform: translateY(-1px); }
      .dashboard-float-refresh.is-loading svg { animation: filterSpin 700ms linear infinite; }

      .dashboard-shell-head {
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        gap: 24px;
        flex-wrap: wrap;
        padding: 10px 4px 18px;
      }
      .dashboard-shell-title-row {
        display: flex;
        align-items: center;
        gap: 12px;
        flex-wrap: wrap;
      }
      .dashboard-shell-title {
        font-family: var(--font-sans);
        font-size: 30px;
        font-weight: 800;
        letter-spacing: -0.6px;
        color: var(--lodge-onyx-900);
        line-height: 1.05;
        margin: 0;
      }
      .dashboard-shell-fresh {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 4px 10px;
        border-radius: 999px;
        background: #ECFDF3;
        color: #027A48;
        font-family: var(--font-sans);
        font-size: 11px;
        font-weight: 700;
      }
      .dashboard-shell-fresh-dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: #12B76A;
        box-shadow: 0 0 0 2px rgba(18, 183, 106, 0.18);
      }
      .dashboard-shell-sub {
        margin-top: 6px;
        font-family: var(--font-sans);
        font-size: 13px;
        color: var(--lodge-onyx-500);
        font-weight: 500;
      }
      .dashboard-briefing-strip {
        display: inline-flex;
        align-items: center;
        gap: 12px;
        padding: 4px 4px 4px 14px;
        background: var(--lodge-white);
        border: 1px solid var(--lodge-onyx-100);
        border-radius: 999px;
        box-shadow: var(--shadow-xs);
        max-width: 100%;
        min-width: 0;
      }
      .dashboard-briefing-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 26px;
        height: 26px;
        border-radius: 8px;
        background: rgba(255, 122, 0, 0.10);
        color: var(--lodge-orange-500);
        flex-shrink: 0;
      }
      .dashboard-briefing-eyebrow {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        color: var(--lodge-onyx-400);
        font-family: var(--font-sans);
        font-size: 10px;
        font-weight: 800;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        white-space: nowrap;
        flex-shrink: 0;
      }
      .dashboard-briefing-headline {
        margin: 0;
        color: var(--lodge-onyx-900);
        font-family: var(--font-sans);
        font-size: 13px;
        line-height: 1.3;
        font-weight: 600;
        letter-spacing: -0.1px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        min-width: 0;
        max-width: 340px;
      }
      .dashboard-briefing-chip {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        padding: 4px 9px;
        border-radius: 999px;
        background: var(--lodge-onyx-50);
        border: 1px solid transparent;
        color: var(--lodge-onyx-700);
        font-family: var(--font-sans);
        font-size: 11px;
        font-weight: 700;
        white-space: nowrap;
        flex-shrink: 0;
      }
      .dashboard-briefing-chip.positive {
        background: var(--color-success-bg);
      }
      .dashboard-briefing-chip.positive span { color: var(--color-success-ink); }
      .dashboard-briefing-chip.warning {
        background: var(--color-warning-bg);
        border-color: transparent;
      }
      .dashboard-briefing-chip.warning span { color: var(--color-warning-ink); }
      .dashboard-briefing-cta {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 8px 14px;
        border-radius: 999px;
        border: none;
        background: var(--lodge-onyx-900);
        color: var(--lodge-onyx-50);
        font-family: var(--font-sans);
        font-size: 12px;
        font-weight: 700;
        cursor: pointer;
        white-space: nowrap;
        flex-shrink: 0;
        transition: background 120ms;
      }
      .dashboard-briefing-cta:hover { background: var(--lodge-onyx-700); }
      .dashboard-tabs-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        margin: 4px 0 24px;
        border-bottom: 1px solid var(--lodge-onyx-100);
      }
      .dashboard-tabs {
        display: inline-flex;
        gap: 2px;
      }
      .dashboard-tab {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 12px 16px 14px;
        border: none;
        border-bottom: 2px solid transparent;
        margin-bottom: -1px;
        background: transparent;
        color: var(--lodge-onyx-500);
        font-family: var(--font-sans);
        font-size: 13px;
        font-weight: 800;
        cursor: pointer;
        transition: color 120ms var(--easing-standard), border-color 120ms var(--easing-standard);
      }
      .dashboard-tab.active {
        color: var(--lodge-onyx-900);
        border-bottom-color: var(--lodge-orange-400);
      }
      .dashboard-tabs-actions {
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }
      .dashboard-tabs-action {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        min-height: 34px;
        padding: 7px 10px;
        border: none;
        background: transparent;
        color: var(--lodge-onyx-600);
        font-family: var(--font-sans);
        font-size: 12px;
        font-weight: 800;
        cursor: pointer;
      }
      .dashboard-drawer-scrim {
        position: fixed;
        inset: 0;
        background: rgba(15, 15, 17, 0.35);
        z-index: 9998;
        display: flex;
        justify-content: flex-end;
        animation: fadeIn 180ms ease-out both;
      }
      .dashboard-briefing-drawer {
        width: min(880px, 92vw);
        height: 100vh;
        background: var(--lodge-white);
        border-left: 1px solid var(--lodge-onyx-100);
        box-shadow: -16px 0 48px -16px rgba(15, 15, 17, 0.2);
        display: flex;
        flex-direction: column;
        animation: drawerIn 280ms cubic-bezier(.2,.7,.2,1) both;
      }
      .dashboard-briefing-drawer-head {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 16px;
        padding: 20px 24px 16px;
        border-bottom: 1px solid var(--lodge-onyx-100);
      }
      .dashboard-briefing-drawer-title {
        margin: 0;
        color: var(--lodge-onyx-900);
        font-family: var(--font-sans);
        font-size: 22px;
        font-weight: 800;
        letter-spacing: -0.4px;
      }
      .dashboard-briefing-drawer-body {
        flex: 1;
        overflow: auto;
        padding: 20px 24px 40px;
      }
      @media (max-width: 1200px) {
        .dashboard-briefing-strip .dashboard-briefing-headline {
          max-width: 200px;
        }
      }
      @media (max-width: 980px) {
        .dashboard-briefing-strip .dashboard-briefing-headline,
        .dashboard-briefing-strip .dashboard-briefing-eyebrow {
          display: none;
        }
      }
      @media (max-width: 1100px) {
        .dashboard-tabs-row {
          align-items: flex-start;
          flex-direction: column;
        }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // State for dashboard data (Revenue & Operations)
  const [dashboardData, setDashboardData] = useState(null);
  const [calculatedMetrics, setCalculatedMetrics] = useState(null);
  const [comparisonData, setComparisonData] = useState(null); // Prior period comparison data (used internally)
  const [comparisonMetrics, setComparisonMetrics] = useState(null);
  const [isLoadingComparison, setIsLoadingComparison] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [noDataAvailable, setNoDataAvailable] = useState(false);

  // State for financial data (separate from dashboard)
  const [financialData, setFinancialData] = useState(null);
  const [financialComparisonData, setFinancialComparisonData] = useState(null);
  const [, setFinancialError] = useState(null);
  const [, setFinancialLastUpdated] = useState(null);
  const [, setFinancialNoDataAvailable] = useState(false);

  // State for forecasting data (always fetches current month)
  const [forecastingData, setForecastingData] = useState(null);
  const [forecastingComparisonData, setForecastingComparisonData] = useState(null);
  const [forecastingError, setForecastingError] = useState(null);
  const [, setForecastingLastUpdated] = useState(null);
  const [selectedLocations, setSelectedLocations] = useState(['all']);
  const [availableLocations, setAvailableLocations] = useState([]);
  // locationId -> Location.currency. Populated alongside locationNames from
  // the locations API so the reporting-currency hook can resolve a currency
  // for each location regardless of where availableLocations was derived from
  // (dashboardData byDate items only carry locationId).
  const [locationCurrencies, setLocationCurrencies] = useState({});
  const [configuredCurrencyPairs, setConfiguredCurrencyPairs] = useState([]);
  // True once /locations has resolved (with data or empty). Gates the skeleton
  // so the first paint already knows the per-location currency, preventing a
  // USD→JPY flicker on first render.
  const [locationsApiResolved, setLocationsApiResolved] = useState(false);
  // Resolve source currencies from the active location selection. The dashboard
  // now requests a server-side target currency for converted portfolio totals.
  const locationsForCurrency = useMemo(
    () => (availableLocations || []).map((entry) => {
      const id = typeof entry === 'object' && entry !== null ? entry.id : entry;
      return { id, currency: locationCurrencies[id] || null };
    }),
    [availableLocations, locationCurrencies]
  );
  const reportingCurrency = useReportingCurrency(locationsForCurrency, selectedLocations);
  const orgDefaultCurrency = currentOrganization?.reportingCurrency || 'USD';
  const [dashboardTargetCurrency, setDashboardTargetCurrency] = useState(orgDefaultCurrency);
  const normalizeDashboardCurrency = useCallback((currency) => {
    if (!currency || typeof currency !== 'string') return null;
    const upper = currency.trim().toUpperCase();
    return /^[A-Z]{3}$/.test(upper) ? upper : null;
  }, []);
  const activeCurrency = normalizeDashboardCurrency(dashboardTargetCurrency) || orgDefaultCurrency;
  // Scope-aware default currency: a single-location user (their RLS-scoped
  // accessible-location list resolves to exactly one entry) defaults to that
  // location's currency; everyone else to the org reporting currency. A saved
  // preference or an explicit in-session pick (currencyUserPinnedRef) wins.
  const singleLocationScopeCurrency = useMemo(() => {
    if (!locationsApiResolved) return null;
    if (locationsForCurrency.length !== 1) return null;
    return normalizeDashboardCurrency(locationsForCurrency[0]?.currency);
  }, [locationsApiResolved, locationsForCurrency, normalizeDashboardCurrency]);
  const scopeDefaultCurrency = singleLocationScopeCurrency || orgDefaultCurrency;
  const currencyUserPinnedRef = useRef(false);
  const dashboardCurrencyOptions = useMemo(() => {
    const set = new Set();
    const add = (currency) => {
      const normalized = normalizeDashboardCurrency(currency);
      if (normalized) set.add(normalized);
    };

    add(orgDefaultCurrency);
    add(scopeDefaultCurrency);
    configuredCurrencyPairs
      .filter((pair) => pair?.isActive !== false)
      .forEach((pair) => {
        add(pair.fromCurrency);
        add(pair.toCurrency);
      });

    return Array.from(set).sort();
  }, [configuredCurrencyPairs, normalizeDashboardCurrency, orgDefaultCurrency, scopeDefaultCurrency]);
  const showDashboardCurrencySelector = dashboardCurrencyOptions.length > 1;
  const [locationNames, setLocationNames] = useState({});
  const [timeAggregation, setTimeAggregation] = useState('day');
  const [selectedQuickDate, setSelectedQuickDate] = useState('yesterday');
  const [savedDefaultQuickDate, setSavedDefaultQuickDate] = useState('yesterday'); // User's saved default
  const [selectedComparisonMode, setSelectedComparisonMode] = useState('prior');
  const [savedComparisonDefaults, setSavedComparisonDefaults] = useState({});
  const [isDashboardDockVisible, setIsDashboardDockVisible] = useState(false);
  const dashboardDockRef = useRef(null);
  const [performanceDeltaMode, setPerformanceDeltaMode] = useState('absolute');
  const [performanceTooltip, setPerformanceTooltip] = useState(null);
  const performanceTooltipTimerRef = useRef(null);
  const pendingPerformanceTooltipRef = useRef(null);
  const clearPerformanceTooltip = useCallback(() => {
    if (performanceTooltipTimerRef.current) {
      clearTimeout(performanceTooltipTimerRef.current);
      performanceTooltipTimerRef.current = null;
    }
    pendingPerformanceTooltipRef.current = null;
    setPerformanceTooltip(null);
  }, []);
  const [isSavingPreference, setIsSavingPreference] = useState(false);
  const [isSavingComparisonPreference, setIsSavingComparisonPreference] = useState(false);
  const [comparisonSaveSuccess, setComparisonSaveSuccess] = useState(false);
  const [dismissedSavePrompt, setDismissedSavePrompt] = useState(false); // User dismissed the save prompt
  const [preferencesLoaded, setPreferencesLoaded] = useState(false); // Track if user preferences have been loaded
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false); // Track if initial data fetch has completed
  const [expandedTable, setExpandedTable] = useState(null); // 'revenue' | 'operations' | 'financial' | null
  const [showDashboardPeriodMenu, setShowDashboardPeriodMenu] = useState(false);
  const [showDashboardComparisonMenu, setShowDashboardComparisonMenu] = useState(false);
  const [activeDashboardTab, setActiveDashboardTab] = useState(() => getDashboardSectionFromPath(window.location.pathname));
  const [isBriefingDrawerOpen, setIsBriefingDrawerOpen] = useState(false);

  // Report-group grants gate the dashboard sections the same way they gate
  // the report nav: Revenue → 'revenue', Operations → 'operations',
  // Financial → 'financial', and Forecast (forward-looking revenue) rides
  // the 'revenue' permission. Resolved by the same service that 403s the
  // report data, so the tabs always agree with enforcement.
  const allowedReportGroups = useAllowedReportGroups();
  const canSeeDashboardSection = useCallback(
    (tabKey) => {
      if (!Array.isArray(allowedReportGroups)) return true; // unrestricted / loading
      const group = tabKey === 'forecast' ? 'revenue' : tabKey;
      return allowedReportGroups.includes(group);
    },
    [allowedReportGroups]
  );
  // If the current tab (deep link, saved path) is not permitted, land on the
  // first permitted one.
  useEffect(() => {
    if (canSeeDashboardSection(activeDashboardTab)) return;
    const fallback = DASHBOARD_SECTION_KEYS.find((key) => canSeeDashboardSection(key));
    if (fallback) setActiveDashboardTab(fallback);
  }, [canSeeDashboardSection, activeDashboardTab]);

  // Lock body scroll and handle Escape key when expanded table overlay is open
  useEffect(() => {
    if (expandedTable) {
      document.body.style.overflow = 'hidden';
      const handleEsc = (e) => { if (e.key === 'Escape') setExpandedTable(null); };
      document.addEventListener('keydown', handleEsc);
      return () => {
        document.body.style.overflow = '';
        document.removeEventListener('keydown', handleEsc);
      };
    } else {
      document.body.style.overflow = '';
    }
  }, [expandedTable]);

  useEffect(() => {
    if (!isBriefingDrawerOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleEsc = (e) => {
      if (e.key === 'Escape') setIsBriefingDrawerOpen(false);
    };
    document.addEventListener('keydown', handleEsc);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isBriefingDrawerOpen]);

  useEffect(() => () => clearPerformanceTooltip(), [clearPerformanceTooltip]);

  useEffect(() => {
    window.addEventListener('scroll', clearPerformanceTooltip, true);
    window.addEventListener('resize', clearPerformanceTooltip);
    window.addEventListener('blur', clearPerformanceTooltip);

    return () => {
      window.removeEventListener('scroll', clearPerformanceTooltip, true);
      window.removeEventListener('resize', clearPerformanceTooltip);
      window.removeEventListener('blur', clearPerformanceTooltip);
    };
  }, [clearPerformanceTooltip]);

  useEffect(() => {
    clearPerformanceTooltip();
  }, [clearPerformanceTooltip, performanceDeltaMode, selectedComparisonMode, selectedQuickDate, selectedLocations, expandedTable]);

  const [saveSuccess, setSaveSuccess] = useState(false); // Track successful save for success message
  const [savedDateLabel, setSavedDateLabel] = useState(''); // Store the friendly label at save time
  const [saveFadingOut, setSaveFadingOut] = useState(false); // Track fade out animation
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [showFloatLocationDropdown, setShowFloatLocationDropdown] = useState(false);

  // Responsive viewport tracking for mobile-first design
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Responsive breakpoints matching mobile app design
  const isMobile = viewportWidth <= 768;
  // const isTablet = viewportWidth > 768 && viewportWidth <= 1024;
  // const isDesktop = viewportWidth > 1024;

  useEffect(() => {
    if (isMobile) {
      setIsDashboardDockVisible(false);
      return undefined;
    }

    let animationFrame = null;

    const updateDockPosition = () => {
      const anchor = dashboardDockRef.current;
      if (!anchor) return;
      // Show the floating filter bar once the main filter bar has scrolled off the top.
      const rect = anchor.getBoundingClientRect();
      setIsDashboardDockVisible(rect.bottom < 12);
    };

    const requestDockUpdate = () => {
      if (animationFrame) {
        window.cancelAnimationFrame(animationFrame);
      }
      animationFrame = window.requestAnimationFrame(updateDockPosition);
    };

    requestDockUpdate();
    window.addEventListener('scroll', requestDockUpdate, { passive: true });
    window.addEventListener('resize', requestDockUpdate);

    return () => {
      if (animationFrame) {
        window.cancelAnimationFrame(animationFrame);
      }
      window.removeEventListener('scroll', requestDockUpdate);
      window.removeEventListener('resize', requestDockUpdate);
    };
  }, [isMobile]);

  // Alert modal state
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [currentAlertMetric, setCurrentAlertMetric] = useState(null);

  // Helper function to format date to YYYY-MM-DD in local timezone
  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const shiftIsoDateByYears = (isoDate, years) => {
    const [year, month, day] = String(isoDate || '').slice(0, 10).split('-').map(Number);
    if (!year || !month || !day) return isoDate;
    const targetYear = year + years;
    const targetMonthLastDay = new Date(targetYear, month, 0).getDate();
    return formatLocalDate(new Date(targetYear, month - 1, Math.min(day, targetMonthLastDay)));
  };

  const getFinancialAvailableThroughDate = (financialCloseDay, today = new Date()) => {
    const parsedCloseDay = Number(financialCloseDay);
    const closeDay = financialCloseDay === null || financialCloseDay === undefined || financialCloseDay === ''
      ? 15
      : Number.isFinite(parsedCloseDay)
        ? parsedCloseDay
        : 15;

    if (closeDay <= 0) {
      return new Date(today.getFullYear(), today.getMonth(), today.getDate());
    }

    if (today.getDate() < closeDay) {
      return new Date(today.getFullYear(), today.getMonth() - 1, 0);
    }

    return new Date(today.getFullYear(), today.getMonth(), 0);
  };

  // Financial section specific date state
  const [financialDateRange, setFinancialDateRange] = useState(() => {
    const today = new Date();
    const availableThrough = getFinancialAvailableThroughDate(currentOrganization?.financialCloseDay, today);
    return {
      start: formatLocalDate(new Date(availableThrough.getFullYear(), 0, 1)),
      end: formatLocalDate(availableThrough)
    };
  });
  const [selectedFinancialQuickDate, setSelectedFinancialQuickDate] = useState('ytd');
  const [selectedFinancialComparisonMode, setSelectedFinancialComparisonMode] = useState('budget');

  // Forecast tab — forward-looking period + pace/budget comparison.
  const [selectedForecastQuickDate, setSelectedForecastQuickDate] = useState('thisMonth');
  const [selectedForecastComparisonMode, setSelectedForecastComparisonMode] = useState('lastYear');

  // Derive the forecast window (days to show ahead) from the selected period.
  const forecastWindow = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const addDays = (n) => {
      const d = new Date(today);
      d.setDate(d.getDate() + n);
      return d;
    };
    switch (selectedForecastQuickDate) {
      case 'next30':   return { start: today, end: addDays(30), nights: 30 };
      case 'next60':   return { start: today, end: addDays(60), nights: 60 };
      case 'next90':   return { start: today, end: addDays(90), nights: 90 };
      case 'thisYear': {
        const yearEnd = new Date(today.getFullYear(), 11, 31);
        return {
          start: new Date(today.getFullYear(), 0, 1),
          end: yearEnd,
          nights: Math.max(1, Math.ceil((yearEnd - today) / 86_400_000)),
        };
      }
      case 'thisMonth':
      default: {
        const eom = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        return {
          start: today,
          end: eom,
          nights: Math.max(1, Math.ceil((eom - today) / 86_400_000) + 1),
        };
      }
    }
  }, [selectedForecastQuickDate]);
  const forecastReferenceDate = formatLocalDate(new Date());

  // Date range state - default to last 7 days
  const [dateRange, setDateRange] = useState(() => {
    // Default when the user has no saved dashboardPreferences.defaultQuickDateRange:
    // yesterday (must agree with the 'yesterday' selectedQuickDate initial state).
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    return {
      start: formatLocalDate(yesterday),
      end: formatLocalDate(yesterday)
    };
  });

  currentOrgIdRef.current = currentOrganization?.id || null;
  selectedLocationsKeyRef.current = selectedLocations.join(',');
  dateRangeKeyRef.current = `${dateRange.start}|${dateRange.end}`;
  comparisonModeRef.current = selectedComparisonMode;
  activeCurrencyRef.current = activeCurrency;
  financialDateRangeKeyRef.current = `${financialDateRange.start}|${financialDateRange.end}`;
  financialComparisonModeRef.current = selectedFinancialComparisonMode;

  useEffect(() => {
    const previousOrgId = previousOrgIdRef.current;
    const nextOrgId = currentOrganization?.id || null;
    if (previousOrgId !== null && previousOrgId !== nextOrgId) {
      setSelectedLocations(['all']);
      setAvailableLocations([]);
      setLocationNames({});
      setLocationCurrencies({});
      setLocationsApiResolved(false);
      setDashboardData(null);
      setFinancialData(null);
      setFinancialComparisonData(null);
      setForecastingData(null);
      setForecastingComparisonData(null);
      setCalculatedMetrics(null);
      setComparisonMetrics(null);
      setNoDataAvailable(false);
      setFinancialNoDataAvailable(false);
      setHasInitiallyLoaded(false);
    }
    previousOrgIdRef.current = nextOrgId;
  }, [currentOrganization?.id]);

  useEffect(() => {
    applyDashboardUrlState();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, currentOrganization?.id]);

  // Load saved dashboard preferences (default date range) on mount
  // This must complete BEFORE dashboard data is fetched to avoid double-loading
  useEffect(() => {
    const loadDashboardPreferences = async () => {
      // If no user email yet, wait for it (don't mark as loaded)
      if (!userSettings?.email) {
        // If userSettings is loaded but no email, mark preferences as loaded with defaults
        if (userSettings && !userSettings.email) {
          setPreferencesLoaded(true);
        }
        return;
      }

      try {
        const preferences = await getDashboardPreferences(userSettings.email);
        const comparisonDefaults = preferences?.comparisonDefaultsByPeriod || {};
        setSavedComparisonDefaults(comparisonDefaults);
        const savedTargetCurrency = preferences?.targetCurrencyByOrg?.[currentOrganization?.id];
        if (savedTargetCurrency) {
          setDashboardTargetCurrency(savedTargetCurrency);
          currencyUserPinnedRef.current = true; // explicit saved choice wins over the scope default
        } else {
          setDashboardTargetCurrency(currentOrganization?.reportingCurrency || 'USD');
        }

        if (urlHasDashboardFilterState()) {
          setPreferencesLoaded(true);
          return;
        }

        if (preferences?.defaultQuickDateRange) {
          const savedOption = preferences.defaultQuickDateRange;
          setSavedDefaultQuickDate(savedOption);
          // Apply saved preference as the initial selection
          setSelectedQuickDate(savedOption);
          setSelectedComparisonMode(getPreferredComparisonMode(savedOption, comparisonDefaults));
          // Also update the date range based on saved preference
          const newRange = getQuickDateRange(savedOption);
          if (newRange) {
            setDateRange(newRange);
          }
        } else {
          setSelectedComparisonMode(getPreferredComparisonMode(selectedQuickDate, comparisonDefaults));
        }
      } catch (error) {
        console.error('Error loading dashboard preferences:', error);
        // Keep default if loading fails
      } finally {
        // Mark preferences as loaded (success or failure) so dashboard can fetch
        setPreferencesLoaded(true);
      }
    };

    loadDashboardPreferences();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSettings?.email, userSettings, currentOrganization?.id, currentOrganization?.reportingCurrency]);

  const handleDashboardTargetCurrencyChange = async (currency) => {
    currencyUserPinnedRef.current = true; // explicit pick — stop auto-defaulting
    setDashboardTargetCurrency(currency);
    if (!userSettings?.email || !currentOrganization?.id) return;
    try {
      const preferences = await getDashboardPreferences(userSettings.email);
      await saveDashboardPreferences(userSettings.email, {
        targetCurrencyByOrg: {
          ...(preferences?.targetCurrencyByOrg || {}),
          [currentOrganization.id]: currency
        }
      });
    } catch (error) {
      console.error('Error saving dashboard currency preference:', error);
    }
  };

  useEffect(() => {
    if (!currentOrganization?.id) {
      setConfiguredCurrencyPairs([]);
      return;
    }

    let cancelled = false;
    api.get(`/currencyPairs?orgId=${currentOrganization.id}`)
      .then((response) => {
        if (cancelled) return;
        setConfiguredCurrencyPairs(Array.isArray(response.data) ? response.data : []);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Error loading dashboard currency pairs:', error);
        setConfiguredCurrencyPairs([]);
      });

    return () => {
      cancelled = true;
    };
  }, [currentOrganization?.id]);

  useEffect(() => {
    if (!dashboardCurrencyOptions.length) return;
    if (!dashboardCurrencyOptions.includes(activeCurrency)) {
      setDashboardTargetCurrency(
        dashboardCurrencyOptions.includes(orgDefaultCurrency)
          ? orgDefaultCurrency
          : dashboardCurrencyOptions[0]
      );
    }
  }, [activeCurrency, dashboardCurrencyOptions, orgDefaultCurrency]);

  // Re-evaluate the scope default when the org changes (a pinned choice from a
  // previous org must not carry over). Snap to the new org's default currency
  // immediately — waiting for the async preferences load left the previous
  // org's currency on the first data request while labels already showed the
  // new org's, so converted numbers rendered under the wrong symbol. A saved
  // per-org preference still overrides once preferences resolve.
  useEffect(() => {
    currencyUserPinnedRef.current = false;
    setDashboardTargetCurrency(currentOrganization?.reportingCurrency || 'USD');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrganization?.id]);

  // Apply the single-location scope default once locations resolve, unless the
  // user has a saved preference or made an explicit in-session pick.
  useEffect(() => {
    if (currencyUserPinnedRef.current) return;
    if (!locationsApiResolved || !preferencesLoaded) return;
    if (!singleLocationScopeCurrency) return;
    setDashboardTargetCurrency(singleLocationScopeCurrency);
  }, [locationsApiResolved, preferencesLoaded, singleLocationScopeCurrency]);

  // Fetch location names from the locations API endpoint
  const fetchLocationNames = useCallback(async () => {
    if (!currentOrganization?.id) return;

    try {

      const response = await api.get(`/locations?orgId=${currentOrganization.id}`);

      if (response.data && response.data.length > 0) {
        // Create a mapping of location ID to location name from API response
        const nameMapping = {};
        const currencyMapping = {};
        response.data.forEach(location => {
          nameMapping[location.id] = getLocationDisplayName(location.id, location.name);
          if (location.currency) currencyMapping[location.id] = location.currency;
        });
        setLocationNames(nameMapping);
        setLocationCurrencies(currencyMapping);

      } else {

        setLocationNames({});
        setLocationCurrencies({});
      }
    } catch (error) {
      console.error('Error fetching location names:', error);
      // Fallback to empty mapping
      setLocationNames({});
      setLocationCurrencies({});
    } finally {
      setLocationsApiResolved(true);
    }
  }, [currentOrganization?.id]);

  // Fetch dashboard data (Revenue & Operations sections only)
  const fetchDashboardData = async () => {

    if (!currentOrganization?.id) {
      console.error('❌ [fetchDashboardData] No organization ID, exiting');
      return;
    }

    if (DASHBOARD_DATA_SOURCE !== 'sql' && !currentOrganization?.workspaceId) {
      console.error('❌ [fetchDashboardData] No workspaceId, showing error');
      setError('Organization workspace ID is not configured. Please contact support.');
      return;
    }

    try {
      startLoading();
      setError(null);
      setIsLoadingComparison(true); // Set early to keep skeleton visible until comparison loads
      const requestOrgId = currentOrganization.id;
      const requestLocationsKey = selectedLocations.join(',');
      const requestDateRangeKey = `${dateRange.start}|${dateRange.end}`;
      const requestComparisonMode = selectedComparisonMode;
      const requestCurrency = activeCurrency;

      const requestData = {
        organizationId: requestOrgId,
        workspaceId: currentOrganization.workspaceId,
        startDate: dateRange.start,
        endDate: dateRange.end,
        targetCurrency: requestCurrency,
        conversionMode: 'actual',
        dashboardDataSource: DASHBOARD_DATA_SOURCE,
        datasetName: 'CUST_DEV_Dataset'
      };

      // Add location filter if single location selected (API level filtering)
      // For multiple locations, we'll filter client-side
      if (selectedLocations.length === 1 && !selectedLocations.includes('all')) {
        requestData.locationId = parseInt(selectedLocations[0]);

      }

      const activeComparisonOption = getComparisonOptions(selectedQuickDate)
        .find(option => option.key === requestComparisonMode);
      const comparisonRange = activeComparisonOption?.type === 'historical'
        ? getComparisonRange(requestComparisonMode, dateRange.start, dateRange.end, selectedQuickDate)
        : null;

      // Build comparison request if the active benchmark needs another historical period.
      const comparisonRequestData = comparisonRange ? {
        ...requestData,
        startDate: comparisonRange.start,
        endDate: comparisonRange.end
      } : null;

      // Fetch current and comparison periods in parallel for faster loading
      const [response, comparisonResponse] = await Promise.all([
        api.post('/dashboardData', requestData),
        comparisonRequestData ? api.post('/dashboardData', comparisonRequestData).catch(err => {
          console.error('Error fetching comparison data:', err);
          return null;
        }) : Promise.resolve(null)
      ]);

      const result = response.data;

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch dashboard data');
      }

      if (
        currentOrgIdRef.current !== requestOrgId ||
        selectedLocationsKeyRef.current !== requestLocationsKey ||
        dateRangeKeyRef.current !== requestDateRangeKey ||
        comparisonModeRef.current !== requestComparisonMode ||
        activeCurrencyRef.current !== requestCurrency
      ) {
        return;
      }

      // Check if response has empty data
      const hasData = result.data.byDate && result.data.byDate.length > 0;
      setNoDataAvailable(!hasData);

      setDashboardData(result.data);

      // Only extract available locations if we're not filtering by specific locations
      // This prevents the location list from changing when locations are selected
      if (selectedLocations.includes('all') && hasData) {
        const locations = [...new Set(result.data.byDate
          .filter(item => item.locationId !== null && item.locationId !== undefined)
          .map(item => item.locationId))]
          .sort((a, b) => a - b);

        setAvailableLocations(locations);

      }

      // Debug: Log the first few data points to see date format
      if (hasData) {

      }

      // Calculate metrics using the provided helpers
      if (hasData) {

        const aggregated = calculateAggregatedMetrics(result.data.byDate);
        const dailyMetrics = getDailyCalculatedMetrics(result.data.byDate);

        setCalculatedMetrics({
          aggregated,
          daily: dailyMetrics,
          current: result.data.summary?.current || {},
          totals: result.data.totals || {}
        });
      } else {

        setCalculatedMetrics(null);
      }

      // Process comparison data (already fetched in parallel above)
      if (comparisonRange && comparisonResponse) {
        const comparisonResult = comparisonResponse.data;

        if (comparisonResult.success && comparisonResult.data.byDate && comparisonResult.data.byDate.length > 0) {
          setComparisonData(comparisonResult.data.byDate || []);

          const comparisonAggregated = calculateAggregatedMetrics(comparisonResult.data.byDate);
          setComparisonMetrics({
            aggregated: comparisonAggregated,
            daily: comparisonResult.data.byDate,
            label: comparisonRange.label,
            dateRange: `${comparisonRange.start} to ${comparisonRange.end}`,
            mode: requestComparisonMode
          });

        } else {
          setComparisonData(null);
          setComparisonMetrics(null);
        }
      } else if (activeComparisonOption?.type === 'budget' || activeComparisonOption?.type === 'forecast') {
        const aggregated = hasData ? calculateAggregatedMetrics(result.data.byDate) : null;
        setComparisonData(null);
        setComparisonMetrics(buildInlineComparisonMetrics(requestComparisonMode, aggregated));
      } else {
        // No comparison for this date range or fetch failed
        setComparisonData(null);
        setComparisonMetrics(null);
      }
      setIsLoadingComparison(false);

      setLastUpdated(new Date());

    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError(err.message);
      setIsLoadingComparison(false); // Ensure we don't get stuck on skeleton
    } finally {
      stopLoading();
    }
  };

  // Fetch forecasting data around today. The Forecast tab is forward-looking and
  // should not inherit the historical dashboard date filter.
  const fetchForecastingData = async () => {
    if (!currentOrganization?.id) return;

    if (DASHBOARD_DATA_SOURCE !== 'sql' && !currentOrganization?.workspaceId) {
      setForecastingError('Organization workspace ID is not configured. Please contact support.');
      return;
    }

    try {
      setForecastingError(null);
      const requestOrgId = currentOrganization.id;
      const requestLocationsKey = selectedLocations.join(',');
      const requestCurrency = activeCurrency;

      const parseDashboardDate = (value) => {
        if (!value || typeof value !== 'string') return new Date();
        const [year, month, day] = value.split('-').map(Number);
        if (!year || !month || !day) return new Date();
        return new Date(year, month - 1, day);
      };

      const anchorDate = parseDashboardDate(forecastReferenceDate);
      const firstDayOfMonth = new Date(anchorDate.getFullYear(), anchorDate.getMonth(), 1);
      const lastDayOfMonth = new Date(anchorDate.getFullYear(), anchorDate.getMonth() + 1, 0);
      const thirtyDaysOut = new Date(anchorDate);
      thirtyDaysOut.setDate(anchorDate.getDate() + 30);
      const rangeEnd = [lastDayOfMonth, thirtyDaysOut, forecastWindow?.end]
        .filter(Boolean)
        .reduce((latest, candidate) => (candidate > latest ? candidate : latest), lastDayOfMonth);

      const currentMonthRange = {
        start: formatLocalDate(firstDayOfMonth),
        end: formatLocalDate(rangeEnd)
      };

      const requestData = {
        organizationId: requestOrgId,
        workspaceId: currentOrganization.workspaceId,
        startDate: currentMonthRange.start,
        endDate: currentMonthRange.end,
        targetCurrency: requestCurrency,
        conversionMode: 'planning',
        dashboardDataSource: DASHBOARD_DATA_SOURCE,
        datasetName: 'CUST_DEV_Dataset'
      };

      // Add location filter if single location selected (API level filtering)
      if (selectedLocations.length === 1 && !selectedLocations.includes('all')) {
        requestData.locationId = parseInt(selectedLocations[0]);

      }

      const comparisonRequestData = {
        ...requestData,
        startDate: shiftIsoDateByYears(currentMonthRange.start, -1),
        endDate: shiftIsoDateByYears(currentMonthRange.end, -1)
      };

      const [response, comparisonResponse] = await Promise.all([
        api.post('/dashboardData', requestData),
        api.post('/dashboardData', comparisonRequestData).catch((comparisonErr) => {
          console.error('Error fetching forecast last-year comparison data:', comparisonErr);
          return null;
        })
      ]);

      const result = response.data;

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch forecasting data');
      }

      if (
        currentOrgIdRef.current !== requestOrgId ||
        selectedLocationsKeyRef.current !== requestLocationsKey ||
        activeCurrencyRef.current !== requestCurrency
      ) {
        return;
      }

      setForecastingData(result.data);
      if (comparisonResponse?.data?.success) {
        setForecastingComparisonData(comparisonResponse.data.data);
      } else {
        setForecastingComparisonData(null);
      }
      setForecastingLastUpdated(new Date());

    } catch (err) {
      console.error('Error fetching forecasting data:', err);
      setForecastingError(err.message);
    }
  };

  // Fetch financial data (Financial section only)
  const fetchFinancialData = async () => {
    if (!currentOrganization?.id) return;

    if (DASHBOARD_DATA_SOURCE !== 'sql' && !currentOrganization?.workspaceId) {
      setFinancialError('Organization workspace ID is not configured. Please contact support.');
      return;
    }

    try {
      setFinancialError(null);
      const requestOrgId = currentOrganization.id;
      const requestLocationsKey = selectedLocations.join(',');
      const requestFinancialDateRangeKey = `${financialDateRange.start}|${financialDateRange.end}`;
      const requestFinancialComparisonMode = selectedFinancialComparisonMode;
      const requestCurrency = activeCurrency;
      const comparisonRange = getFinancialComparisonDateRange(requestFinancialComparisonMode, financialDateRange);

      const requestData = {
        organizationId: requestOrgId,
        workspaceId: currentOrganization.workspaceId,
        startDate: financialDateRange.start,
        endDate: financialDateRange.end,
        targetCurrency: requestCurrency,
        conversionMode: 'planning',
        dashboardDataSource: DASHBOARD_DATA_SOURCE,
        datasetName: 'CUST_DEV_Dataset',
        metricScope: 'financial-monthly'
      };

      // Add location filter if single location selected (API level filtering)
      if (selectedLocations.length === 1 && !selectedLocations.includes('all')) {
        requestData.locationId = parseInt(selectedLocations[0]);

      }

      const comparisonRequestData = comparisonRange ? {
        ...requestData,
        startDate: comparisonRange.start,
        endDate: comparisonRange.end
      } : null;

      const [response, comparisonResponse] = await Promise.all([
        api.post('/dashboardData', requestData),
        comparisonRequestData ? api.post('/dashboardData', comparisonRequestData).catch(err => {
          console.error('Error fetching financial comparison data:', err);
          return null;
        }) : Promise.resolve(null)
      ]);

      const result = response.data;

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch financial data');
      }

      if (
        currentOrgIdRef.current !== requestOrgId ||
        selectedLocationsKeyRef.current !== requestLocationsKey ||
        financialDateRangeKeyRef.current !== requestFinancialDateRangeKey ||
        financialComparisonModeRef.current !== requestFinancialComparisonMode ||
        activeCurrencyRef.current !== requestCurrency
      ) {
        return;
      }

      // Check if response has empty data
      const hasData = result.data.byDate && result.data.byDate.length > 0;
      setFinancialNoDataAvailable(!hasData);

      setFinancialData(result.data);
      setFinancialComparisonData(comparisonResponse?.data?.success ? comparisonResponse.data.data : null);
      setFinancialLastUpdated(new Date());

    } catch (err) {
      console.error('Error fetching financial data:', err);
      setFinancialError(err.message);
      setFinancialNoDataAvailable(true);
    }
  };

  // Calculation helper functions (updated to include comp rooms)
  const calculateOccupancyRate = (occupiedRooms, availableRooms, compRooms = 0) => {
    if (!availableRooms || availableRooms === 0) return 0;
    const paidOccupiedRooms = Math.max(0, (occupiedRooms || 0) - (compRooms || 0));
    return (paidOccupiedRooms / availableRooms) * 100;
  };

  const calculateADR = (roomRevenue, occupiedRooms, compRooms = 0) => {
    const paidOccupiedRooms = Math.max(0, (occupiedRooms || 0) - (compRooms || 0));
    if (!paidOccupiedRooms || paidOccupiedRooms === 0) return 0;
    return roomRevenue / paidOccupiedRooms;
  };

  const calculateRevPAR = (roomRevenue, availableRooms) => {
    if (!availableRooms || availableRooms === 0) return 0;
    return roomRevenue / availableRooms;
  };

  const calculateAggregatedMetrics = (dataPoints) => {
    if (!dataPoints || dataPoints.length === 0) {
      return {
        occupancyRate: 0,
        adr: 0,
        revPAR: 0,
        totalRoomRevenue: 0,
        totalRevenue: 0,
        totalRoomRevenueBudget: 0,
        totalRevenueBudget: 0,
        totalBudgetOccupiedRooms: 0,
        totalBudgetAvailableRooms: 0,
        totalOccupiedRooms: 0,
        totalAvailableRooms: 0,
        totalForecastRevenue: 0,
        totalRmsForecast: 0
      };
    }

    // Filter out subtotals and grand totals - only use actual daily data from real locations
    let dailyData = dataPoints.filter(point =>
      point.date !== null &&           // Has actual date (not subtotal)
      point.locationId !== null &&     // Has actual location ID (not grand total)
      point.locationId !== undefined   // Ensure location ID exists
    );

    // Apply location filtering if specific locations are selected
    // If no locations selected or "all" selected, show all data
    if (!selectedLocations.includes('all') && selectedLocations.length > 0) {
      const selectedLocationIds = selectedLocations.map(id => parseInt(id));
      dailyData = dailyData.filter(point => selectedLocationIds.includes(point.locationId));
    }

    // Debug: Log the filtered data to see what we're actually summing

    const totals = dailyData.reduce((acc, point) => {
      // Always include revenue and costs from all locations
      acc.roomRevenue += point.roomRevenue || 0;
      acc.totalRevenue += point.totalRevenue || 0;
      acc.roomRevenueBudget += point.roomRevenueBudget || 0;
      acc.totalRevenueBudget += point.totalRevenueBudget || 0;
      acc.budgetOccupiedRooms += point.budgetOccupiedRooms || 0;
      acc.budgetAvailableRooms += point.budgetAvailableRooms || 0;
      acc.otPremiumCost += point.otPremiumCost || 0;

      // Only include room counts if they are not null (some locations may not have room data)
      if (point.occupiedRooms !== null && point.occupiedRooms !== undefined) {
        acc.occupiedRooms += point.occupiedRooms;
      }
      if (point.availableRooms !== null && point.availableRooms !== undefined) {
        acc.availableRooms += point.availableRooms;
      }
      if (point.compRooms !== null && point.compRooms !== undefined) {
        acc.compRooms += point.compRooms;
      }

      // Operations metrics
      acc.hoursWorked += point.hoursWorked || 0;
      acc.wages += point.wages || 0;
      acc.otHours += point.otHours || 0;
      acc.budgetedHours += point.budgetedHours || 0;

      // Financial metrics
      acc.netIncome += point.netIncome || 0;
      acc.budgetNetIncome += point.budgetNetIncome || 0;
      acc.revenue += point.revenue || 0;
      acc.expense += point.expense || 0;
      acc.budgetExpense += point.budgetExpense || 0;
      acc.budgetRevenue += point.budgetRevenue || 0;
      acc.forecastRevenue += point.otbForecast || 0;
      acc.rmsForecast += point.rmsForecast || 0;

      return acc;
    }, {
      roomRevenue: 0,
      totalRevenue: 0,
      roomRevenueBudget: 0,
      totalRevenueBudget: 0,
      budgetOccupiedRooms: 0,
      budgetAvailableRooms: 0,
      occupiedRooms: 0,
      availableRooms: 0,
      otPremiumCost: 0,
      compRooms: 0,
      hoursWorked: 0,
      wages: 0,
      otHours: 0,
      budgetedHours: 0,
      netIncome: 0,
      budgetNetIncome: 0,
      revenue: 0,
      expense: 0,
      budgetExpense: 0,
      budgetRevenue: 0,
      forecastRevenue: 0,
      rmsForecast: 0
    });

    const paidOccupiedRooms = Math.max(0, totals.occupiedRooms - totals.compRooms);
    const budgetOccupancyRate = totals.budgetAvailableRooms > 0
      ? (totals.budgetOccupiedRooms / totals.budgetAvailableRooms) * 100
      : 0;

    return {
      occupancyRate: totals.availableRooms > 0 ? (paidOccupiedRooms / totals.availableRooms) * 100 : 0,
      adr: paidOccupiedRooms > 0 ? totals.roomRevenue / paidOccupiedRooms : 0,
      revPAR: totals.availableRooms > 0 ? totals.roomRevenue / totals.availableRooms : 0,
      totalRoomRevenue: totals.roomRevenue,
      totalRevenue: totals.totalRevenue,
      totalRoomRevenueBudget: totals.roomRevenueBudget,
      totalRevenueBudget: totals.totalRevenueBudget,
      totalBudgetOccupiedRooms: totals.budgetOccupiedRooms,
      totalBudgetAvailableRooms: totals.budgetAvailableRooms,
      budgetOccupancyRate,
      totalOccupiedRooms: totals.occupiedRooms,
      totalAvailableRooms: totals.availableRooms,
      totalOTPremiumCost: totals.otPremiumCost,
      totalCompRooms: totals.compRooms,
      paidOccupiedRooms: paidOccupiedRooms,
      // Operations metrics
      totalHoursWorked: totals.hoursWorked,
      totalWages: totals.wages,
      totalOTHours: totals.otHours,
      totalBudgetedHours: totals.budgetedHours,
      hoursPerOccupiedRoom: totals.occupiedRooms > 0 ? totals.hoursWorked / totals.occupiedRooms : 0,
      dollarPerOccupiedRoom: totals.occupiedRooms > 0 ? totals.wages / totals.occupiedRooms : 0,
      otPercentage: totals.hoursWorked > 0 ? (totals.otHours / totals.hoursWorked) * 100 : 0,
      // Financial metrics
      totalNetIncome: totals.netIncome,
      totalBudgetNetIncome: totals.budgetNetIncome,
      totalRevenueFin: totals.revenue,
      totalExpense: totals.expense,
      totalBudgetExpense: totals.budgetExpense,
      totalBudgetRevenue: totals.budgetRevenue,
      totalForecastRevenue: totals.forecastRevenue,
      totalRmsForecast: totals.rmsForecast
    };
  };

  // Helper to calculate percent change between current and prior values
  const calculatePercentChange = (current, prior) => {
    if (prior === 0 || prior === null || prior === undefined) {
      if (current === 0 || current === null || current === undefined) {
        return 0;
      }
      return 100; // If prior is 0 but current has value, show 100% increase
    }
    return ((current - prior) / prior) * 100;
  };

  // Helper to calculate absolute delta and format it
  const calculateAbsoluteDelta = (current, prior) => {
    return current - prior;
  };

  // Helper to format absolute delta based on metric type
  const formatAbsoluteDelta = (delta, metricType = 'currency') => {
    if (delta === 0 || delta === null || delta === undefined || isNaN(delta)) {
      return null;
    }

    const sign = delta > 0 ? '+' : '';

    switch (metricType) {
      case 'currency':
        return `${sign}${formatCurrency(Math.abs(delta))}`;
      case 'percentage':
        return `${sign}${delta.toFixed(1)}pp`; // pp = percentage points
      case 'number':
        return `${sign}${Math.abs(delta).toFixed(2)}`;
      default:
        return `${sign}${delta.toFixed(2)}`;
    }
  };

  // Helper to format comparison value for tooltip
  const formatComparisonValue = (value, metricType = 'currency') => {
    if (value === null || value === undefined || isNaN(value)) {
      return 'N/A';
    }

    switch (metricType) {
      case 'currency':
        return formatCurrency(value);
      case 'percentage':
        return formatPercentage(value);
      case 'number':
        return value.toFixed(2);
      default:
        return value.toString();
    }
  };

  const getDailyCalculatedMetrics = (dataPoints) => {
    // Filter out subtotals and grand totals - only get actual daily data from real locations
    let dailyData = dataPoints.filter(point =>
      point.date !== null &&           // Has actual date (not subtotal)
      point.locationId !== null &&     // Has actual location ID (not grand total)
      point.locationId !== undefined   // Ensure location ID exists
    );

    // Apply location filtering if specific locations are selected
    // If no locations selected or "all" selected, show all data
    if (!selectedLocations.includes('all') && selectedLocations.length > 0) {
      const selectedLocationIds = selectedLocations.map(id => parseInt(id));
      dailyData = dailyData.filter(point => selectedLocationIds.includes(point.locationId));
    }

    return dailyData.map(point => {
      // Parse date safely without timezone issues
      if (!point.date) {
        const paidOccupiedRooms = Math.max(0, (point.occupiedRooms || 0) - (point.compRooms || 0));
        const budgetOccupancyRate = point.budgetAvailableRooms > 0
          ? ((point.budgetOccupiedRooms || 0) / point.budgetAvailableRooms) * 100
          : 0;
        return {
          ...point,
          occupancyRate: calculateOccupancyRate(point.occupiedRooms, point.availableRooms, point.compRooms),
          budgetOccupancyRate,
          adr: calculateADR(point.roomRevenue, point.occupiedRooms, point.compRooms),
          revPAR: calculateRevPAR(point.roomRevenue, point.availableRooms),
          paidOccupiedRooms: paidOccupiedRooms,
          date: 'N/A'
        };
      }

      // Parse date more robustly to avoid timezone issues
      let safeDate;
      let dateStr = point.date;

      // If it's an ISO datetime, extract just the date part
      if (dateStr.includes('T')) {
        dateStr = dateStr.split('T')[0];
      }

      // Parse YYYY-MM-DD format manually to avoid timezone shifts
      if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, dayNum] = dateStr.split('-').map(num => parseInt(num));
        safeDate = new Date(year, month - 1, dayNum); // month is 0-indexed
      } else {
        // Fallback for other formats - try to create a date at noon UTC to avoid timezone issues
        safeDate = new Date(dateStr + 'T12:00:00.000Z');
      }

      const paidOccupiedRooms = Math.max(0, (point.occupiedRooms || 0) - (point.compRooms || 0));
      const budgetOccupancyRate = point.budgetAvailableRooms > 0
        ? ((point.budgetOccupiedRooms || 0) / point.budgetAvailableRooms) * 100
        : 0;

      return {
        ...point,
        occupancyRate: calculateOccupancyRate(point.occupiedRooms, point.availableRooms, point.compRooms),
        budgetOccupancyRate,
        adr: calculateADR(point.roomRevenue, point.occupiedRooms, point.compRooms),
        revPAR: calculateRevPAR(point.roomRevenue, point.availableRooms),
        paidOccupiedRooms: paidOccupiedRooms,
        date: safeDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      };
    });
  };

  // Fetch location names when organization changes
  useEffect(() => {
    if (currentOrganization?.id && !isLoadingUserSettings) {
      fetchLocationNames();
    }
  }, [currentOrganization?.id, isLoadingUserSettings, fetchLocationNames]);

  // Update financial date range when organization changes (in case financial close day changes)
  useEffect(() => {
    if (currentOrganization?.id && !isLoadingUserSettings) {
      // Update financial date range based on new close day
      const defaultRange = getFinancialQuickDateRange('ytd');
      setFinancialDateRange(defaultRange);
      setSelectedFinancialQuickDate('ytd');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrganization?.id, currentOrganization?.financialCloseDay, isLoadingUserSettings]);

  // Combined fetch for initial load and location changes - runs all fetches in parallel
  const fetchAllDashboardData = async () => {
    if (!currentOrganization?.id || !currentOrganization?.workspaceId) return;

    // Run all three fetches in parallel for faster initial load
    await Promise.all([
      fetchDashboardData(),
      fetchFinancialData(),
      fetchForecastingData()
    ]);
  };

  // Initial load: fetch all data in parallel once preferences are loaded
  useEffect(() => {
    if (currentOrganization?.id && !isLoadingUserSettings && preferencesLoaded) {
      fetchAllDashboardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrganization?.id, isLoadingUserSettings, preferencesLoaded, selectedLocations, activeCurrency]);

  // Re-fetch only dashboard data when date range or benchmark changes (not initial load)
  useEffect(() => {
    if (hasInitiallyLoaded && currentOrganization?.id && !isLoadingUserSettings && preferencesLoaded) {
      fetchDashboardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, selectedComparisonMode]);

  // Re-fetch forecast data when the forward-looking window changes.
  useEffect(() => {
    if (hasInitiallyLoaded && currentOrganization?.id && !isLoadingUserSettings && preferencesLoaded) {
      fetchForecastingData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedForecastQuickDate]);

  // Re-fetch only financial data when the range or benchmark changes (not initial load)
  useEffect(() => {
    if (hasInitiallyLoaded && currentOrganization?.id && !isLoadingUserSettings) {
      fetchFinancialData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [financialDateRange, selectedFinancialComparisonMode]);

  // Mark initial load complete after first fetch
  useEffect(() => {
    if (currentOrganization?.id && !isLoadingUserSettings && preferencesLoaded && !hasInitiallyLoaded) {
      setHasInitiallyLoaded(true);
    }
  }, [currentOrganization?.id, isLoadingUserSettings, preferencesLoaded, hasInitiallyLoaded]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showLocationDropdown && !event.target.closest('[data-location-dropdown]')) {
        setShowLocationDropdown(false);
      }
      if (showFloatLocationDropdown && !event.target.closest('[data-float-location-dropdown]')) {
        setShowFloatLocationDropdown(false);
      }
      if (showDashboardPeriodMenu && !event.target.closest('[data-dashboard-period-menu]')) {
        setShowDashboardPeriodMenu(false);
      }
      if (showDashboardComparisonMenu && !event.target.closest('[data-dashboard-comparison-menu]')) {
        setShowDashboardComparisonMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showLocationDropdown, showFloatLocationDropdown, showDashboardPeriodMenu, showDashboardComparisonMenu]);

  // Handle date range change
  const handleDateRangeChange = (newRange) => {

    setDateRange(newRange);
    setSelectedQuickDate(null); // Clear quick date selection when manually changing dates
    setSelectedComparisonMode(getPreferredComparisonMode(null));
  };

  // Financial date range options with close day logic
  const getFinancialQuickDateRange = (option) => {
    const today = new Date();
    const availableThrough = getFinancialAvailableThroughDate(currentOrganization?.financialCloseDay, today);

    switch (option) {
      case 'lastMonth':
        const firstOfLastMonth = new Date(availableThrough.getFullYear(), availableThrough.getMonth(), 1);
        return {
          start: formatLocalDate(firstOfLastMonth),
          end: formatLocalDate(availableThrough)
        };
      case 'ytd':
        return {
          start: formatLocalDate(new Date(availableThrough.getFullYear(), 0, 1)),
          end: formatLocalDate(availableThrough)
        };
      case 'trailing12':
        const twelveMonthsAgo = new Date(availableThrough);
        twelveMonthsAgo.setMonth(availableThrough.getMonth() - 11); // -11 to include 12 full months
        twelveMonthsAgo.setDate(1); // First of the month
        return {
          start: formatLocalDate(twelveMonthsAgo),
          end: formatLocalDate(availableThrough)
        };
      default:
        return financialDateRange;
    }
  };

  const handleFinancialQuickDateRange = (option) => {
    const newRange = getFinancialQuickDateRange(option);
    setFinancialDateRange(newRange);
    setSelectedFinancialQuickDate(option);
  };

  const handleFinancialDateRangeChange = (newRange) => {
    const maxDate = formatLocalDate(getFinancialAvailableThroughDate(currentOrganization?.financialCloseDay));
    setFinancialDateRange({
      start: newRange.start > maxDate ? maxDate : newRange.start,
      end: newRange.end > maxDate ? maxDate : newRange.end
    });
    setSelectedFinancialQuickDate(null); // Clear quick date selection when manually changing dates
  };

  // Helper function to get location display name (masks actual names for DEMO org)
  const getLocationDisplayName = (locationId, actualName) => {
    const isDemoOrg = localStorage.getItem('currentOrgCode') === 'DEMO';
    if (isDemoOrg) {
      return `Location ${locationId}`;
    }
    return actualName || `Location ${locationId}`;
  };

  // Quick date range options
  const getQuickDateRange = (option) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    switch (option) {
      case 'yesterday':
        return {
          start: formatLocalDate(yesterday),
          end: formatLocalDate(yesterday)
        };
      case 'last7':
        const week = new Date(today);
        week.setDate(today.getDate() - 7);
        return {
          start: formatLocalDate(week),
          end: formatLocalDate(yesterday)
        };
      case 'last30':
        const month = new Date(today);
        month.setDate(today.getDate() - 30);
        return {
          start: formatLocalDate(month),
          end: formatLocalDate(yesterday)
        };
      case 'last60':
        const twoMonths = new Date(today);
        twoMonths.setDate(today.getDate() - 60);
        return {
          start: formatLocalDate(twoMonths),
          end: formatLocalDate(yesterday)
        };
      case 'last90':
        const threeMonths = new Date(today);
        threeMonths.setDate(today.getDate() - 90);
        return {
          start: formatLocalDate(threeMonths),
          end: formatLocalDate(yesterday)
        };
      case 'thisMonth':
        // On the first of the month, yesterday is still in the previous month,
        // so "this month" would produce an invalid range (start > end).
        // Fall back to showing the full previous month instead.
        if (today.getDate() === 1) {
          const firstOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          const lastOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
          return {
            start: formatLocalDate(firstOfLastMonth),
            end: formatLocalDate(lastOfLastMonth)
          };
        }
        const firstOfThisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        return {
          start: formatLocalDate(firstOfThisMonth),
          end: formatLocalDate(yesterday)
        };
      case 'lastMonth':
        const firstOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
        return {
          start: formatLocalDate(firstOfLastMonth),
          end: formatLocalDate(lastOfLastMonth)
        };
      default:
        return dateRange;
    }
  };

  const handleQuickDateRange = (option) => {
    const newRange = getQuickDateRange(option);
    setDateRange(newRange);
    setSelectedQuickDate(option);
    setSelectedComparisonMode(getPreferredComparisonMode(option));
    setDismissedSavePrompt(false); // Reset dismiss state when date changes
  };

  // Get friendly label for date range
  const getDateRangeLabel = (key) => {
    if (!key) return 'Custom range';

    const labels = {
      'yesterday': 'Yesterday',
      'last7': 'Last 7 days',
      'last30': 'Last 30 days',
      'last60': 'Last 60 days',
      'last90': 'Last 90 days',
      'thisMonth': 'This Month',
      'lastMonth': 'Last Month',
      'mtd': 'Month to Date',
      'ytd': 'Year to Date'
    };
    return labels[key] || key;
  };

  const parseLocalDate = (dateStr) => {
    const [year, month, day] = dateStr.split('-').map(num => parseInt(num, 10));
    return new Date(year, month - 1, day);
  };

  const shiftDateByYearsClamped = (dateStr, years) => {
    const [year, month, day] = dateStr.split('-').map(num => parseInt(num, 10));
    const targetYear = year + years;
    const lastDayOfTargetMonth = new Date(targetYear, month, 0).getDate();
    return formatLocalDate(new Date(targetYear, month - 1, Math.min(day, lastDayOfTargetMonth)));
  };

  const getFinancialComparisonDateRange = (mode, range = financialDateRange) => {
    if (mode !== 'ly' || !range?.start || !range?.end) return null;
    return {
      start: shiftDateByYearsClamped(range.start, -1),
      end: shiftDateByYearsClamped(range.end, -1),
      label: 'vs LY'
    };
  };

  const shiftDateRange = (currentStart, currentEnd, { years = 0, months = 0, days = 0, label }) => {
    const startDate = parseLocalDate(currentStart);
    const endDate = parseLocalDate(currentEnd);
    startDate.setFullYear(startDate.getFullYear() + years);
    endDate.setFullYear(endDate.getFullYear() + years);
    startDate.setMonth(startDate.getMonth() + months);
    endDate.setMonth(endDate.getMonth() + months);
    startDate.setDate(startDate.getDate() + days);
    endDate.setDate(endDate.getDate() + days);
    return {
      start: formatLocalDate(startDate),
      end: formatLocalDate(endDate),
      label
    };
  };

  const getSameWeekdayLastYearRange = (currentStart, currentEnd, label) => {
    const startDate = parseLocalDate(currentStart);
    const endDate = parseLocalDate(currentEnd);
    // Hotel reporting comparisons should line up by weekday, not calendar date.
    // A 52-week lookback keeps Sunday-to-Sunday comparisons correct through leap years.
    startDate.setDate(startDate.getDate() - 364);
    endDate.setDate(endDate.getDate() - 364);
    return {
      start: formatLocalDate(startDate),
      end: formatLocalDate(endDate),
      label
    };
  };

  const getComparisonOptions = (quickDateOption) => {
    // 'forecast' option is temporarily disabled until we finalize the comparison semantics.
    // 'none' is always available — turns off comparison rendering entirely for clean snapshots.
    const noneOption = { key: 'none', label: 'None', longLabel: 'No comparison', type: 'none' };

    if (quickDateOption === 'yesterday') {
      return [
        { key: 'sdlw', label: 'SDLW', longLabel: 'Same day last week', type: 'historical' },
        { key: 'sdly', label: 'SDLY', longLabel: 'Same weekday last year', type: 'historical' },
        { key: 'budget', label: 'Budget', longLabel: 'Budget', type: 'budget' },
        noneOption,
      ];
    }

    if (quickDateOption === 'thisMonth') {
      return [
        { key: 'splm', label: 'SPLM', longLabel: 'Same point last month', type: 'historical' },
        { key: 'samePeriodLY', label: 'Same MTD LY', longLabel: 'Same weekday-aligned month-to-date last year', type: 'historical' },
        { key: 'budget', label: 'Budget', longLabel: 'Budget', type: 'budget' },
        noneOption,
      ];
    }

    if (quickDateOption === 'lastMonth') {
      return [
        { key: 'prior', label: 'Prior month', longLabel: 'Prior month', type: 'historical' },
        { key: 'samePeriodLY', label: 'Same month LY', longLabel: 'Same weekday-aligned month last year', type: 'historical' },
        { key: 'budget', label: 'Budget', longLabel: 'Budget', type: 'budget' },
        noneOption,
      ];
    }

    return [
      { key: 'prior', label: 'Prior period', longLabel: `Prior ${getDateRangeLabel(quickDateOption).toLowerCase()}`, type: 'historical' },
      { key: 'samePeriodLY', label: 'Last year', longLabel: 'Same weekday-aligned period last year', type: 'historical' },
      { key: 'budget', label: 'Budget', longLabel: 'Budget', type: 'budget' },
      noneOption,
    ];
  };

  const getPreferredComparisonMode = (quickDateOption, defaults = savedComparisonDefaults) => {
    const options = getComparisonOptions(quickDateOption);
    const preferred = defaults?.[quickDateOption];
    if (preferred && options.some(option => option.key === preferred)) {
      return preferred;
    }
    return options[0]?.key || 'prior';
  };

  const urlHasDashboardFilterState = () => {
    if (!isDashboardSectionRoute(location.pathname)) return false;
    const params = new URLSearchParams(location.search);
    return [
      'period',
      'start',
      'end',
      'compare',
      'locations',
      'agg',
      'finPeriod',
      'finStart',
      'finEnd',
      'finCompare'
    ].some(param => params.has(param));
  };

  const applyDashboardUrlState = () => {
    if (!isDashboardSectionRoute(location.pathname)) return;

    const params = new URLSearchParams(location.search);
    const nextSection = getDashboardSectionFromPath(location.pathname);
    if (activeDashboardTab !== nextSection) {
      setActiveDashboardTab(nextSection);
    }

    let nextQuickDate = selectedQuickDate;
    let nextDateRange = dateRange;
    const periodParam = params.get('period');
    const startParam = params.get('start');
    const endParam = params.get('end');

    if (periodParam && DASHBOARD_QUICK_DATE_KEYS.includes(periodParam)) {
      nextQuickDate = periodParam;
      nextDateRange = getQuickDateRange(periodParam);
    } else if (isIsoDateParam(startParam) && isIsoDateParam(endParam) && startParam <= endParam) {
      nextQuickDate = null;
      nextDateRange = { start: startParam, end: endParam };
    }

    if (nextQuickDate !== selectedQuickDate) {
      setSelectedQuickDate(nextQuickDate);
    }
    if (!rangesMatch(nextDateRange, dateRange)) {
      setDateRange(nextDateRange);
    }

    if (params.has('compare')) {
      const comparisonParam = params.get('compare');
      const allowedComparison = getComparisonOptions(nextQuickDate).some(option => option.key === comparisonParam);
      const nextComparison = allowedComparison ? comparisonParam : getPreferredComparisonMode(nextQuickDate);
      if (nextComparison !== selectedComparisonMode) {
        setSelectedComparisonMode(nextComparison);
      }
    }

    if (params.has('locations')) {
      const nextLocations = parseLocationsParam(params.get('locations'));
      if (!locationsMatch(nextLocations, selectedLocations)) {
        setSelectedLocations(nextLocations);
      }
    }

    if (params.has('agg')) {
      const aggregationParam = params.get('agg');
      if (DASHBOARD_AGGREGATION_KEYS.includes(aggregationParam) && aggregationParam !== timeAggregation) {
        setTimeAggregation(aggregationParam);
      }
    }

    let nextFinancialQuickDate = selectedFinancialQuickDate;
    let nextFinancialDateRange = financialDateRange;
    const financialPeriodParam = params.get('finPeriod');
    const financialStartParam = params.get('finStart');
    const financialEndParam = params.get('finEnd');

    if (financialPeriodParam && FINANCIAL_QUICK_DATE_KEYS.includes(financialPeriodParam)) {
      nextFinancialQuickDate = financialPeriodParam;
      nextFinancialDateRange = getFinancialQuickDateRange(financialPeriodParam);
    } else if (isIsoDateParam(financialStartParam) && isIsoDateParam(financialEndParam) && financialStartParam <= financialEndParam) {
      nextFinancialQuickDate = null;
      nextFinancialDateRange = { start: financialStartParam, end: financialEndParam };
    }

    if (nextFinancialQuickDate !== selectedFinancialQuickDate) {
      setSelectedFinancialQuickDate(nextFinancialQuickDate);
    }
    if (!rangesMatch(nextFinancialDateRange, financialDateRange)) {
      setFinancialDateRange(nextFinancialDateRange);
    }

    if (params.has('finCompare')) {
      const financialComparisonParam = params.get('finCompare');
      const nextFinancialComparison = FINANCIAL_COMPARISON_KEYS.includes(financialComparisonParam)
        ? financialComparisonParam
        : 'budget';
      if (nextFinancialComparison !== selectedFinancialComparisonMode) {
        setSelectedFinancialComparisonMode(nextFinancialComparison);
      }
    }
  };

  const buildDashboardUrl = () => {
    const section = DASHBOARD_SECTION_KEYS.includes(activeDashboardTab) ? activeDashboardTab : 'revenue';
    const params = new URLSearchParams();

    if (selectedQuickDate) {
      params.set('period', selectedQuickDate);
    } else if (dateRange?.start && dateRange?.end) {
      params.set('start', dateRange.start);
      params.set('end', dateRange.end);
    }

    if (selectedComparisonMode) {
      params.set('compare', selectedComparisonMode);
    }

    const locationsParam = serializeLocationsParam(selectedLocations);
    if (locationsParam) {
      params.set('locations', locationsParam);
    }

    if (timeAggregation !== 'day') {
      params.set('agg', timeAggregation);
    }

    if (selectedFinancialQuickDate) {
      params.set('finPeriod', selectedFinancialQuickDate);
    } else if (financialDateRange?.start && financialDateRange?.end) {
      params.set('finStart', financialDateRange.start);
      params.set('finEnd', financialDateRange.end);
    }

    if (selectedFinancialComparisonMode !== 'budget') {
      params.set('finCompare', selectedFinancialComparisonMode);
    }

    const queryString = params.toString();
    return `${DASHBOARD_SECTION_PATHS[section]}${queryString ? `?${queryString}` : ''}`;
  };

  const handleDashboardTabChange = (tabKey) => {
    if (!DASHBOARD_SECTION_KEYS.includes(tabKey)) return;
    setActiveDashboardTab(tabKey);
    const nextPath = DASHBOARD_SECTION_PATHS[tabKey];
    if (location.pathname !== nextPath) {
      navigate(`${nextPath}${location.search}`, { replace: false });
    }
  };

  useEffect(() => {
    if (!preferencesLoaded || !isDashboardSectionRoute(location.pathname)) return;

    const nextUrl = buildDashboardUrl();
    const currentUrl = `${location.pathname}${location.search}`;
    if (currentUrl !== nextUrl) {
      navigate(nextUrl, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    preferencesLoaded,
    activeDashboardTab,
    selectedQuickDate,
    dateRange?.start,
    dateRange?.end,
    selectedComparisonMode,
    selectedLocations,
    timeAggregation,
    selectedFinancialQuickDate,
    financialDateRange?.start,
    financialDateRange?.end,
    selectedFinancialComparisonMode
  ]);

  const getComparisonRange = (mode, currentStart, currentEnd, quickDateOption) => {
    switch (mode) {
      case 'sdlw':
        return shiftDateRange(currentStart, currentEnd, { days: -7, label: 'vs SDLW' });
      case 'sdly':
        return getSameWeekdayLastYearRange(currentStart, currentEnd, 'vs SDLY');
      case 'splm':
        return shiftDateRange(currentStart, currentEnd, { months: -1, label: 'vs SPLM' });
      case 'samePeriodLY':
        return getSameWeekdayLastYearRange(currentStart, currentEnd, 'vs last year');
      case 'prior':
        return getPriorPeriodDateRange(currentStart, currentEnd, quickDateOption);
      default:
        return null;
    }
  };

  const buildInlineComparisonMetrics = (mode, aggregated) => {
    if (!aggregated) return null;

    const availableRooms = aggregated.totalAvailableRooms || 0;
    const paidRooms = aggregated.paidOccupiedRooms || 0;

    if (mode === 'budget') {
      const roomRevenueBudget = aggregated.totalRoomRevenueBudget || aggregated.totalBudgetRevenue || 0;
      const totalRevenueBudget = aggregated.totalRevenueBudget || aggregated.totalBudgetRevenue || roomRevenueBudget;
      const budgetAvailableRooms = aggregated.totalBudgetAvailableRooms || 0;
      const budgetOccupiedRooms = aggregated.totalBudgetOccupiedRooms || 0;
      const budgetOccupancyRate = budgetAvailableRooms > 0 ? (budgetOccupiedRooms / budgetAvailableRooms) * 100 : 0;
      if (
        totalRevenueBudget === 0 &&
        (aggregated.totalBudgetExpense || 0) === 0 &&
        (aggregated.totalBudgetedHours || 0) === 0 &&
        budgetAvailableRooms === 0 &&
        budgetOccupiedRooms === 0
      ) {
        return null;
      }
      return {
        aggregated: {
          ...aggregated,
          revPAR: budgetAvailableRooms > 0 ? roomRevenueBudget / budgetAvailableRooms : (availableRooms > 0 ? roomRevenueBudget / availableRooms : 0),
          adr: budgetOccupiedRooms > 0 ? roomRevenueBudget / budgetOccupiedRooms : (paidRooms > 0 ? roomRevenueBudget / paidRooms : 0),
          occupancyRate: budgetOccupancyRate,
          totalRoomRevenue: roomRevenueBudget,
          totalRevenue: totalRevenueBudget,
          totalOccupiedRooms: budgetOccupiedRooms,
          totalAvailableRooms: budgetAvailableRooms,
          paidOccupiedRooms: budgetOccupiedRooms,
          hoursPerOccupiedRoom: budgetOccupiedRooms > 0 ? (aggregated.totalBudgetedHours || 0) / budgetOccupiedRooms : 0,
          dollarPerOccupiedRoom: aggregated.dollarPerOccupiedRoom || 0,
          otPercentage: aggregated.otPercentage || 0
        },
        label: 'vs budget',
        dateRange: `${dateRange.start} to ${dateRange.end}`,
        mode
      };
    }

    if (mode === 'forecast') {
      const forecastRevenue = aggregated.totalForecastRevenue || aggregated.totalRmsForecast || 0;
      if (forecastRevenue === 0) return null;
      return {
        aggregated: {
          ...aggregated,
          revPAR: availableRooms > 0 ? forecastRevenue / availableRooms : 0,
          adr: paidRooms > 0 ? forecastRevenue / paidRooms : 0,
          occupancyRate: aggregated.occupancyRate || 0,
          totalRoomRevenue: forecastRevenue,
          totalRevenue: forecastRevenue
        },
        label: 'vs forecast',
        dateRange: `${dateRange.start} to ${dateRange.end}`,
        mode
      };
    }

    return null;
  };

  const handleSaveDefaultComparison = async () => {
    if (!userSettings?.email || isSavingComparisonPreference) return;

    const nextDefaults = {
      ...savedComparisonDefaults,
      [selectedQuickDate]: selectedComparisonMode
    };

    setIsSavingComparisonPreference(true);
    try {
      await saveDashboardPreferences(userSettings.email, {
        comparisonDefaultsByPeriod: nextDefaults
      });
      setSavedComparisonDefaults(nextDefaults);
      setComparisonSaveSuccess(true);
      setTimeout(() => setComparisonSaveSuccess(false), 2500);
    } catch (error) {
      console.error('Error saving comparison preference:', error);
    } finally {
      setIsSavingComparisonPreference(false);
    }
  };

  // Single Save control: persists BOTH the default date range and the
  // comparison default for the selected period in one write.
  const handleSaveDefaults = async () => {
    if (!userSettings?.email || isSavingPreference) return;

    const nextComparisonDefaults = {
      ...savedComparisonDefaults,
      [selectedQuickDate]: selectedComparisonMode
    };

    setIsSavingPreference(true);
    try {
      await saveDashboardPreferences(userSettings.email, {
        defaultQuickDateRange: selectedQuickDate,
        comparisonDefaultsByPeriod: nextComparisonDefaults
      });
      setSavedDefaultQuickDate(selectedQuickDate);
      setSavedComparisonDefaults(nextComparisonDefaults);
      setSavedDateLabel(selectedQuickDate ? getDateRangeLabel(selectedQuickDate) : 'Custom range');
      setSaveSuccess(true);
      setSaveFadingOut(false);
      // Linger for 4 seconds, then start 2-second fade
      setTimeout(() => {
        setSaveFadingOut(true);
        setTimeout(() => {
          setSaveSuccess(false);
          setSaveFadingOut(false);
          setDismissedSavePrompt(true);
        }, 2000);
      }, 4000);
    } catch (error) {
      console.error('Error saving dashboard defaults:', error);
    } finally {
      setIsSavingPreference(false);
    }
  };

  // Check if current selection differs from saved default (and not dismissed)
  const hasUnsavedDatePreference = selectedQuickDate !== savedDefaultQuickDate && !dismissedSavePrompt;
  const hasUnsavedComparisonPreference =
    selectedComparisonMode !== getPreferredComparisonMode(selectedQuickDate, savedComparisonDefaults);
  // The Save button lights up when either the date range or the comparison
  // differs from the saved defaults
  const hasUnsavedDefaults = hasUnsavedDatePreference || hasUnsavedComparisonPreference;

  // Get prior period date range for comparison
  const getPriorPeriodDateRange = (currentStart, currentEnd, quickDateOption) => {
    // Parse dates correctly to avoid timezone issues
    const parseLocalDate = (dateStr) => {
      const [year, month, day] = dateStr.split('-').map(num => parseInt(num));
      return new Date(year, month - 1, day); // month is 0-indexed
    };

    const startDate = parseLocalDate(currentStart);
    const endDate = parseLocalDate(currentEnd);

    // Calculate the number of days in the current period
    const currentPeriodDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

    switch (quickDateOption) {
      case 'yesterday':
        // Compare to same day last week (yesterday - 7 days)
        // Since startDate/endDate are both yesterday for this mode, we go back 7 days
        const priorWeekDay = new Date(endDate); // Use endDate (which is yesterday)
        priorWeekDay.setDate(endDate.getDate() - 7);
        return {
          start: formatLocalDate(priorWeekDay),
          end: formatLocalDate(priorWeekDay),
          label: 'vs last week'
        };

      case 'last7':
        // Compare to previous 7 days (-14 to -7)
        const priorWeekEnd = new Date(startDate);
        priorWeekEnd.setDate(startDate.getDate() - 1);
        const priorWeekStart = new Date(priorWeekEnd);
        priorWeekStart.setDate(priorWeekEnd.getDate() - 6);
        return {
          start: formatLocalDate(priorWeekStart),
          end: formatLocalDate(priorWeekEnd),
          label: 'vs prior week'
        };

      case 'last30':
        // Compare to previous 30 day period
        const prior30End = new Date(startDate);
        prior30End.setDate(startDate.getDate() - 1);
        const prior30Start = new Date(prior30End);
        prior30Start.setDate(prior30End.getDate() - 29);
        return {
          start: formatLocalDate(prior30Start),
          end: formatLocalDate(prior30End),
          label: 'vs prior 30 days'
        };

      case 'thisMonth':
        // Compare to same date range in previous month
        // For example: Oct 1-5 compares to Sep 1-5
        const thisMonthStart = new Date(startDate);
        const thisMonthEnd = new Date(endDate);

        const lastMonthStart = new Date(thisMonthStart);
        lastMonthStart.setMonth(lastMonthStart.getMonth() - 1);

        const lastMonthEnd = new Date(thisMonthEnd);
        lastMonthEnd.setMonth(lastMonthEnd.getMonth() - 1);

        return {
          start: formatLocalDate(lastMonthStart),
          end: formatLocalDate(lastMonthEnd),
          label: 'vs last month'
        };

      case 'lastMonth':
        return shiftDateRange(currentStart, currentEnd, { months: -1, label: 'vs prior month' });

      case 'last60':
      case 'last90':
        const priorLongEnd = new Date(startDate);
        priorLongEnd.setDate(startDate.getDate() - 1);
        const priorLongStart = new Date(priorLongEnd);
        priorLongStart.setDate(priorLongEnd.getDate() - (currentPeriodDays - 1));
        return {
          start: formatLocalDate(priorLongStart),
          end: formatLocalDate(priorLongEnd),
          label: `vs prior ${currentPeriodDays} days`
        };

      default:
        // For custom date ranges
        // Check if it's 60 days or more
        if (currentPeriodDays >= 60) {
          return null;
        }

        // For custom ranges < 60 days, compare to prior equivalent period
        const priorEnd = new Date(startDate);
        priorEnd.setDate(startDate.getDate() - 1);
        const priorStart = new Date(priorEnd);
        priorStart.setDate(priorEnd.getDate() - (currentPeriodDays - 1));

        return {
          start: formatLocalDate(priorStart),
          end: formatLocalDate(priorEnd),
          label: 'vs prior period'
        };
    }
  };

  // Alert handlers
  const handleOpenAlertModal = (metricConfig) => {
    setCurrentAlertMetric(metricConfig);
    setShowAlertModal(true);
  };

  const handleSaveAlert = async (alertConfig) => {
    try {

      // Prepare alert data for API
      const alertData = {
        ...alertConfig,
        organizationId: currentOrganization.id,
        userId: userSettings.email, // Use email as the unique identifier across the system
        createdAt: new Date().toISOString(),
        lastChecked: null
      };

      // Call API to save alert
      const response = await api.post('/alerts', alertData);

      if (response.data.success) {

        // TODO: Show success notification
      } else {
        console.error('Failed to create alert:', response.data.error);
        // TODO: Show error notification
      }
    } catch (error) {
      console.error('Error creating alert:', error);
      // TODO: Show error notification
    }
  };

  // Currency-aware formatters keyed off reportingCurrency. JPY has zero minor
  // units; the shared helper handles that — callers don't special-case it.
  const formatCurrency = (value) => {
    if (value === null || value === undefined) {
      return `${sharedCurrencySymbol(activeCurrency)}0${activeCurrency === 'JPY' ? '' : '.00'}`;
    }
    return sharedFormatCurrency(value, activeCurrency);
  };

  const formatCurrencyRounded = (value) => {
    if (value === null || value === undefined) {
      return `${sharedCurrencySymbol(activeCurrency)}0`;
    }
    return sharedFormatCurrencyRounded(value, activeCurrency);
  };

  const formatWholeNumber = (value) => {
    if (value === null || value === undefined) return '0';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.round(value));
  };

  // Format percentage
  const formatPercentage = (value) => {
    if (value === null || value === undefined) return '0%';
    return `${value.toFixed(1)}%`;
  };

  // Show skeleton loader immediately if we don't have all required data
  // Note: locationNames may be empty for users with no location-specific access (e.g., orgViewer)
  // Wait for comparison data too to prevent layout shift when it loads.
  // Also gate on the locations API so the first paint already has each
  // location's currency — otherwise KPI tiles flash USD before swapping to
  // the resolved currency (e.g. JPY) once /locations completes.
  const isDataReady = !isLoadingUserSettings &&
                     preferencesLoaded &&
                     currentOrganization &&
                     dashboardData &&
                     locationsApiResolved &&
                     !isLoadingComparison;

  if (!isDataReady) {
    return isMobile ? <MobileDashboardSkeleton /> : <DashboardSkeleton isDarkMode={isDarkMode} />;
  }

  // Error state
  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <Card>
          <div style={{ textAlign: 'center', padding: 24 }}>
            <h3 style={{ color: 'var(--destructive)', marginBottom: 8 }}>Error Loading Dashboard</h3>
            <p style={{ color: 'var(--muted)', marginBottom: 16 }}>{error}</p>
            <button
              onClick={fetchDashboardData}
              style={{
                padding: '8px 16px',
                backgroundColor: 'var(--brand)',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer'
              }}
            >
              <RefreshCw size={16} style={{ marginRight: 8 }} />
              Retry
            </button>
          </div>
        </Card>
      </div>
    );
  }

  // No data available state
  if (noDataAvailable) {
    return (
      <div
        style={{
          minHeight: '100vh',
          padding: 'var(--sp-6)',
        }}
      >
        <div
          style={{
            maxWidth: 1760,
            margin: '0 auto',
            position: 'relative',
          }}
        >
          <Card>
            <div style={{ textAlign: 'center', padding: 48 }}>
              <div style={{ marginBottom: 24 }}>
                <Hotel size={64} style={{ color: isDarkMode ? '#a8a8a8' : '#a8a8a8', margin: '0 auto' }} />
              </div>
              <h3 style={{ fontSize: 24, fontWeight: 600, color: isDarkMode ? '#ffffff' : '#111111', marginBottom: 12 }}>
                No Data Available
              </h3>
              <p style={{ color: isDarkMode ? '#e5e5e5' : '#a8a8a8', marginBottom: 32, fontSize: 16 }}>
                No data is available for the selected date range: {dateRange.start} to {dateRange.end}
              </p>

              {/* Quick date range suggestions */}
              <div style={{ marginBottom: 24 }}>
                <p style={{ color: isDarkMode ? '#e5e5e5' : '#a8a8a8', marginBottom: 16, fontSize: 14 }}>
                  Try selecting a different date range:
                </p>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                  {[
                    { key: 'yesterday', label: 'Yesterday' },
                    { key: 'last7', label: 'Last 7 days' },
                    { key: 'last30', label: 'Last 30 days' },
                    { key: 'last60', label: 'Last 60 days' }
                  ].map(option => (
                    <button
                      key={option.key}
                      onClick={() => handleQuickDateRange(option.key)}
                      style={{
                        padding: '8px 16px',
                        fontSize: 14,
                        fontWeight: 500,
                        borderRadius: 8,
                        border: isDarkMode ? '1px solid #333333' : '1px solid #e2e8f0',
                        backgroundColor: isDarkMode ? '#111111' : '#ffffff',
                        color: isDarkMode ? '#e5e5e5' : '#a8a8a8',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                      }}
                      onMouseEnter={(event) => {
                        event.currentTarget.style.backgroundColor = isDarkMode ? '#333333' : '#ffffff';
                        event.currentTarget.style.borderColor = '#ffffff';
                      }}
                      onMouseLeave={(event) => {
                        event.currentTarget.style.backgroundColor = isDarkMode ? '#111111' : '#ffffff';
                        event.currentTarget.style.borderColor = isDarkMode ? '#333333' : '#e2e8f0';
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={() => fetchDashboardData(true)}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#ffffff',
                  color: 'white',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 500,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8
                }}
              >
                <RefreshCw size={16} />
                Refresh Data
              </button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // Prepare forecasting data - will be replaced with actual API data later
  const getOtbRooms = (point) => {
    const otbRooms = Number(point?.otbRooms);
    if (Number.isFinite(otbRooms) && otbRooms !== 0) return otbRooms;
    return Number(point?.occupiedRooms) || 0;
  };

  const getOtbAvailableRooms = (point) => {
    const otbAvailableRooms = Number(point?.otbAvailableRooms);
    if (Number.isFinite(otbAvailableRooms) && otbAvailableRooms !== 0) return otbAvailableRooms;
    return Number(point?.availableRooms) || 0;
  };

  const prepareForecastingData = (dailyData) => {
    console.log('=== LINE CHART: prepareForecastingData START ===');
    console.log('CHART: Input dailyData length:', dailyData?.length);

    if (!dailyData || dailyData.length === 0) {
      console.log('CHART: No data, returning empty array');
      return [];
    }

    console.log('CHART: Selected locations:', selectedLocations);

    // Apply location filtering if specific locations are selected
    let filteredData = dailyData;
    if (!selectedLocations.includes('all') && selectedLocations.length > 0) {
      const selectedLocationIds = selectedLocations.map(id => parseInt(id));
      console.log('CHART: Filtering to location IDs:', selectedLocationIds);
      filteredData = dailyData.filter(point =>
        point.locationId !== null &&
        point.locationId !== undefined &&
        selectedLocationIds.includes(point.locationId)
      );
      console.log('CHART: Filtered data length:', filteredData.length);
    }

    // Group data by date and sum across selected locations
    const nullableNumber = (value) => {
      if (value === null || value === undefined) return null;
      const n = Number(value);
      return Number.isFinite(n) ? n : null;
    };
    const addNullable = (bucket, key, value) => {
      const n = nullableNumber(value);
      if (n === null) return;
      bucket[key] = bucket[key] === null ? n : bucket[key] + n;
    };

    const dateGroups = filteredData.reduce((acc, point) => {
      const dateKey = point.date;
      if (!acc[dateKey]) {
        acc[dateKey] = {
          date: dateKey,
          roomRevenue: 0,
          availableRooms: 0,
          occupiedRooms: 0,
          otbRooms: 0,
          otbAvailableRooms: 0,
          compRooms: 0,
          // Placeholder fields - will be populated from API later
          revparProjection: 0,
          otbForecast: 0,
          rmsForecast: 0,
          pickupRooms1Day: null,
          pickupRevenue1Day: null,
          pickupRooms7Day: null,
          pickupRevenue7Day: null,
          pickupRooms30Day: null,
          pickupRevenue30Day: null,
          latestForecastReportDate: null,
          hasCurrentPickupData: false,
          hasStalePickupData: false,
          pickupDataCurrent: true,
          pickupDataStaleDays: null
        };
      }

      acc[dateKey].roomRevenue += point.roomRevenue || 0;
      acc[dateKey].availableRooms += point.availableRooms || 0;
      acc[dateKey].occupiedRooms += point.occupiedRooms || 0;
      acc[dateKey].otbRooms += getOtbRooms(point);
      acc[dateKey].otbAvailableRooms += getOtbAvailableRooms(point);
      acc[dateKey].compRooms += point.compRooms || 0;

      // Use actual forecasting data from API
      acc[dateKey].revparProjection += point.revparProjection || 0;
      acc[dateKey].otbForecast += point.otbForecast || 0;
      acc[dateKey].rmsForecast += point.rmsForecast || 0;
      addNullable(acc[dateKey], 'pickupRooms1Day', point.pickupRooms1Day);
      addNullable(acc[dateKey], 'pickupRevenue1Day', point.pickupRevenue1Day);
      addNullable(acc[dateKey], 'pickupRooms7Day', point.pickupRooms7Day);
      addNullable(acc[dateKey], 'pickupRevenue7Day', point.pickupRevenue7Day);
      addNullable(acc[dateKey], 'pickupRooms30Day', point.pickupRooms30Day);
      addNullable(acc[dateKey], 'pickupRevenue30Day', point.pickupRevenue30Day);
      if (point.latestForecastReportDate && (!acc[dateKey].latestForecastReportDate || point.latestForecastReportDate > acc[dateKey].latestForecastReportDate)) {
        acc[dateKey].latestForecastReportDate = point.latestForecastReportDate;
      }
      if (point.pickupDataCurrent === true || point.pickupDataCurrent === 1) {
        acc[dateKey].hasCurrentPickupData = true;
      }
      if (point.pickupDataCurrent === false || point.pickupDataCurrent === 0) {
        acc[dateKey].hasStalePickupData = true;
      }
      acc[dateKey].pickupDataCurrent = acc[dateKey].hasCurrentPickupData || !acc[dateKey].hasStalePickupData;
      const staleDays = nullableNumber(point.pickupDataStaleDays);
      if (staleDays !== null) {
        acc[dateKey].pickupDataStaleDays = acc[dateKey].pickupDataStaleDays === null
          ? staleDays
          : Math.max(acc[dateKey].pickupDataStaleDays, staleDays);
      }

      return acc;
    }, {});

    // Convert to array and format dates
    const aggregatedData = Object.values(dateGroups).map(day => {
      if (!day.date) {
        return {
          ...day,
          date: 'N/A',
          originalDate: day.date
        };
      }

      // Parse date more robustly to avoid timezone issues
      let safeDate;
      let dateStr = day.date;

      // If it's an ISO datetime, extract just the date part
      if (dateStr.includes('T')) {
        dateStr = dateStr.split('T')[0];
      }

      // Parse YYYY-MM-DD format manually to avoid timezone shifts
      if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, dayNum] = dateStr.split('-').map(num => parseInt(num));
        safeDate = new Date(year, month - 1, dayNum); // month is 0-indexed
      } else {
        // Fallback for other formats
        safeDate = new Date(dateStr + 'T12:00:00.000Z');
      }

      return {
        ...day,
        originalDate: day.date,
        safeDate: safeDate,
        date: safeDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      };
    });

    // Sort by original date and filter out invalid dates
    const sortedData = aggregatedData
      .filter(item => {
        if (!item.originalDate) return false;
        const itemDate = new Date(item.originalDate);
        const minDate = new Date('2020-01-01');
        const maxDate = new Date();
        maxDate.setFullYear(maxDate.getFullYear() + 1);
        return itemDate >= minDate && itemDate <= maxDate;
      })
      .sort((a, b) => new Date(a.originalDate) - new Date(b.originalDate));

    // Calculate average daily RevPAR projection for future dates
    // Get today's date at midnight for comparison
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    console.log('CHART: Today date for comparison:', today.toISOString());

    // Need to calculate per-location monthly projection, then divide by days
    // First, group the ORIGINAL filtered data by location to preserve location breakdown
    const locationData = {};
    const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();

    filteredData.forEach(day => {
      const locationId = day.locationId;

      if (!locationData[locationId]) {
        locationData[locationId] = {
          roomRevenue: 0,
          availableRooms: 0,
          maxAvailableRooms: 0
        };
      }

      locationData[locationId].roomRevenue += day.roomRevenue || 0;
      locationData[locationId].availableRooms += getOtbAvailableRooms(day);

      const dayCapacity = getOtbAvailableRooms(day);
      if (dayCapacity > locationData[locationId].maxAvailableRooms) {
        locationData[locationId].maxAvailableRooms = dayCapacity;
      }
    });

    console.log('CHART: Location data for projection:', JSON.stringify(locationData, null, 2));

    // Calculate total monthly projection using the same DAX logic as table
    let totalMonthlyProjection = 0;
    Object.entries(locationData).forEach(([locationId, location]) => {
      const revPAR = location.availableRooms > 0
        ? location.roomRevenue / location.availableRooms
        : 0;
      const locationProjection = revPAR * location.maxAvailableRooms * daysInMonth;
      console.log(`CHART: Location ${locationId} monthly projection: ${locationProjection}`);
      totalMonthlyProjection += locationProjection;
    });

    console.log('CHART: Total monthly projection:', totalMonthlyProjection);

    // Calculate total actual room revenue for historical days
    let totalActualRoomRevenue = 0;
    sortedData.forEach(day => {
      const dayDate = new Date(day.originalDate);
      dayDate.setHours(0, 0, 0, 0);
      const isHistorical = dayDate < today && (day.roomRevenue || 0) > 0;
      if (isHistorical) {
        totalActualRoomRevenue += day.roomRevenue || 0;
      }
    });

    console.log('CHART: Total actual room revenue (historical):', totalActualRoomRevenue);

    // Calculate days remaining including today
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const daysRemainingIncludingToday = lastDayOfMonth.getDate() - today.getDate() + 1;

    console.log('CHART: Days remaining including today:', daysRemainingIncludingToday);

    // Calculate average daily projection for remaining days
    // Formula: (Total RevPAR Projection - Actual Room Revenue) / days remaining including today
    const remainingProjection = totalMonthlyProjection - totalActualRoomRevenue;
    const avgDailyRevPARProjection = daysRemainingIncludingToday > 0
      ? remainingProjection / daysRemainingIncludingToday
      : 0;

    console.log('CHART: Remaining projection:', remainingProjection);
    console.log('CHART: Average daily RevPAR projection (corrected):', avgDailyRevPARProjection);

    // Update the data: use room revenue for historical dates, flat line projection for future
    const processedData = sortedData.map(day => {
      const dayDate = new Date(day.originalDate);
      dayDate.setHours(0, 0, 0, 0);

      // For dates before today with actual revenue, show room revenue
      // For today and future dates (or if room revenue is 0), show the flat line projection
      const isHistorical = dayDate < today && (day.roomRevenue || 0) > 0;

      const result = {
        ...day,
        revparProjection: isHistorical ? day.roomRevenue : avgDailyRevPARProjection,
        otbForecast: day.otbForecast,
        rmsForecast: day.rmsForecast,
        pickupRooms1Day: day.pickupRooms1Day,
        pickupRevenue1Day: day.pickupRevenue1Day,
        pickupRooms7Day: day.pickupRooms7Day,
        pickupRevenue7Day: day.pickupRevenue7Day,
        pickupRooms30Day: day.pickupRooms30Day,
        pickupRevenue30Day: day.pickupRevenue30Day
      };

      // Log first few days for debugging
      if (day.originalDate <= '2025-10-25') {
        console.log(`CHART: ${day.originalDate} - isHistorical: ${isHistorical}, roomRevenue: ${day.roomRevenue}, revparProjection: ${result.revparProjection}`);
      }

      return result;
    });

    console.log('=== LINE CHART: prepareForecastingData END ===\n');
    return processedData;
  };

  // Use separate forecasting data state instead of dashboard data
  const processedForecastingData = forecastingData?.byDate ? prepareForecastingData(forecastingData.byDate) : [];

  // Calculate aggregate forecasting metrics
  const calculateForecastingMetrics = (forecastData) => {
    console.log('=== KPI CARD: calculateForecastingMetrics START ===');
    console.log('KPI: Input forecastData length:', forecastData?.length);

    if (!forecastData || forecastData.length === 0) {
      console.log('KPI: No data, returning zeros');
      return {
        avgRevPARProjection: 0,
        totalOTBForecast: 0,
        totalRMSForecast: 0,
        totalPickupRooms1Day: null,
        totalPickupRevenue1Day: null,
        totalPickupRooms7Day: null,
        totalPickupRevenue7Day: null,
        totalPickupRooms30Day: null,
        totalPickupRevenue30Day: null
      };
    }

    const nullableNumber = (value) => {
      if (value === null || value === undefined) return null;
      const n = Number(value);
      return Number.isFinite(n) ? n : null;
    };

    // Get days in current month
    const today = new Date();
    const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    console.log('KPI: Days in month:', daysInMonth);

    // Group by location and calculate RevPAR projection per location (same logic as table)
    // Note: Backend now filters out records without locationId
    const locationTotals = {};

    forecastData.forEach(day => {
      const locationId = day.locationId;

      if (!locationTotals[locationId]) {
        locationTotals[locationId] = {
          roomRevenue: 0,
          availableRooms: 0,
          maxAvailableRooms: 0,
          otbRooms: 0,
          otbAvailableRooms: 0,
          otbForecast: 0,
          rmsForecast: 0,
          pickupRooms1Day: null,
          pickupRevenue1Day: null,
          pickupRooms7Day: null,
          pickupRevenue7Day: null,
          pickupRooms30Day: null,
          pickupRevenue30Day: null
        };
      }

      const addNullable = (target, key, value) => {
        const next = nullableNumber(value);
        if (next === null) return;
        target[key] = (target[key] ?? 0) + next;
      };

      locationTotals[locationId].roomRevenue += day.roomRevenue || 0;
      locationTotals[locationId].availableRooms += getOtbAvailableRooms(day);
      locationTotals[locationId].otbRooms += getOtbRooms(day);
      locationTotals[locationId].otbAvailableRooms += getOtbAvailableRooms(day);

      const dayCapacity = getOtbAvailableRooms(day);
      if (dayCapacity > locationTotals[locationId].maxAvailableRooms) {
        locationTotals[locationId].maxAvailableRooms = dayCapacity;
      }

      locationTotals[locationId].otbForecast += day.otbForecast || 0;
      locationTotals[locationId].rmsForecast += day.rmsForecast || 0;
      addNullable(locationTotals[locationId], 'pickupRooms1Day', day.pickupRooms1Day);
      addNullable(locationTotals[locationId], 'pickupRevenue1Day', day.pickupRevenue1Day);
      addNullable(locationTotals[locationId], 'pickupRooms7Day', day.pickupRooms7Day);
      addNullable(locationTotals[locationId], 'pickupRevenue7Day', day.pickupRevenue7Day);
      addNullable(locationTotals[locationId], 'pickupRooms30Day', day.pickupRooms30Day);
      addNullable(locationTotals[locationId], 'pickupRevenue30Day', day.pickupRevenue30Day);
    });

    console.log('KPI: Location totals:', JSON.stringify(locationTotals, null, 2));

    // Calculate RevPAR Projection using DAX logic for each location, then sum
    let totalRevPARProjection = 0;
    let totalOTBForecast = 0;
    let totalRMSForecast = 0;
    let totalPickupRooms1Day = null;
    let totalPickupRevenue1Day = null;
    let totalPickupRooms7Day = null;
    let totalPickupRevenue7Day = null;
    let totalPickupRooms30Day = null;
    let totalPickupRevenue30Day = null;

    const addMetricNullable = (current, next) => {
      const value = nullableNumber(next);
      if (value === null) return current;
      return (current ?? 0) + value;
    };

    Object.entries(locationTotals).forEach(([locationId, location]) => {
      const revPAR = location.availableRooms > 0
        ? location.roomRevenue / location.availableRooms
        : 0;
      const locationProjection = revPAR * location.maxAvailableRooms * daysInMonth;

      console.log(`KPI: Location ${locationId}:`);
      console.log(`  - Room Revenue: ${location.roomRevenue}`);
      console.log(`  - Available Rooms: ${location.availableRooms}`);
      console.log(`  - Max Available Rooms: ${location.maxAvailableRooms}`);
      console.log(`  - RevPAR: ${revPAR}`);
      console.log(`  - Location Projection: ${locationProjection}`);

      totalRevPARProjection += locationProjection;
      totalOTBForecast += location.otbForecast;
      totalRMSForecast += location.rmsForecast;
      totalPickupRooms1Day = addMetricNullable(totalPickupRooms1Day, location.pickupRooms1Day);
      totalPickupRevenue1Day = addMetricNullable(totalPickupRevenue1Day, location.pickupRevenue1Day);
      totalPickupRooms7Day = addMetricNullable(totalPickupRooms7Day, location.pickupRooms7Day);
      totalPickupRevenue7Day = addMetricNullable(totalPickupRevenue7Day, location.pickupRevenue7Day);
      totalPickupRooms30Day = addMetricNullable(totalPickupRooms30Day, location.pickupRooms30Day);
      totalPickupRevenue30Day = addMetricNullable(totalPickupRevenue30Day, location.pickupRevenue30Day);
    });

    console.log('KPI: FINAL Total RevPAR Projection:', totalRevPARProjection);
    console.log('=== KPI CARD: calculateForecastingMetrics END ===\n');

    return {
      avgRevPARProjection: totalRevPARProjection,
      totalOTBForecast: totalOTBForecast,
      totalRMSForecast: totalRMSForecast,
      totalPickupRooms1Day,
      totalPickupRevenue1Day,
      totalPickupRooms7Day,
      totalPickupRevenue7Day,
      totalPickupRooms30Day,
      totalPickupRevenue30Day
    };
  };

  // Use raw byDate data for KPI calculation to preserve location breakdown
  const forecastingMetrics = calculateForecastingMetrics(forecastingData?.byDate || []);

  // Prepare chart data - aggregate by date across selected locations
  const prepareChartData = (dailyData) => {
    if (!dailyData || dailyData.length === 0) return [];

    // Apply location filtering if specific locations are selected
    // If no locations selected or "all" selected, show all data
    let filteredData = dailyData;
    if (!selectedLocations.includes('all') && selectedLocations.length > 0) {
      const selectedLocationIds = selectedLocations.map(id => parseInt(id));
      filteredData = dailyData.filter(point =>
        point.locationId !== null &&
        point.locationId !== undefined &&
        selectedLocationIds.includes(point.locationId)
      );
    }

    // Group data by date and sum across selected locations
    const dateGroups = filteredData.reduce((acc, point) => {
      const dateKey = point.date;
      if (!acc[dateKey]) {
        acc[dateKey] = {
          date: dateKey,
          roomRevenue: 0,
          totalRevenue: 0,
          roomRevenueBudget: 0,
          totalRevenueBudget: 0,
          budgetOccupiedRooms: 0,
          budgetAvailableRooms: 0,
          occupiedRooms: 0,
          availableRooms: 0,
          otPremiumCost: 0,
          compRooms: 0,
          hoursWorked: 0,
          wages: 0,
          otHours: 0,
          budgetedHours: 0,
          netIncome: 0,
          revenue: 0,
          expense: 0,
          budgetRevenue: 0,
          budgetExpense: 0,
          otbForecast: 0,
          rmsForecast: 0
        };
      }

      acc[dateKey].roomRevenue += point.roomRevenue || 0;
      acc[dateKey].totalRevenue += point.totalRevenue || 0;
      acc[dateKey].roomRevenueBudget += point.roomRevenueBudget || 0;
      acc[dateKey].totalRevenueBudget += point.totalRevenueBudget || 0;
      acc[dateKey].budgetOccupiedRooms += point.budgetOccupiedRooms || 0;
      acc[dateKey].budgetAvailableRooms += point.budgetAvailableRooms || 0;
      acc[dateKey].otPremiumCost += point.otPremiumCost || 0;

      if (point.occupiedRooms !== null) {
        acc[dateKey].occupiedRooms += point.occupiedRooms;
      }
      if (point.availableRooms !== null) {
        acc[dateKey].availableRooms += point.availableRooms;
      }
      if (point.compRooms !== null) {
        acc[dateKey].compRooms += point.compRooms;
      }

      // Add operations and financial metrics
      acc[dateKey].hoursWorked += point.hoursWorked || 0;
      acc[dateKey].wages += point.wages || 0;
      acc[dateKey].otHours += point.otHours || 0;
      acc[dateKey].budgetedHours += point.budgetedHours || 0;
      acc[dateKey].netIncome += point.netIncome || 0;
      acc[dateKey].revenue += point.revenue || 0;
      acc[dateKey].expense += point.expense || 0;
      acc[dateKey].budgetRevenue += point.budgetRevenue || 0;
      acc[dateKey].budgetExpense += point.budgetExpense || 0;
      acc[dateKey].otbForecast += point.otbForecast || 0;
      acc[dateKey].rmsForecast += point.rmsForecast || 0;

      return acc;
    }, {});

    // Convert to array and calculate metrics
    const aggregatedData = Object.values(dateGroups).map(day => {
      const paidOccupiedRooms = Math.max(0, (day.occupiedRooms || 0) - (day.compRooms || 0));

      // Parse date safely without timezone issues
      if (!day.date) {
        return {
          ...day,
          occupancyRate: day.availableRooms > 0 ? (paidOccupiedRooms / day.availableRooms) * 100 : 0,
          adr: paidOccupiedRooms > 0 ? day.roomRevenue / paidOccupiedRooms : 0,
          revPAR: day.availableRooms > 0 ? day.roomRevenue / day.availableRooms : 0,
          paidOccupiedRooms: paidOccupiedRooms,
          originalDate: day.date,
          date: 'N/A'
        };
      }

      // Parse date more robustly to avoid timezone issues
      let safeDate;
      let dateStr = day.date;

      // If it's an ISO datetime, extract just the date part
      if (dateStr.includes('T')) {
        dateStr = dateStr.split('T')[0];
      }

      // Parse YYYY-MM-DD format manually to avoid timezone shifts
      if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, dayNum] = dateStr.split('-').map(num => parseInt(num));
        safeDate = new Date(year, month - 1, dayNum); // month is 0-indexed
      } else {
        // Fallback for other formats - try to create a date at noon UTC to avoid timezone issues
        safeDate = new Date(dateStr + 'T12:00:00.000Z');
      }

      return {
        ...day,
        occupancyRate: day.availableRooms > 0 ? (paidOccupiedRooms / day.availableRooms) * 100 : 0,
        budgetOccupancyRate: day.budgetAvailableRooms > 0 ? ((day.budgetOccupiedRooms || 0) / day.budgetAvailableRooms) * 100 : 0,
        adr: paidOccupiedRooms > 0 ? day.roomRevenue / paidOccupiedRooms : 0,
        revPAR: day.availableRooms > 0 ? day.roomRevenue / day.availableRooms : 0,
        paidOccupiedRooms: paidOccupiedRooms,
        originalDate: day.date, // Keep original date for sorting
        safeDate: safeDate,
        date: safeDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      };
    });

    // Sort by original date
    const sortedData = aggregatedData
      .filter(item => {
        // Filter out null dates and invalid dates
        if (!item.originalDate) return false;

        // Check if the date is within a reasonable range
        const itemDate = new Date(item.originalDate);
        const minDate = new Date('2020-01-01');
        const maxDate = new Date();
        maxDate.setFullYear(maxDate.getFullYear() + 1);

        return itemDate >= minDate && itemDate <= maxDate;
      })
      .sort((a, b) => new Date(a.originalDate) - new Date(b.originalDate));

    // Apply time aggregation
    return aggregateByTimeperiod(sortedData, timeAggregation);
  };

  // Aggregate data by time period (day, month, year)
  const aggregateByTimeperiod = (data, period) => {
    if (period === 'day') {
      // For daily, limit data based on range size to keep charts readable
      const daysDiff = Math.abs(new Date(dateRange.end) - new Date(dateRange.start)) / (1000 * 60 * 60 * 24);
      if (daysDiff > 30) {
        return data; // Show all data for longer ranges
      } else {
        return data.slice(-30); // Limit to 30 days for shorter ranges
      }
    }

    // Group by month or year
    const grouped = data.reduce((acc, item) => {
      if (!item.safeDate) return acc;

      let key;
      let displayDate;

      if (period === 'month') {
        key = `${item.safeDate.getFullYear()}-${String(item.safeDate.getMonth() + 1).padStart(2, '0')}`;
        displayDate = item.safeDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
      } else if (period === 'year') {
        key = item.safeDate.getFullYear().toString();
        displayDate = key;
      }

      if (!acc[key]) {
        acc[key] = {
          date: displayDate,
          originalDate: key,
          roomRevenue: 0,
          totalRevenue: 0,
          roomRevenueBudget: 0,
          totalRevenueBudget: 0,
          budgetOccupiedRooms: 0,
          budgetAvailableRooms: 0,
          occupiedRooms: 0,
          availableRooms: 0,
          otPremiumCost: 0,
          compRooms: 0,
          hoursWorked: 0,
          wages: 0,
          otHours: 0,
          budgetedHours: 0,
          netIncome: 0,
          revenue: 0,
          expense: 0,
          budgetRevenue: 0,
          budgetExpense: 0,
          otbForecast: 0,
          rmsForecast: 0,
          count: 0
        };
      }

      acc[key].roomRevenue += item.roomRevenue || 0;
      acc[key].totalRevenue += item.totalRevenue || 0;
      acc[key].roomRevenueBudget += item.roomRevenueBudget || 0;
      acc[key].totalRevenueBudget += item.totalRevenueBudget || 0;
      acc[key].budgetOccupiedRooms += item.budgetOccupiedRooms || 0;
      acc[key].budgetAvailableRooms += item.budgetAvailableRooms || 0;
      acc[key].occupiedRooms += item.occupiedRooms || 0;
      acc[key].availableRooms += item.availableRooms || 0;
      acc[key].otPremiumCost += item.otPremiumCost || 0;
      acc[key].compRooms += item.compRooms || 0;
      acc[key].hoursWorked += item.hoursWorked || 0;
      acc[key].wages += item.wages || 0;
      acc[key].otHours += item.otHours || 0;
      acc[key].budgetedHours += item.budgetedHours || 0;
      acc[key].netIncome += item.netIncome || 0;
      acc[key].revenue += item.revenue || 0;
      acc[key].expense += item.expense || 0;
      acc[key].budgetRevenue += item.budgetRevenue || 0;
      acc[key].budgetExpense += item.budgetExpense || 0;
      acc[key].otbForecast += item.otbForecast || 0;
      acc[key].rmsForecast += item.rmsForecast || 0;
      acc[key].count += 1;

      return acc;
    }, {});

    // Calculate aggregated metrics
    return Object.values(grouped).map(item => {
      const paidOccupiedRooms = Math.max(0, (item.occupiedRooms || 0) - (item.compRooms || 0));
      return {
        ...item,
        occupancyRate: item.availableRooms > 0 ? (paidOccupiedRooms / item.availableRooms) * 100 : 0,
        budgetOccupancyRate: item.budgetAvailableRooms > 0 ? ((item.budgetOccupiedRooms || 0) / item.budgetAvailableRooms) * 100 : 0,
        adr: paidOccupiedRooms > 0 ? item.roomRevenue / paidOccupiedRooms : 0,
        revPAR: item.availableRooms > 0 ? item.roomRevenue / item.availableRooms : 0,
        paidOccupiedRooms: paidOccupiedRooms
      };
    }).sort((a, b) => a.originalDate.localeCompare(b.originalDate));
  };

  const chartData = calculatedMetrics?.daily ? prepareChartData(dashboardData?.byDate || []) : [];
  const comparisonChartData = comparisonMetrics?.daily ? prepareChartData(comparisonMetrics.daily) : [];

  // Per-point series for KPI tile sparklines, covering only the selected timeframe.
  // With a single-day selection chartData has one point, so the sparkline stays hidden.
  const kpiTrends = {
    revPAR: chartData.map(d => d.revPAR),
    adr: chartData.map(d => d.adr),
    occupancyRate: chartData.map(d => d.occupancyRate),
    roomRevenue: chartData.map(d => d.roomRevenue),
    totalRevenue: chartData.map(d => d.totalRevenue),
    hoursPerOccupiedRoom: chartData.map(d => (d.occupiedRooms > 0 ? d.hoursWorked / d.occupiedRooms : 0)),
    dollarPerOccupiedRoom: chartData.map(d => (d.occupiedRooms > 0 ? d.wages / d.occupiedRooms : 0)),
    otPercentage: chartData.map(d => (d.hoursWorked > 0 ? (d.otHours / d.hoursWorked) * 100 : 0)),
  };
  const revenueData = chartData.map((point, index) => {
    let benchmarkRevenue = null;
    if (selectedComparisonMode === 'budget') {
      benchmarkRevenue = point.totalRevenueBudget || point.budgetRevenue || point.roomRevenueBudget || null;
    } else if (selectedComparisonMode === 'forecast') {
      benchmarkRevenue = point.otbForecast || point.rmsForecast || null;
    } else if (comparisonChartData[index]) {
      benchmarkRevenue = comparisonChartData[index].totalRevenue || null;
    }

    return {
      ...point,
      benchmarkRevenue
    };
  });
  const occupancyData = chartData.map((d, index) => {
    let benchmarkOccupancy = null;
    if (selectedComparisonMode === 'budget') {
      benchmarkOccupancy = d.budgetOccupancyRate || null;
    } else if (comparisonChartData[index]) {
      benchmarkOccupancy = comparisonChartData[index].occupancyRate ?? null;
    }

    return {
      day: d.date,
      occupancy: d.occupancyRate,
      benchmarkOccupancy
    };
  });
  const showBenchmarkOccupancy = occupancyData.some(d => d.benchmarkOccupancy !== null && d.benchmarkOccupancy !== undefined);
  const showBenchmarkRevenue = Boolean(comparisonMetrics) &&
    revenueData.some(item => item.benchmarkRevenue !== null && item.benchmarkRevenue !== undefined);

  // bklit time-series shells coerce x values through new Date(); dashboard rows
  // carry display labels ("Aug 5", "Aug 2025"), so synthesize evenly spaced
  // dates for spacing and feed the real labels through xLabels.
  const withSyntheticChartDates = (rows) => rows.map((row, index) => ({
    ...row,
    xDate: new Date(Date.UTC(2024, 0, 1 + index)),
  }));

  // Prepare financial chart data with separate date range and monthly aggregation
  const prepareFinancialChartData = (dailyData) => {
    if (!dailyData || dailyData.length === 0) return [];

    // Apply location filtering if specific locations are selected
    let filteredData = dailyData;
    if (!selectedLocations.includes('all') && selectedLocations.length > 0) {
      const selectedLocationIds = selectedLocations.map(id => parseInt(id));
      filteredData = dailyData.filter(point =>
        point.locationId !== null &&
        point.locationId !== undefined &&
        selectedLocationIds.includes(point.locationId)
      );
    }

    // Note: No need to filter by financial date range here since financialData
    // is already fetched for the correct date range

    // Group by month
    const monthGroups = filteredData.reduce((acc, point) => {
      if (!point.date) return acc;

      // Parse date as local date to avoid timezone issues
      const [dateYear, dateMonth, day] = point.date.split('-').map(num => parseInt(num));
      const pointDate = new Date(dateYear, dateMonth - 1, day); // month is 0-based in Date constructor
      const monthKey = `${pointDate.getFullYear()}-${String(pointDate.getMonth() + 1).padStart(2, '0')}`;

      // Debug: Log a few sample dates to see what's happening
      if (Object.keys(acc).length < 5) {

      }

      // Create consistent month display format
      const year = pointDate.getFullYear();
      const month = pointDate.getMonth(); // 0-based
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthDisplay = `${monthNames[month]} ${year}`;

      if (!acc[monthKey]) {
        acc[monthKey] = {
          date: monthDisplay,
          originalDate: monthKey,
          netIncome: 0,
          revenue: 0,
          expense: 0,
          budgetRevenue: 0,
          budgetExpense: 0,
          count: 0
        };
      }

      acc[monthKey].netIncome += point.netIncome || 0;
      acc[monthKey].revenue += point.revenue || 0;
      acc[monthKey].expense += point.expense || 0;
      acc[monthKey].budgetRevenue += point.budgetRevenue || 0;
      acc[monthKey].budgetExpense += point.budgetExpense || 0;
      acc[monthKey].count += 1;

      return acc;
    }, {});

    // Convert to array and sort by date
    const monthArray = Object.values(monthGroups)
      .sort((a, b) => a.originalDate.localeCompare(b.originalDate));

    // Debug logging to see what months we actually have

    return monthArray;
  };

  const financialChartData = financialData?.byDate ? prepareFinancialChartData(financialData.byDate) : [];

  // Debug: Log what we're working with

  // Calculate financial metrics for the financial date range
  const calculateFinancialMetrics = (financialData) => {
    if (!financialData || financialData.length === 0) {
      return {
        totalRevenue: 0,
        totalExpense: 0,
        totalNetIncome: 0,
        totalBudgetRevenue: 0,
        totalBudgetExpense: 0,
        totalBudgetNetIncome: 0
      };
    }

    const totals = financialData.reduce((acc, month) => {
      acc.revenue += month.revenue || 0;
      acc.expense += month.expense || 0;
      acc.netIncome += month.netIncome || 0;
      acc.budgetRevenue += month.budgetRevenue || 0;
      acc.budgetExpense += month.budgetExpense || 0;
      return acc;
    }, {
      revenue: 0,
      expense: 0,
      netIncome: 0,
      budgetRevenue: 0,
      budgetExpense: 0
    });

    return {
      totalRevenue: totals.revenue,
      totalExpense: totals.expense,
      totalNetIncome: totals.netIncome,
      totalBudgetRevenue: totals.budgetRevenue,
      totalBudgetExpense: totals.budgetExpense,
      totalBudgetNetIncome: totals.budgetRevenue - totals.budgetExpense
    };
  };

  const financialComparisonChartData = financialComparisonData?.byDate ? prepareFinancialChartData(financialComparisonData.byDate) : [];
  const financialComparisonMetrics = calculateFinancialMetrics(financialComparisonChartData);
  const financialComparisonOptions = [
    { key: 'budget', label: 'Budget', longLabel: 'Budget' },
    { key: 'ly', label: 'LY', longLabel: 'Same range last year' }
  ];
  const selectedFinancialComparisonOption =
    financialComparisonOptions.find(option => option.key === selectedFinancialComparisonMode) || financialComparisonOptions[0];
  const financialComparisonLabel = selectedFinancialComparisonOption.label;
  const financialComparisonRange = getFinancialComparisonDateRange(selectedFinancialComparisonMode, financialDateRange);
  const financialComparisonDateText = financialComparisonRange
    ? `${financialComparisonRange.start} to ${financialComparisonRange.end}`
    : `${financialDateRange.start} to ${financialDateRange.end}`;
  const financialMetrics = calculateFinancialMetrics(financialChartData);
  const financialBenchmarkMetrics = {
    revenue: selectedFinancialComparisonMode === 'ly'
      ? financialComparisonMetrics.totalRevenue
      : financialMetrics.totalBudgetRevenue,
    expense: selectedFinancialComparisonMode === 'ly'
      ? financialComparisonMetrics.totalExpense
      : financialMetrics.totalBudgetExpense,
    netIncome: selectedFinancialComparisonMode === 'ly'
      ? financialComparisonMetrics.totalNetIncome
      : financialMetrics.totalBudgetNetIncome
  };
  const buildFinancialKpiComparison = (currentValue, benchmarkValue, metricType = 'revenue') => {
    const benchmark = Number(benchmarkValue || 0);
    const current = Number(currentValue || 0);
    const rawDelta = current - benchmark;
    const favorableDelta = metricType === 'expense' ? benchmark - current : rawDelta;
    const percentChange = benchmark !== 0
      ? (favorableDelta / Math.abs(benchmark)) * 100
      : (current !== 0 ? 100 : 0);
    return {
      formattedValue: formatCurrency(benchmark),
      absoluteDelta: `${favorableDelta >= 0 ? '+' : '-'}${formatCurrency(Math.abs(favorableDelta))}`,
      percentChange,
      label: `vs ${financialComparisonLabel}`,
      dateRange: selectedFinancialComparisonMode === 'ly'
        ? financialComparisonDateText
        : `${financialDateRange.start} to ${financialDateRange.end}`
    };
  };
  const financialKpiComparisons = {
    revenue: buildFinancialKpiComparison(financialMetrics.totalRevenue, financialBenchmarkMetrics.revenue, 'revenue'),
    expense: buildFinancialKpiComparison(financialMetrics.totalExpense, financialBenchmarkMetrics.expense, 'expense'),
    netIncome: buildFinancialKpiComparison(financialMetrics.totalNetIncome, financialBenchmarkMetrics.netIncome, 'netIncome')
  };
  const financialDisplayChartData = financialChartData.map((point, index) => {
    const comparisonPoint = selectedFinancialComparisonMode === 'ly'
      ? financialComparisonChartData[index]
      : null;
    const comparisonRevenue = selectedFinancialComparisonMode === 'ly'
      ? comparisonPoint?.revenue
      : point.budgetRevenue;
    const comparisonExpense = selectedFinancialComparisonMode === 'ly'
      ? comparisonPoint?.expense
      : point.budgetExpense;
    const comparisonNetIncome = selectedFinancialComparisonMode === 'ly'
      ? comparisonPoint?.netIncome
      : (point.budgetRevenue || 0) - (point.budgetExpense || 0);

    return {
      ...point,
      comparisonRevenue: comparisonRevenue ?? null,
      comparisonExpense: comparisonExpense ?? null,
      comparisonNetIncome: comparisonNetIncome ?? null
    };
  });

  // Monthly series for the financial KPI tile sparklines (hidden when a single month is selected)
  const financialKpiTrends = {
    revenue: financialChartData.map(p => p.revenue),
    expense: financialChartData.map(p => p.expense),
    netIncome: financialChartData.map(p => p.netIncome),
  };

  const monoNumStyle = (isTotal) => ({
    fontFamily: 'var(--font-mono)',
    fontSize: 13,
    fontVariantNumeric: 'tabular-nums',
    fontWeight: isTotal ? 600 : 500,
    display: 'block',
    textAlign: 'right',
  });

  const comparisonTableLabel = comparisonMetrics?.label?.replace(/^vs\s+/i, '') || 'Benchmark';

  const getMetricKind = (metricKey) => {
    if (metricKey === 'occupiedRooms' || metricKey === 'compRooms') return 'rooms';
    if (metricKey === 'occupancyRate') return 'percentagePoint';
    return 'currency';
  };

  const getMetricLabel = (metricKey) => ({
    occupiedRooms: 'Occupied Rooms',
    compRooms: 'Comp Rooms',
    occupancyRate: 'Occupancy',
    adr: 'ADR',
    revPAR: 'RevPAR',
    roomRevenue: 'Room Revenue',
    totalRevenue: 'Total Revenue'
  }[metricKey] || metricKey);

  const getDeltaColor = (delta) => {
    if (delta > 0) return '#3F7F4B';
    if (delta < 0) return '#C2412D';
    return 'var(--lodge-onyx-400)';
  };

  const formatSignedCurrency = (value, rounded = false) => {
    const sign = value > 0 ? '+' : value < 0 ? '-' : '';
    const formatted = rounded ? formatCurrencyRounded(Math.abs(value)) : formatCurrency(Math.abs(value));
    return `${sign}${formatted}`;
  };

  const formatSignedNumber = (value, suffix = '') => {
    const sign = value > 0 ? '+' : value < 0 ? '-' : '';
    const absValue = Math.abs(value);
    const formatted = Number.isInteger(absValue) ? absValue.toLocaleString() : absValue.toFixed(1);
    return `${sign}${formatted}${suffix}`;
  };

  const formatSignedPoints = (value) => {
    const sign = value > 0 ? '+' : value < 0 ? '-' : '';
    return `${sign}${Math.abs(value).toFixed(1)}pt`;
  };

  const formatMetricDelta = (current, benchmark, metricKey) => {
    const hasBenchmark = benchmark !== null && benchmark !== undefined && !Number.isNaN(Number(benchmark));
    if (!hasBenchmark) {
      return { label: '-', direction: 'flat', color: 'var(--lodge-onyx-400)', hasBenchmark: false };
    }

    const currentValue = Number(current) || 0;
    const benchmarkValue = Number(benchmark) || 0;
    const delta = currentValue - benchmarkValue;
    const kind = getMetricKind(metricKey);

    if (benchmarkValue === 0) {
      return { label: '-', direction: 'flat', color: 'var(--lodge-onyx-400)', hasBenchmark: false };
    }

    if (performanceDeltaMode === 'absolute' || kind === 'percentagePoint') {
      const label = kind === 'currency'
        ? formatSignedCurrency(delta, metricKey === 'roomRevenue' || metricKey === 'totalRevenue')
        : kind === 'percentagePoint'
        ? formatSignedPoints(delta)
        : formatSignedNumber(delta);

      return {
        label,
        direction: delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat',
        color: getDeltaColor(delta),
        hasBenchmark: true
      };
    }

    const percentDelta = (delta / Math.abs(benchmarkValue)) * 100;
    return {
      label: `${percentDelta > 0 ? '+' : ''}${percentDelta.toFixed(1)}%`,
      direction: percentDelta > 0 ? 'up' : percentDelta < 0 ? 'down' : 'flat',
      color: getDeltaColor(percentDelta),
      hasBenchmark: true
    };
  };

  const getRowHealth = (row) => {
    if (!row || row.isTotal || !row.comparison) {
      return { color: 'transparent', label: 'No benchmark' };
    }

    const current = row.totalRevenue || row.roomRevenue || row.revPAR || 0;
    const benchmark = row.comparison.totalRevenue || row.comparison.roomRevenue || row.comparison.revPAR || 0;
    if (!benchmark) {
      return { color: '#C9C6BE', label: 'Flat / new' };
    }

    const percentDelta = ((current - benchmark) / Math.abs(benchmark)) * 100;
    if (percentDelta >= 15) return { color: '#3F7F4B', label: 'Strong (+15%+)' };
    if (percentDelta >= 5) return { color: '#A5C96B', label: 'Positive (+5-15%)' };
    if (percentDelta >= 0) return { color: '#C9C6BE', label: 'Flat / new' };
    if (percentDelta > -5) return { color: '#EFC6B8', label: 'Weak (-5-0%)' };
    return { color: '#D9534F', label: 'Negative (-5%+)' };
  };

  const getPerformanceTooltipStyle = (tooltip) => {
    const width = 244;
    const estimatedHeight = 264;
    const offset = 14;
    const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1440;
    const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 900;
    const hasRightRoom = tooltip.x + offset + width <= viewportWidth - 12;
    const hasBottomRoom = tooltip.y + offset + estimatedHeight <= viewportHeight - 12;

    return {
      left: hasRightRoom
        ? tooltip.x + offset
        : Math.max(12, tooltip.x - width - offset),
      top: hasBottomRoom
        ? tooltip.y + offset
        : Math.max(12, tooltip.y - estimatedHeight - offset),
      width
    };
  };

  const renderMetricWithBenchmark = (info, metricKey, formatter, valueStyle = {}) => {
    const row = info.row.original || {};
    const isTotal = row.isTotal;
    const value = info.getValue();
    const benchmark = row.comparison?.[metricKey];
    const delta = formatMetricDelta(value, benchmark, metricKey);
    const metricKind = getMetricKind(metricKey);
    const deltaModeLabel = metricKind === 'percentagePoint'
      ? 'Point change'
      : performanceDeltaMode === 'absolute'
      ? (metricKind === 'currency' ? 'Dollar change' : 'Room change')
      : 'Percent change';

    const buildTooltipPayload = (event) => {
      const rect = event.currentTarget.getBoundingClientRect();
      return {
        x: event.clientX || rect.right,
        y: event.clientY || rect.top,
        metric: getMetricLabel(metricKey),
        location: row.locationName,
        comparisonLabel: comparisonTableLabel,
        hasBenchmark: delta.hasBenchmark,
        currentValue: formatter(value),
        comparisonValue: delta.hasBenchmark ? formatter(benchmark) : 'No comparison value',
        deltaLabel: delta.label === '-' ? 'No change' : delta.label,
        direction: delta.direction,
        deltaColor: delta.color,
        deltaModeLabel
      };
    };

    const showTooltip = (event) => {
      setPerformanceTooltip(buildTooltipPayload(event));
    };

    const scheduleTooltip = (event) => {
      pendingPerformanceTooltipRef.current = buildTooltipPayload(event);
      if (performanceTooltipTimerRef.current) return;

      performanceTooltipTimerRef.current = setTimeout(() => {
        setPerformanceTooltip(pendingPerformanceTooltipRef.current);
        performanceTooltipTimerRef.current = null;
      }, 90);
    };

    const updateTooltipPosition = (event) => {
      const nextPayload = buildTooltipPayload(event);
      const nextPosition = { x: nextPayload.x, y: nextPayload.y };
      pendingPerformanceTooltipRef.current = pendingPerformanceTooltipRef.current
        ? { ...pendingPerformanceTooltipRef.current, ...nextPayload }
        : null;
      setPerformanceTooltip(prev => prev ? { ...prev, ...nextPosition } : prev);
    };

    return (
      <div
        onMouseEnter={scheduleTooltip}
        onMouseMove={updateTooltipPosition}
        onMouseLeave={clearPerformanceTooltip}
        onFocus={showTooltip}
        onBlur={clearPerformanceTooltip}
        style={{ textAlign: 'right' }}
      >
        <span style={{ ...monoNumStyle(isTotal), ...valueStyle }}>
          {formatter(value)}
        </span>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 4,
            marginTop: 3,
            color: delta.color,
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            fontVariantNumeric: 'tabular-nums',
            fontWeight: 700,
            whiteSpace: 'nowrap',
            minHeight: 14
          }}
        >
          {delta.hasBenchmark && (
            <>
              {delta.direction === 'up' && <span style={{ fontSize: 10 }}>▲</span>}
              {delta.direction === 'down' && <span style={{ fontSize: 10 }}>▼</span>}
              {delta.direction === 'flat' && <span style={{ color: 'var(--lodge-onyx-400)' }}>-</span>}
              <span>
                {delta.direction === 'flat' ? '' : delta.label}
              </span>
            </>
          )}
        </span>
      </div>
    );
  };

  // Table columns for location-based performance summary
  const columns = [
    {
      accessorKey: 'locationName',
      header: 'Location',
      cell: info => {
        const row = info.row.original || {};
        const health = getRowHealth(row);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span
              title={health.label}
              style={{
                width: 4,
                height: row.isTotal ? 0 : 18,
                borderRadius: 999,
                backgroundColor: health.color,
                flexShrink: 0
              }}
            />
            <span style={{ fontWeight: row.isTotal ? 700 : 600, color: 'var(--lodge-onyx-800)' }}>
              {info.getValue()}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: 'occupiedRooms',
      header: () => <span style={{ display: 'block', textAlign: 'right' }}>Occupied Rooms</span>,
      cell: info => renderMetricWithBenchmark(info, 'occupiedRooms', value => value ? value.toLocaleString() : '0'),
    },
    {
      accessorKey: 'compRooms',
      header: () => <span style={{ display: 'block', textAlign: 'right' }}>Comp Rooms</span>,
      cell: info => renderMetricWithBenchmark(info, 'compRooms', value => value ? value.toLocaleString() : '0'),
    },
    {
      accessorKey: 'occupancyRate',
      header: () => <span style={{ display: 'block', textAlign: 'right' }}>Occupancy %</span>,
      cell: info => {
        const val = info.getValue();
        const color = val >= 70 ? 'var(--color-success)' : val >= 40 ? 'var(--color-warning)' : 'var(--color-error)';
        return renderMetricWithBenchmark(info, 'occupancyRate', formatPercentage, { color });
      },
    },
    {
      accessorKey: 'adr',
      header: () => <span style={{ display: 'block', textAlign: 'right' }}>ADR</span>,
      cell: info => renderMetricWithBenchmark(info, 'adr', formatCurrency),
    },
    {
      accessorKey: 'revPAR',
      header: () => <span style={{ display: 'block', textAlign: 'right' }}>RevPAR</span>,
      cell: info => renderMetricWithBenchmark(info, 'revPAR', formatCurrency),
    },
    {
      accessorKey: 'roomRevenue',
      header: () => <span style={{ display: 'block', textAlign: 'right' }}>Room Revenue</span>,
      cell: info => renderMetricWithBenchmark(info, 'roomRevenue', formatCurrencyRounded),
    },
    {
      accessorKey: 'totalRevenue',
      header: () => <span style={{ display: 'block', textAlign: 'right' }}>Total Revenue</span>,
      cell: info => renderMetricWithBenchmark(info, 'totalRevenue', formatCurrencyRounded),
    },
  ];

  const getFilteredLocationRows = (dailyData = []) => {
    if (!Array.isArray(dailyData)) return [];

    let rows = dailyData.filter(item =>
      item.date !== null &&
      item.locationId !== null &&
      item.locationId !== undefined
    );

    if (!selectedLocations.includes('all') && selectedLocations.length > 0) {
      const selectedLocationIds = selectedLocations.map(id => parseInt(id, 10));
      rows = rows.filter(item => selectedLocationIds.includes(item.locationId));
    }

    return rows;
  };

  const buildLocationMetricMap = (dailyData = [], mode = 'actual') => {
    const rows = getFilteredLocationRows(dailyData);
    const groups = rows.reduce((acc, item) => {
      const locationId = String(item.locationId);
      if (!acc[locationId]) {
        acc[locationId] = {
          roomRevenue: 0,
          totalRevenue: 0,
          occupiedRooms: 0,
          availableRooms: 0,
          compRooms: 0,
          roomRevenueBudget: 0,
          totalRevenueBudget: 0,
          budgetOccupiedRooms: 0,
          budgetAvailableRooms: 0,
          budgetRevenue: 0,
          otbForecast: 0,
          rmsForecast: 0
        };
      }

      acc[locationId].roomRevenue += item.roomRevenue || 0;
      acc[locationId].totalRevenue += item.totalRevenue || 0;
      acc[locationId].occupiedRooms += item.occupiedRooms || 0;
      acc[locationId].availableRooms += item.availableRooms || 0;
      acc[locationId].compRooms += item.compRooms || 0;
      acc[locationId].roomRevenueBudget += item.roomRevenueBudget || 0;
      acc[locationId].totalRevenueBudget += item.totalRevenueBudget || 0;
      acc[locationId].budgetOccupiedRooms += item.budgetOccupiedRooms || 0;
      acc[locationId].budgetAvailableRooms += item.budgetAvailableRooms || 0;
      acc[locationId].budgetRevenue += item.budgetRevenue || 0;
      acc[locationId].otbForecast += item.otbForecast || 0;
      acc[locationId].rmsForecast += item.rmsForecast || 0;
      return acc;
    }, {});

    const toMetrics = (totals) => {
      const paidOccupiedRooms = Math.max(0, totals.occupiedRooms - totals.compRooms);
      const benchmarkOccupiedRooms = mode === 'budget' ? (totals.budgetOccupiedRooms || 0) : paidOccupiedRooms;
      const benchmarkAvailableRooms = mode === 'budget' ? (totals.budgetAvailableRooms || 0) : totals.availableRooms;
      const revenue =
        mode === 'budget'
          ? (totals.roomRevenueBudget || totals.budgetRevenue)
          : mode === 'forecast'
          ? (totals.otbForecast || totals.rmsForecast)
          : totals.roomRevenue;
      const totalRevenue =
        mode === 'budget'
          ? (totals.totalRevenueBudget || totals.budgetRevenue || totals.roomRevenueBudget)
          : mode === 'forecast'
          ? (totals.otbForecast || totals.rmsForecast)
          : totals.totalRevenue;

      return {
        occupiedRooms: mode === 'budget' ? totals.budgetOccupiedRooms : (mode === 'actual' ? totals.occupiedRooms : null),
        availableRooms: mode === 'budget' ? totals.budgetAvailableRooms : (mode === 'actual' ? totals.availableRooms : null),
        compRooms: mode === 'actual' ? totals.compRooms : null,
        occupancyRate: benchmarkAvailableRooms > 0 ? (benchmarkOccupiedRooms / benchmarkAvailableRooms) * 100 : null,
        adr: benchmarkOccupiedRooms > 0 ? revenue / benchmarkOccupiedRooms : null,
        revPAR: benchmarkAvailableRooms > 0 ? revenue / benchmarkAvailableRooms : null,
        roomRevenue: revenue,
        totalRevenue
      };
    };

    const metricMap = Object.fromEntries(
      Object.entries(groups).map(([locationId, totals]) => [locationId, toMetrics(totals)])
    );

    if (Object.keys(groups).length > 1) {
      const totalTotals = Object.values(groups).reduce((acc, totals) => {
        Object.keys(acc).forEach(key => {
          acc[key] += totals[key] || 0;
        });
        return acc;
      }, {
        roomRevenue: 0,
        totalRevenue: 0,
        occupiedRooms: 0,
        availableRooms: 0,
        compRooms: 0,
        roomRevenueBudget: 0,
        totalRevenueBudget: 0,
        budgetOccupiedRooms: 0,
        budgetAvailableRooms: 0,
        budgetRevenue: 0,
        otbForecast: 0,
        rmsForecast: 0
      });
      metricMap.__total = toMetrics(totalTotals);
    }

    return metricMap;
  };

  const getLocationBenchmarkMap = () => {
    if (!comparisonMetrics) return {};

    if (selectedComparisonMode === 'budget' || selectedComparisonMode === 'forecast') {
      return buildLocationMetricMap(dashboardData?.byDate || [], selectedComparisonMode);
    }

    return buildLocationMetricMap(comparisonData || [], 'actual');
  };

  // Prepare location-based table data
  const prepareLocationTableData = () => {
    if (!dashboardData?.byDate || !calculatedMetrics) return [];
    const benchmarkMap = getLocationBenchmarkMap();

    // If specific locations are selected, show only those locations
    if (!selectedLocations.includes('all') && selectedLocations.length > 0) {
      const selectedLocationIds = selectedLocations.map(id => parseInt(id));
      const locationData = dashboardData.byDate.filter(item =>
        selectedLocationIds.includes(item.locationId) &&
        item.date !== null &&
        item.locationId !== null &&
        item.locationId !== undefined
      );

      if (locationData.length === 0) return [];

      // Group data by location for selected locations
      const locationGroups = {};

      locationData.forEach(item => {
        const locationId = item.locationId;

        if (!locationGroups[locationId]) {
          locationGroups[locationId] = {
            roomRevenue: 0,
            totalRevenue: 0,
            occupiedRooms: 0,
            availableRooms: 0,
            compRooms: 0
          };
        }

        locationGroups[locationId].roomRevenue += item.roomRevenue || 0;
        locationGroups[locationId].totalRevenue += item.totalRevenue || 0;
        locationGroups[locationId].occupiedRooms += item.occupiedRooms || 0;
        locationGroups[locationId].availableRooms += item.availableRooms || 0;
        locationGroups[locationId].compRooms += item.compRooms || 0;
      });

      // Convert to table format with calculated metrics
      const filteredLocationData = Object.entries(locationGroups).map(([locationId, totals]) => {
        const paidOccupiedRooms = Math.max(0, totals.occupiedRooms - totals.compRooms);

        return {
          locationId: parseInt(locationId),
          locationName: getLocationDisplayName(locationId, locationNames[parseInt(locationId)]),
          adr: paidOccupiedRooms > 0 ? totals.roomRevenue / paidOccupiedRooms : 0,
          occupiedRooms: totals.occupiedRooms,
          compRooms: totals.compRooms,
          revPAR: totals.availableRooms > 0 ? totals.roomRevenue / totals.availableRooms : 0,
          totalRevenue: totals.totalRevenue,
          roomRevenue: totals.roomRevenue,
          occupancyRate: totals.availableRooms > 0 ? (paidOccupiedRooms / totals.availableRooms) * 100 : 0,
          comparison: benchmarkMap[String(locationId)] || null
        };
      });

      return filteredLocationData.sort((a, b) => a.locationName.localeCompare(b.locationName));
    }

    // Group data by location for all locations
    const locationGroups = {};

    dashboardData.byDate.forEach(item => {
      if (item.date !== null && item.locationId !== null && item.locationId !== undefined) {
        const locationId = item.locationId;

        if (!locationGroups[locationId]) {
          locationGroups[locationId] = {
            roomRevenue: 0,
            totalRevenue: 0,
            occupiedRooms: 0,
            availableRooms: 0,
            compRooms: 0
          };
        }

        locationGroups[locationId].roomRevenue += item.roomRevenue || 0;
        locationGroups[locationId].totalRevenue += item.totalRevenue || 0;
        locationGroups[locationId].occupiedRooms += item.occupiedRooms || 0;
        locationGroups[locationId].availableRooms += item.availableRooms || 0;
        locationGroups[locationId].compRooms += item.compRooms || 0;
      }
    });

    // Convert to table format with calculated metrics
    const locationData = Object.entries(locationGroups).map(([locationId, totals]) => {
      const paidOccupiedRooms = Math.max(0, totals.occupiedRooms - totals.compRooms);

      return {
        locationId: parseInt(locationId),
        locationName: getLocationDisplayName(locationId, locationNames[parseInt(locationId)]),
        adr: paidOccupiedRooms > 0 ? totals.roomRevenue / paidOccupiedRooms : 0,
        occupiedRooms: totals.occupiedRooms,
        compRooms: totals.compRooms,
        revPAR: totals.availableRooms > 0 ? totals.roomRevenue / totals.availableRooms : 0,
        totalRevenue: totals.totalRevenue,
        roomRevenue: totals.roomRevenue,
        occupancyRate: totals.availableRooms > 0 ? (paidOccupiedRooms / totals.availableRooms) * 100 : 0,
        comparison: benchmarkMap[String(locationId)] || null
      };
    });

    // Sort by location name
    const sortedData = locationData.sort((a, b) => a.locationName.localeCompare(b.locationName));

    // Add total row if there are multiple locations
    if (sortedData.length > 1) {
      // Calculate totals from the original data, not from the processed location data
      const totals = dashboardData.byDate.reduce((acc, point) => {
        if (point.date !== null && point.locationId !== null && point.locationId !== undefined) {
          acc.roomRevenue += point.roomRevenue || 0;
          acc.totalRevenue += point.totalRevenue || 0;
          acc.occupiedRooms += point.occupiedRooms || 0;
          acc.availableRooms += point.availableRooms || 0;
          acc.compRooms += point.compRooms || 0;
        }
        return acc;
      }, {
        occupiedRooms: 0,
        compRooms: 0,
        totalRevenue: 0,
        roomRevenue: 0,
        availableRooms: 0
      });

      const totalPaidOccupiedRooms = Math.max(0, totals.occupiedRooms - totals.compRooms);

      sortedData.push({
        locationName: 'TOTAL',
        adr: totalPaidOccupiedRooms > 0 ? totals.roomRevenue / totalPaidOccupiedRooms : 0,
        occupiedRooms: totals.occupiedRooms,
        compRooms: totals.compRooms,
        revPAR: totals.availableRooms > 0 ? totals.roomRevenue / totals.availableRooms : 0,
        totalRevenue: totals.totalRevenue,
        roomRevenue: totals.roomRevenue,
        occupancyRate: totals.availableRooms > 0 ? (totalPaidOccupiedRooms / totals.availableRooms) * 100 : 0,
        comparison: benchmarkMap.__total || null,
        isTotal: true // Flag to style this row differently
      });
    }

    return sortedData;
  };

  const tableData = prepareLocationTableData();

  // Prepare operations performance table data by location
  const prepareOperationsTableData = () => {
    if (!dashboardData?.byDate || !calculatedMetrics) return [];

    // Apply location filtering
    let filteredData = dashboardData.byDate.filter(item =>
      item.date !== null &&
      item.locationId !== null &&
      item.locationId !== undefined
    );

    if (!selectedLocations.includes('all') && selectedLocations.length > 0) {
      const selectedLocationIds = selectedLocations.map(id => parseInt(id));
      filteredData = filteredData.filter(item => selectedLocationIds.includes(item.locationId));
    }

    if (filteredData.length === 0) return [];

    // Group by location
    const locationGroups = {};
    filteredData.forEach(item => {
      const locationId = item.locationId;
      if (!locationGroups[locationId]) {
        locationGroups[locationId] = {
          hoursWorked: 0,
          budgetedHours: 0,
          otHours: 0,
          wages: 0,
          occupiedRooms: 0
        };
      }

      locationGroups[locationId].hoursWorked += item.hoursWorked || 0;
      locationGroups[locationId].budgetedHours += item.budgetedHours || 0;
      locationGroups[locationId].otHours += item.otHours || 0;
      locationGroups[locationId].wages += item.wages || 0;
      locationGroups[locationId].occupiedRooms += item.occupiedRooms || 0;
    });

    // Convert to table format
    const operationsData = Object.entries(locationGroups).map(([locationId, totals]) => {
      const otPercentage = totals.hoursWorked > 0 ? (totals.otHours / totals.hoursWorked) * 100 : 0;
      const hpor = totals.occupiedRooms > 0 ? totals.hoursWorked / totals.occupiedRooms : 0;
      const dpor = totals.occupiedRooms > 0 ? totals.wages / totals.occupiedRooms : 0;

      return {
        locationId: parseInt(locationId),
        locationName: getLocationDisplayName(locationId, locationNames[parseInt(locationId)]),
        hoursWorked: Math.round(totals.hoursWorked),
        budgetedHours: Math.round(totals.budgetedHours),
        otHours: Math.round(totals.otHours * 100) / 100,
        otPercentage,
        wages: totals.wages,
        dpor,
        hpor
      };
    });

    const sortedData = operationsData.sort((a, b) => a.locationName.localeCompare(b.locationName));

    // Add total row if there are multiple locations
    if (sortedData.length > 1) {
      const totals = filteredData.reduce((acc, item) => {
        acc.hoursWorked += item.hoursWorked || 0;
        acc.budgetedHours += item.budgetedHours || 0;
        acc.otHours += item.otHours || 0;
        acc.wages += item.wages || 0;
        acc.occupiedRooms += item.occupiedRooms || 0;
        return acc;
      }, {
        hoursWorked: 0,
        budgetedHours: 0,
        otHours: 0,
        wages: 0,
        occupiedRooms: 0
      });

      const totalOtPercentage = totals.hoursWorked > 0 ? (totals.otHours / totals.hoursWorked) * 100 : 0;
      const totalHpor = totals.occupiedRooms > 0 ? totals.hoursWorked / totals.occupiedRooms : 0;
      const totalDpor = totals.occupiedRooms > 0 ? totals.wages / totals.occupiedRooms : 0;

      sortedData.push({
        locationName: 'TOTAL',
        hoursWorked: Math.round(totals.hoursWorked),
        budgetedHours: Math.round(totals.budgetedHours),
        otHours: Math.round(totals.otHours * 100) / 100,
        otPercentage: totalOtPercentage,
        wages: totals.wages,
        dpor: totalDpor,
        hpor: totalHpor,
        isTotal: true
      });
    }

    return sortedData;
  };

  // Prepare financial performance table data by location
  const prepareFinancialTableData = () => {
    if (!financialData?.byDate || !financialMetrics) return [];

    // Apply location filtering
    let filteredData = financialData.byDate.filter(item =>
      item.date !== null &&
      item.locationId !== null &&
      item.locationId !== undefined
    );

    if (!selectedLocations.includes('all') && selectedLocations.length > 0) {
      const selectedLocationIds = selectedLocations.map(id => parseInt(id));
      filteredData = filteredData.filter(item => selectedLocationIds.includes(item.locationId));
    }

    if (filteredData.length === 0) return [];

    const buildFinancialLocationGroups = (rows) => {
      const groups = {};
      rows.forEach(item => {
        if (item.locationId === null || item.locationId === undefined) return;
        const locationId = item.locationId;
        if (!groups[locationId]) {
          groups[locationId] = {
            revenue: 0,
            budgetRevenue: 0,
            expense: 0,
            budgetExpense: 0,
            netIncome: 0,
            budgetNetIncome: 0
          };
        }

        groups[locationId].revenue += item.revenue || 0;
        groups[locationId].budgetRevenue += item.budgetRevenue || 0;
        groups[locationId].expense += item.expense || 0;
        groups[locationId].budgetExpense += item.budgetExpense || 0;
        groups[locationId].netIncome += item.netIncome || 0;
        groups[locationId].budgetNetIncome += item.budgetNetIncome ?? ((item.budgetRevenue || 0) - (item.budgetExpense || 0));
      });
      return groups;
    };

    const comparisonRows = selectedFinancialComparisonMode === 'ly' && financialComparisonData?.byDate
      ? financialComparisonData.byDate.filter(item =>
          item.date !== null &&
          item.locationId !== null &&
          item.locationId !== undefined
        )
      : [];
    const selectedLocationIds = selectedLocations.map(id => parseInt(id));
    const filteredComparisonRows = (!selectedLocations.includes('all') && selectedLocations.length > 0)
      ? comparisonRows.filter(item => selectedLocationIds.includes(item.locationId))
      : comparisonRows;

    // Group by location
    const locationGroups = {};
    filteredData.forEach(item => {
      const locationId = item.locationId;
      if (!locationGroups[locationId]) {
        locationGroups[locationId] = {
          revenue: 0,
          budgetRevenue: 0,
          expense: 0,
          budgetExpense: 0,
          netIncome: 0,
          budgetNetIncome: 0
        };
      }

      locationGroups[locationId].revenue += item.revenue || 0;
      locationGroups[locationId].budgetRevenue += item.budgetRevenue || 0;
      locationGroups[locationId].expense += item.expense || 0;
      locationGroups[locationId].budgetExpense += item.budgetExpense || 0;
      locationGroups[locationId].netIncome += item.netIncome || 0;
      locationGroups[locationId].budgetNetIncome += item.budgetNetIncome ?? ((item.budgetRevenue || 0) - (item.budgetExpense || 0));
    });
    const comparisonLocationGroups = selectedFinancialComparisonMode === 'ly'
      ? buildFinancialLocationGroups(filteredComparisonRows)
      : null;

    // Convert to table format
    const financialDataTable = Object.entries(locationGroups).map(([locationId, totals]) => {
      const benchmarkTotals = comparisonLocationGroups?.[locationId];
      const benchmarkRevenue = benchmarkTotals ? benchmarkTotals.revenue : totals.budgetRevenue;
      const benchmarkExpense = benchmarkTotals ? benchmarkTotals.expense : totals.budgetExpense;
      const benchmarkNetIncome = benchmarkTotals ? benchmarkTotals.netIncome : totals.budgetNetIncome;
      const revenueVariance = totals.revenue - benchmarkRevenue;
      const expenseVariance = benchmarkExpense - totals.expense;
      const netIncomeVariance = totals.netIncome - benchmarkNetIncome;

      return {
        locationId: parseInt(locationId),
        locationName: getLocationDisplayName(locationId, locationNames[parseInt(locationId)]),
        revenue: totals.revenue,
        budgetRevenue: benchmarkRevenue,
        revenueVariance,
        expense: totals.expense,
        budgetExpense: benchmarkExpense,
        expenseVariance,
        netIncome: totals.netIncome,
        budgetNetIncome: benchmarkNetIncome,
        netIncomeVariance
      };
    });

    const sortedData = financialDataTable.sort((a, b) => a.locationName.localeCompare(b.locationName));

    // Add total row if there are multiple locations
    if (sortedData.length > 1) {
      const totals = filteredData.reduce((acc, item) => {
        acc.revenue += item.revenue || 0;
        acc.budgetRevenue += item.budgetRevenue || 0;
        acc.expense += item.expense || 0;
        acc.budgetExpense += item.budgetExpense || 0;
        acc.netIncome += item.netIncome || 0;
        acc.budgetNetIncome += item.budgetNetIncome ?? ((item.budgetRevenue || 0) - (item.budgetExpense || 0));
        return acc;
      }, {
        revenue: 0,
        budgetRevenue: 0,
        expense: 0,
        budgetExpense: 0,
        netIncome: 0,
        budgetNetIncome: 0
      });

      const comparisonTotals = comparisonLocationGroups
        ? Object.values(comparisonLocationGroups).reduce((acc, item) => {
            acc.revenue += item.revenue || 0;
            acc.expense += item.expense || 0;
            acc.netIncome += item.netIncome || 0;
            return acc;
          }, { revenue: 0, expense: 0, netIncome: 0 })
        : null;
      const benchmarkRevenue = comparisonTotals ? comparisonTotals.revenue : totals.budgetRevenue;
      const benchmarkExpense = comparisonTotals ? comparisonTotals.expense : totals.budgetExpense;
      const benchmarkNetIncome = comparisonTotals ? comparisonTotals.netIncome : totals.budgetNetIncome;
      const totalRevenueVariance = totals.revenue - benchmarkRevenue;
      const totalExpenseVariance = benchmarkExpense - totals.expense;
      const totalNetIncomeVariance = totals.netIncome - benchmarkNetIncome;

      sortedData.push({
        locationName: 'TOTAL',
        revenue: totals.revenue,
        budgetRevenue: benchmarkRevenue,
        revenueVariance: totalRevenueVariance,
        expense: totals.expense,
        budgetExpense: benchmarkExpense,
        expenseVariance: totalExpenseVariance,
        netIncome: totals.netIncome,
        budgetNetIncome: benchmarkNetIncome,
        netIncomeVariance: totalNetIncomeVariance,
        isTotal: true
      });
    }

    return sortedData;
  };

  const operationsTableData = prepareOperationsTableData();
  const financialTableData = prepareFinancialTableData();

  // ----------------------------------------------------------------
  // PerformanceSummary adapter: convert legacy *TableData rows into
  // the shape the new component expects (per-cell value + delta + tone).
  // ----------------------------------------------------------------

  const PERF_NEUTRAL_PCT = 1.5; // |%| <= 1.5% is treated as flat

  const getInitials = (name) => {
    if (!name || typeof name !== 'string') return '—';
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return '—';
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
  };

  // 'pos' | 'neg' | 'warn' | 'neu' from a raw delta + benchmark + inversion flag
  const cellTone = (value, benchmark, { inverted = false } = {}) => {
    if (
      value == null ||
      benchmark == null ||
      Number.isNaN(Number(value)) ||
      Number.isNaN(Number(benchmark)) ||
      benchmark === 0
    ) {
      return 'neu';
    }
    const delta = Number(value) - Number(benchmark);
    const pct = (delta / Math.abs(benchmark)) * 100;
    if (Math.abs(pct) <= PERF_NEUTRAL_PCT) return 'neu';
    if (inverted) {
      // Costs / cost-like metrics: down is good
      if (pct < -PERF_NEUTRAL_PCT) return 'pos';
      if (pct > 5) return 'neg';
      return 'warn';
    }
    return pct > 0 ? 'pos' : 'neg';
  };

  // Build the PerformanceSummary `cells` shape for a single row.
  // Each cell includes a rich `tooltipPayload` consumed by the custom
  // floating tooltip portal at the bottom of the dashboard.
  const buildPerfCells = (
    row,
    columns,
    {
      comparisonLabel = 'Benchmark',
      locationName = '',
      tableSource = null,
      locationId = null,
      isTotal = false,
    } = {}
  ) => {
    const cells = {};
    const fmtForCol = (col, v) => {
      switch (col.fmt) {
        case 'currency':        return formatCurrency(v ?? 0);
        case 'currencyRounded': return formatCurrencyRounded(v ?? 0);
        case 'percent':         return `${Number(v ?? 0).toFixed(1)}%`;
        case 'pts':             return `${Number(v ?? 0).toFixed(1)} pts`;
        case 'number':          return Number(v ?? 0).toFixed(2);
        case 'integer':         return Math.round(Number(v ?? 0)).toLocaleString();
        case 'hours':           return Number(v ?? 0).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
        default:                return v == null ? '—' : String(v);
      }
    };

    columns.forEach((col) => {
      const value = row?.[col.key];
      let benchmark = null;
      if (!col.varianceOnly) {
        const bkey = col.benchmarkKey || col.key;
        benchmark = row?.comparison?.[bkey];
        if (benchmark == null && row?.[bkey + 'Benchmark'] != null) {
          benchmark = row[bkey + 'Benchmark'];
        }
        // Financial rows store budget* directly on the row (not in row.comparison).
        if (benchmark == null && row?.[bkey] != null && bkey !== col.key) {
          benchmark = row[bkey];
        }
      }

      let delta = null;
      let deltaPct = null;
      if (!col.varianceOnly && value != null && benchmark != null && Number(benchmark) !== 0) {
        delta = Number(value) - Number(benchmark);
        deltaPct = (delta / Math.abs(Number(benchmark))) * 100;
      }

      let tone;
      if (col.varianceOnly) {
        const v = Number(value) || 0;
        if (Math.abs(v) < 1) tone = 'neu';
        else if (col.inverted) tone = v < 0 ? 'pos' : v > 0 ? 'neg' : 'neu';
        else tone = v > 0 ? 'pos' : v < 0 ? 'neg' : 'neu';
      } else {
        tone = cellTone(value, benchmark, { inverted: col.inverted });
      }

      // Common fields the sparkline-aware tooltip portal uses to look up
      // the daily series for this metric/location.
      const sparklineLookup = tableSource
        ? {
            tableSource,
            locationId,
            isTotal,
            metricKey: col.key,
            metricFmt: col.fmt,
            inverted: !!col.inverted,
          }
        : null;

      // Rich tooltip payload for the floating portal.
      let tooltipPayload = null;
      if (col.varianceOnly) {
        const v = Number(value) || 0;
        const favorable = col.inverted ? v < 0 : v > 0;
        const direction = v > 0 ? 'up' : v < 0 ? 'down' : 'flat';
        const deltaColor = v === 0
          ? 'var(--lodge-onyx-400)'
          : favorable ? 'var(--color-success, #12B76A)' : 'var(--color-error, #F04438)';
        tooltipPayload = {
          ...(sparklineLookup || {}),
          metric: col.label,
          location: locationName,
          comparisonLabel,
          hasBenchmark: true,
          currentValue: fmtForCol(col, value),
          comparisonValue: '—',
          deltaLabel: `${v > 0 ? '+' : v < 0 ? '−' : ''}${fmtForCol(col, Math.abs(v))}`,
          deltaModeLabel: 'Variance',
          direction,
          deltaColor,
        };
      } else if (benchmark != null) {
        const direction = delta == null || delta === 0 ? 'flat' : delta > 0 ? 'up' : 'down';
        const favorable = col.inverted ? (delta != null && delta < 0) : (delta != null && delta > 0);
        const deltaColor = delta == null || delta === 0
          ? 'var(--lodge-onyx-400)'
          : favorable ? 'var(--color-success, #12B76A)' : 'var(--color-error, #F04438)';
        let deltaModeLabel = 'Change';
        if (performanceDeltaMode === 'percent') deltaModeLabel = 'Percent change';
        else if (col.fmt === 'percent' || col.fmt === 'pts') deltaModeLabel = 'Point change';
        else if (col.fmt === 'integer' || col.fmt === 'hours') deltaModeLabel = 'Unit change';
        else if (col.fmt === 'currency' || col.fmt === 'currencyRounded') deltaModeLabel = 'Dollar change';
        const deltaLabel = delta == null
          ? '—'
          : performanceDeltaMode === 'percent'
            ? `${delta > 0 ? '+' : delta < 0 ? '−' : ''}${Math.abs(deltaPct).toFixed(1)}%`
            : `${delta > 0 ? '+' : delta < 0 ? '−' : ''}${fmtForCol(col, Math.abs(delta))}`;
        tooltipPayload = {
          ...(sparklineLookup || {}),
          metric: col.label,
          location: locationName,
          comparisonLabel,
          hasBenchmark: true,
          currentValue: fmtForCol(col, value),
          comparisonValue: fmtForCol(col, benchmark),
          deltaLabel,
          deltaModeLabel,
          direction,
          deltaColor,
        };
      } else if (value != null) {
        tooltipPayload = {
          ...(sparklineLookup || {}),
          metric: col.label,
          location: locationName,
          comparisonLabel,
          hasBenchmark: false,
          currentValue: fmtForCol(col, value),
          comparisonValue: 'No comparison value',
          deltaLabel: '—',
          deltaModeLabel: 'Change',
          direction: 'flat',
          deltaColor: 'var(--lodge-onyx-400)',
        };
      }

      cells[col.key] = { value, delta, deltaPct, tone, tooltipPayload };
    });
    return cells;
  };

  // Row-level tone driven by a primary metric per tab.
  // If `primaryIsVariance` is true, derive tone from the value's sign directly
  // (the row property is already a variance number, no benchmark needed).
  const buildRowTone = (row, primaryKey, { inverted = false, primaryIsVariance = false } = {}) => {
    if (!row || row.isTotal) return 'neu';
    const value = row?.[primaryKey];
    if (primaryIsVariance) {
      const v = Number(value) || 0;
      if (Math.abs(v) < 1) return 'neu';
      if (inverted) return v < 0 ? 'pos' : 'neg';
      return v > 0 ? 'pos' : 'neg';
    }
    const benchmark = row?.comparison?.[primaryKey];
    return cellTone(value, benchmark, { inverted });
  };

  // Top-level adapter — produces an array of PerformanceSummary rows.
  const buildPerformanceRows = (tableData, columns, options = {}) => {
    if (!Array.isArray(tableData)) return [];
    const {
      primaryKey,
      primaryInverted = false,
      primaryIsVariance = false,
      comparisonLabel = 'Benchmark',
      tableSource = null,
    } = options;
    return tableData.map((row, idx) => {
      const isTotal = !!row.isTotal;
      const name = row.locationName || (isTotal ? 'TOTAL' : `Row ${idx + 1}`);
      const flag = isTotal ? 'PF' : getInitials(name);
      const rowTone = isTotal
        ? 'neu'
        : (primaryKey
          ? buildRowTone(row, primaryKey, { inverted: primaryInverted, primaryIsVariance })
          : 'neu');
      return {
        id: row.locationId ?? (isTotal ? 'total' : `row-${idx}`),
        flag,
        name: isTotal ? 'Portfolio' : name,
        rowTone,
        isTotal,
        cells: buildPerfCells(row, columns, {
          comparisonLabel,
          locationName: isTotal ? 'Portfolio' : name,
          tableSource,
          locationId: row.locationId ?? null,
          isTotal,
        }),
      };
    });
  };

  // Hover handlers shared by every PerformanceSummary instance — they drive the
  // existing performanceTooltip floating portal at the bottom of this file.
  const performanceCellHover = {
    onEnter: (payload, event) => {
      const next = { ...payload, x: event.clientX, y: event.clientY };
      pendingPerformanceTooltipRef.current = next;
      if (!performanceTooltipTimerRef.current) {
        performanceTooltipTimerRef.current = setTimeout(() => {
          setPerformanceTooltip(pendingPerformanceTooltipRef.current);
          performanceTooltipTimerRef.current = null;
        }, 90);
      }
    },
    onMove: (payload, event) => {
      if (pendingPerformanceTooltipRef.current) {
        pendingPerformanceTooltipRef.current = {
          ...pendingPerformanceTooltipRef.current,
          x: event.clientX,
          y: event.clientY,
        };
      }
      setPerformanceTooltip((prev) => (prev ? { ...prev, x: event.clientX, y: event.clientY } : prev));
    },
    onLeave: clearPerformanceTooltip,
  };

  // ----------------------------------------------------------------
  // Sparkline series builder — used by the tooltip portal to render a
  // small per-cell trend chart with per-point favorability coloring.
  // ----------------------------------------------------------------

  const computeSparklineValue = (record, metricKey) => {
    if (!record) return null;
    const num = (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    };
    switch (metricKey) {
      case 'adr': {
        const occ = num(record.occupiedRooms);
        return occ > 0 ? num(record.roomRevenue) / occ : null;
      }
      case 'revPAR': {
        const avail = num(record.availableRooms);
        return avail > 0 ? num(record.roomRevenue) / avail : null;
      }
      case 'occupancyRate': {
        const avail = num(record.availableRooms);
        return avail > 0 ? (num(record.occupiedRooms) / avail) * 100 : null;
      }
      case 'otPercentage': {
        const hw = num(record.hoursWorked);
        return hw > 0 ? (num(record.otHours) / hw) * 100 : null;
      }
      case 'hpor': {
        const occ = num(record.occupiedRooms);
        return occ > 0 ? num(record.hoursWorked) / occ : null;
      }
      case 'dpor': {
        const occ = num(record.occupiedRooms);
        return occ > 0 ? num(record.wages) / occ : null;
      }
      case 'revenueVariance': {
        return num(record.revenue) - num(record.budgetRevenue);
      }
      case 'expenseVariance': {
        return num(record.budgetExpense) - num(record.expense);
      }
      case 'netIncomeVariance': {
        return num(record.netIncome) - num(record.budgetNetIncome);
      }
      default: {
        const raw = record[metricKey];
        if (raw == null) return null;
        const n = Number(raw);
        return Number.isFinite(n) ? n : null;
      }
    }
  };

  // Resolves the budget/benchmark counterpart of a metric from the SAME
  // record, used when comparisonData is null (e.g. budget compare mode).
  const computeBudgetSparklineValue = (record, metricKey) => {
    if (!record) return null;
    const num = (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    };
    switch (metricKey) {
      case 'totalRevenue':   return record.totalRevenueBudget != null ? num(record.totalRevenueBudget) : null;
      case 'roomRevenue':    return record.roomRevenueBudget != null ? num(record.roomRevenueBudget) : null;
      case 'occupiedRooms':  return record.budgetOccupiedRooms != null ? num(record.budgetOccupiedRooms) : null;
      case 'occupancyRate': {
        const avail = num(record.budgetAvailableRooms);
        return avail > 0 ? (num(record.budgetOccupiedRooms) / avail) * 100 : null;
      }
      case 'adr': {
        const occ = num(record.budgetOccupiedRooms);
        return occ > 0 ? num(record.roomRevenueBudget) / occ : null;
      }
      case 'revPAR': {
        const avail = num(record.budgetAvailableRooms);
        return avail > 0 ? num(record.roomRevenueBudget) / avail : null;
      }
      case 'hoursWorked':    return record.budgetedHours != null ? num(record.budgetedHours) : null;
      case 'hpor': {
        const occ = num(record.budgetOccupiedRooms);
        return occ > 0 ? num(record.budgetedHours) / occ : null;
      }
      case 'revenue':   return record.budgetRevenue != null ? num(record.budgetRevenue) : null;
      case 'expense':   return record.budgetExpense != null ? num(record.budgetExpense) : null;
      case 'netIncome': return record.budgetNetIncome != null ? num(record.budgetNetIncome) : null;
      default: return null;
    }
  };

  const computeBudgetTotalSparklineValue = (records, metricKey) => {
    if (!records || records.length === 0) return null;
    const sum = (k) => records.reduce((acc, r) => acc + (Number(r?.[k]) || 0), 0);
    switch (metricKey) {
      case 'totalRevenue':   return sum('totalRevenueBudget');
      case 'roomRevenue':    return sum('roomRevenueBudget');
      case 'occupiedRooms':  return sum('budgetOccupiedRooms');
      case 'occupancyRate': {
        const avail = sum('budgetAvailableRooms');
        return avail > 0 ? (sum('budgetOccupiedRooms') / avail) * 100 : null;
      }
      case 'adr': {
        const occ = sum('budgetOccupiedRooms');
        return occ > 0 ? sum('roomRevenueBudget') / occ : null;
      }
      case 'revPAR': {
        const avail = sum('budgetAvailableRooms');
        return avail > 0 ? sum('roomRevenueBudget') / avail : null;
      }
      case 'hoursWorked':    return sum('budgetedHours');
      case 'hpor': {
        const occ = sum('budgetOccupiedRooms');
        return occ > 0 ? sum('budgetedHours') / occ : null;
      }
      case 'revenue':   return sum('budgetRevenue');
      case 'expense':   return sum('budgetExpense');
      case 'netIncome': return sum('budgetNetIncome');
      default: return null;
    }
  };

  const computeTotalSparklineValue = (records, metricKey) => {
    if (!records || records.length === 0) return null;
    const sum = (k) => records.reduce((acc, r) => acc + (Number(r?.[k]) || 0), 0);
    switch (metricKey) {
      case 'adr': {
        const occ = sum('occupiedRooms');
        return occ > 0 ? sum('roomRevenue') / occ : null;
      }
      case 'revPAR': {
        const avail = sum('availableRooms');
        return avail > 0 ? sum('roomRevenue') / avail : null;
      }
      case 'occupancyRate': {
        const avail = sum('availableRooms');
        return avail > 0 ? (sum('occupiedRooms') / avail) * 100 : null;
      }
      case 'otPercentage': {
        const hw = sum('hoursWorked');
        return hw > 0 ? (sum('otHours') / hw) * 100 : null;
      }
      case 'hpor': {
        const occ = sum('occupiedRooms');
        return occ > 0 ? sum('hoursWorked') / occ : null;
      }
      case 'dpor': {
        const occ = sum('occupiedRooms');
        return occ > 0 ? sum('wages') / occ : null;
      }
      case 'revenueVariance':
        return sum('revenue') - sum('budgetRevenue');
      case 'expenseVariance':
        return sum('budgetExpense') - sum('expense');
      case 'netIncomeVariance':
        return sum('netIncome') - sum('budgetNetIncome');
      default:
        return sum(metricKey);
    }
  };

  const buildSparklineSeries = (payload) => {
    if (!payload || !payload.metricKey || !payload.tableSource) return null;
    const { tableSource, locationId, isTotal, metricKey } = payload;

    let actualRows = [];
    let compRows = [];
    if (tableSource === 'revenue' || tableSource === 'operations') {
      actualRows = Array.isArray(dashboardData?.byDate) ? dashboardData.byDate : [];
      compRows = Array.isArray(comparisonData) ? comparisonData : [];
    } else if (tableSource === 'financial') {
      actualRows = Array.isArray(financialData?.byDate) ? financialData.byDate : [];
      compRows = Array.isArray(financialComparisonData?.byDate) ? financialComparisonData.byDate : [];
    }

    // Limit selection: by location for body rows, all locations summed for totals.
    const filterByLoc = (rows) => {
      if (isTotal || locationId == null) {
        if (!selectedLocations.includes('all') && selectedLocations.length > 0) {
          const sel = selectedLocations.map((id) => parseInt(id));
          return rows.filter((r) => sel.includes(r.locationId));
        }
        return rows;
      }
      return rows.filter((r) => r.locationId === locationId);
    };

    const actual = filterByLoc(actualRows);
    const comp = filterByLoc(compRows);

    // Group by date and compute the metric value. `mode` selects the
    // value resolver: 'actual' uses the row's primary metric; 'budget'
    // pulls the budget counterpart off the same record (used when there
    // is no separate comparison series — e.g. budget/forecast compare).
    // The financial tab spans longer windows (up to trailing 12 months) so
    // we bucket by month to keep the sparkline readable.
    const bucketByMonth = tableSource === 'financial';
    const bucketKey = (raw) => {
      const date = raw?.effectiveDate || raw?.date;
      if (!date) return null;
      return bucketByMonth ? String(date).slice(0, 7) : date;
    };
    const groupByDate = (rows, mode = 'actual') => {
      const buckets = new Map();
      rows.forEach((r) => {
        const key = bucketKey(r);
        if (!key) return;
        if (!buckets.has(key)) buckets.set(key, []);
        buckets.get(key).push(r);
      });
      const resolveTotal = mode === 'budget' ? computeBudgetTotalSparklineValue : computeTotalSparklineValue;
      const resolveSingle = mode === 'budget' ? computeBudgetSparklineValue : computeSparklineValue;
      return [...buckets.entries()]
        .sort((a, b) => String(a[0]).localeCompare(String(b[0])))
        .map(([date, recs]) => ({
          date,
          // For monthly buckets, sum the bucketed records (always use the
          // total-style resolver) so multi-day rows roll into one figure.
          value: (isTotal || bucketByMonth)
            ? resolveTotal(recs, metricKey)
            : resolveSingle(recs[0], metricKey),
        }));
    };

    const actualSeries = groupByDate(actual, 'actual');
    // Use the separate comp rows if we got any; otherwise derive the benchmark
    // from budget-on-actual fields (the shape used by budget/forecast compare).
    const compMode = comp.length > 0 ? 'comp' : 'budget';
    const compSeries = compMode === 'comp'
      ? groupByDate(comp, 'actual')
      : groupByDate(actual, 'budget');

    // Pair by index (comp dates may differ — e.g. SDLW is 7 days prior).
    const paired = actualSeries.map((pt, i) => ({
      date: pt.date,
      value: pt.value,
      benchmark: compSeries[i]?.value ?? null,
    }));

    // Drop trailing nulls but keep gaps in the middle.
    while (paired.length && paired[paired.length - 1].value == null) {
      paired.pop();
    }

    return paired.length >= 2 ? paired : null;
  };

  const renderSparklineSvg = (series, { inverted = false } = {}) => {
    if (!series || series.length < 2) return null;
    const width = 224;
    const height = 70;
    const padX = 4;
    const padY = 8;
    const innerW = width - padX * 2;
    const innerH = height - padY * 2;

    // Y-domain spans both actual and benchmark values.
    const all = [];
    series.forEach((p) => {
      if (Number.isFinite(p.value)) all.push(p.value);
      if (Number.isFinite(p.benchmark)) all.push(p.benchmark);
    });
    if (all.length === 0) return null;
    const min = Math.min(...all);
    const max = Math.max(...all);
    const range = max - min || Math.abs(max) || 1;
    const xAt = (i) => padX + (i / Math.max(1, series.length - 1)) * innerW;
    const yAt = (v) => padY + (1 - (v - min) / range) * innerH;

    // Palette comes from the chart tokens (src/index.css) so this sparkline
    // shares one source of truth with the bklit charts — see
    // components/charts/LODGEIC.md. It stays hand-drawn SVG on purpose: it
    // lives inside a hover tooltip that mounts per cell, where a chart shell's
    // ResizeObserver and reveal animation would be cost without benefit.
    const aboveFill = 'color-mix(in srgb, var(--chart-positive) 20%, transparent)';
    const belowFill = 'color-mix(in srgb, var(--chart-negative) 18%, transparent)';
    // The Actual line overlays the green/red favorability fills, so it needs a
    // neutral high-contrast ink to stay legible over both (orange would clash
    // with the red fill). Orange is reserved as a single accent on the end dot.
    const actualStroke = 'var(--chart-foreground)';
    const benchStroke = 'var(--chart-foreground-muted)';

    const favorable = (a, b) => (inverted ? a < b : a > b);

    // Build between-area fill segments, splitting at crossovers so each filled
    // region matches the favorability of that span.
    const fillPaths = [];
    for (let i = 0; i < series.length - 1; i++) {
      const a0 = series[i].value;
      const a1 = series[i + 1].value;
      const b0 = series[i].benchmark;
      const b1 = series[i + 1].benchmark;
      if (a0 == null || a1 == null || b0 == null || b1 == null) continue;

      const x0 = xAt(i);
      const x1 = xAt(i + 1);
      const a0y = yAt(a0);
      const a1y = yAt(a1);
      const b0y = yAt(b0);
      const b1y = yAt(b1);

      const fav0 = favorable(a0, b0);
      const fav1 = favorable(a1, b1);
      const equal0 = a0 === b0;
      const equal1 = a1 === b1;

      if (fav0 === fav1 || equal0 || equal1) {
        const color = (fav0 || fav1) ? aboveFill : belowFill;
        fillPaths.push(
          <path key={`f-${i}`} d={`M${x0},${a0y} L${x1},${a1y} L${x1},${b1y} L${x0},${b0y} Z`} fill={color} />
        );
      } else {
        // Crossover within segment — split into two triangles.
        const denom = (a1 - a0) - (b1 - b0);
        if (denom === 0) continue;
        const t = (b0 - a0) / denom;
        const xc = x0 + t * (x1 - x0);
        const yc = a0y + t * (a1y - a0y);
        fillPaths.push(
          <path key={`f-${i}-a`} d={`M${x0},${a0y} L${xc},${yc} L${x0},${b0y} Z`} fill={fav0 ? aboveFill : belowFill} />
        );
        fillPaths.push(
          <path key={`f-${i}-b`} d={`M${xc},${yc} L${x1},${a1y} L${x1},${b1y} Z`} fill={fav1 ? aboveFill : belowFill} />
        );
      }
    }

    const actualPoints = series
      .map((p, i) => (p.value == null ? null : `${xAt(i)},${yAt(p.value)}`))
      .filter(Boolean)
      .join(' ');
    const benchPoints = series
      .map((p, i) => (p.benchmark == null ? null : `${xAt(i)},${yAt(p.benchmark)}`))
      .filter(Boolean)
      .join(' ');

    const last = series[series.length - 1];
    // Single brand accent: orange dot marks the current value. Favorability is
    // already conveyed by the green/red area fills.
    const dotColor = 'var(--chart-line-primary)';

    return (
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label="Daily trend"
        style={{ display: 'block', marginTop: 6 }}
      >
        {fillPaths}
        {benchPoints && (
          <polyline
            fill="none"
            stroke={benchStroke}
            strokeWidth={1.5}
            strokeDasharray="3 3"
            points={benchPoints}
          />
        )}
        {actualPoints && (
          <polyline
            fill="none"
            stroke={actualStroke}
            strokeWidth={1.75}
            strokeLinejoin="round"
            points={actualPoints}
          />
        )}
        {last.value != null && (
          <circle
            cx={xAt(series.length - 1)}
            cy={yAt(last.value)}
            r={3}
            fill={dotColor}
          />
        )}
      </svg>
    );
  };

  const renderSparklineLegend = () => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 10,
        marginTop: 6,
        fontSize: 10,
        fontWeight: 700,
        color: 'var(--lodge-onyx-500)',
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <span style={{ width: 14, height: 2, background: 'var(--lodge-onyx-900)', borderRadius: 1 }} />
        Actual
      </span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <span style={{
          width: 14,
          height: 0,
          borderTop: '1.5px dashed var(--lodge-onyx-400)',
        }} />
        Benchmark
      </span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <span style={{
          width: 9,
          height: 9,
          borderRadius: 2,
          background: 'rgba(18, 183, 106, 0.40)',
        }} />
        Favorable
      </span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <span style={{
          width: 9,
          height: 9,
          borderRadius: 2,
          background: 'rgba(240, 68, 56, 0.35)',
        }} />
        Unfavorable
      </span>
    </div>
  );

  // Column specs per tab. `key` is the row accessor; `benchmarkKey` overrides
  // when the benchmark lives under a different key (financial columns).
  const REVENUE_COLUMNS = [
    { key: 'occupiedRooms', label: 'Occupied Rooms', fmt: 'integer' },
    { key: 'compRooms',     label: 'Comp Rooms',     fmt: 'integer', hideDelta: true },
    { key: 'occupancyRate', label: 'Occupancy %',    fmt: 'percent' },
    { key: 'adr',           label: 'ADR',            fmt: 'currency' },
    { key: 'revPAR',        label: 'RevPAR',         fmt: 'currency' },
    { key: 'roomRevenue',   label: 'Room Revenue',   fmt: 'currencyRounded' },
    { key: 'totalRevenue',  label: 'Total Revenue',  fmt: 'currencyRounded' },
  ];

  const OPERATIONS_COLUMNS = [
    { key: 'hoursWorked',   label: 'Hours Worked',    fmt: 'integer' },
    { key: 'budgetedHours', label: 'Budgeted Hours',  fmt: 'integer', hideDelta: true },
    { key: 'hpor',          label: 'HPOR',            fmt: 'number',   inverted: true },
    { key: 'otHours',       label: 'OT Hours',        fmt: 'hours',    inverted: true },
    { key: 'otPercentage',  label: 'OT %',            fmt: 'percent',  inverted: true },
    { key: 'wages',         label: 'Total Wages',     fmt: 'currencyRounded', inverted: true },
    { key: 'dpor',          label: '$POR',  fmt: 'currency', inverted: true },
  ];

  // Financial columns: revenue/expense/netIncome + their benchmarks + variance.
  // Variance columns are `varianceOnly` — tone is derived from the value's sign.
  const FINANCIAL_COLUMNS = [
    { key: 'revenue',          label: 'Revenue',                            fmt: 'currencyRounded', benchmarkKey: 'budgetRevenue' },
    { key: 'budgetRevenue',    label: `${financialComparisonLabel} Revenue`, fmt: 'currencyRounded', hideDelta: true },
    { key: 'revenueVariance',  label: 'Rev Variance',                       fmt: 'currencyRounded', varianceOnly: true },
    { key: 'expense',          label: 'Expenses',                           fmt: 'currencyRounded', benchmarkKey: 'budgetExpense', inverted: true },
    { key: 'budgetExpense',    label: `${financialComparisonLabel} Expense`, fmt: 'currencyRounded', hideDelta: true },
    { key: 'expenseVariance',  label: 'Exp Variance',                       fmt: 'currencyRounded', varianceOnly: true },
    { key: 'netIncome',        label: 'Net Income',                         fmt: 'currencyRounded', benchmarkKey: 'budgetNetIncome' },
    { key: 'budgetNetIncome',  label: `${financialComparisonLabel} Net Income`, fmt: 'currencyRounded', hideDelta: true },
    { key: 'netIncomeVariance',label: 'Net Inc Variance',                   fmt: 'currencyRounded', varianceOnly: true },
  ];

  // Adapt the variance-column lookup so the cells reference the right value.
  // The legacy financial rows store `revenueVariance` directly on the row.
  // No extra mapping needed here — the column key matches the row property.

  // ----------------------------------------------------------------
  // Forecast-by-property table data (no comparison; sums to a Portfolio
  // total row). Returns:
  //   { rows: [{ locationName, revparProjection, otbForecast, rmsForecast, isTotal? }], columns }
  // ----------------------------------------------------------------
  const buildForecastByPropertyTable = () => {
    const byDate = forecastingData?.byDate;
    if (!Array.isArray(byDate) || byDate.length === 0) {
      return { rows: [], columns: [] };
    }

    const locationTotals = {};
    const today = new Date();
    const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();

    byDate.forEach((day) => {
      const locationId = day.locationId;
      if (locationId == null) return;
      if (!locationTotals[locationId]) {
        locationTotals[locationId] = {
          locationId,
          locationName: locationNames[locationId] || `Location ${locationId}`,
          roomRevenue: 0,
          availableRooms: 0,
          maxAvailableRooms: 0,
          otbForecast: 0,
          rmsForecast: 0,
        };
      }
      locationTotals[locationId].roomRevenue += day.roomRevenue || 0;
      locationTotals[locationId].availableRooms += day.availableRooms || 0;
      if (day.availableRooms > locationTotals[locationId].maxAvailableRooms) {
        locationTotals[locationId].maxAvailableRooms = day.availableRooms;
      }
      locationTotals[locationId].otbForecast += day.otbForecast || 0;
      locationTotals[locationId].rmsForecast += day.rmsForecast || 0;
    });

    Object.values(locationTotals).forEach((row) => {
      const revPAR = row.availableRooms > 0 ? row.roomRevenue / row.availableRooms : 0;
      row.revparProjection = revPAR * row.maxAvailableRooms * daysInMonth;
    });

    const sorted = Object.values(locationTotals).sort((a, b) =>
      a.locationName.localeCompare(b.locationName)
    );

    const grand = sorted.reduce(
      (acc, r) => ({
        revparProjection: acc.revparProjection + (r.revparProjection || 0),
        otbForecast: acc.otbForecast + (r.otbForecast || 0),
        rmsForecast: acc.rmsForecast + (r.rmsForecast || 0),
      }),
      { revparProjection: 0, otbForecast: 0, rmsForecast: 0 }
    );

    const rows = [
      ...sorted,
      { ...grand, locationId: 'total', locationName: 'Portfolio', isTotal: true },
    ];

    const columns = [
      forecastingMetrics.avgRevPARProjection > 0 && {
        key: 'revparProjection', label: 'RevPAR Projection', fmt: 'currencyRounded', hideDelta: true,
      },
      forecastingMetrics.totalOTBForecast > 0 && {
        key: 'otbForecast', label: 'OTB Forecast', fmt: 'currencyRounded', hideDelta: true,
      },
      forecastingMetrics.totalRMSForecast > 0 && {
        key: 'rmsForecast', label: 'RMS Forecast', fmt: 'currencyRounded', hideDelta: true,
      },
    ].filter(Boolean);

    return { rows, columns };
  };

  const forecastByProperty = buildForecastByPropertyTable();

  // Handle location selection - simplified logic
  const handleLocationToggle = (locationId) => {
    if (locationId === 'all') {
      // Toggle between "All" and empty selection
      if (selectedLocations.includes('all')) {
        setSelectedLocations([]); // Allow empty selection
      } else {
        setSelectedLocations(['all']);
      }
    } else {
      const locationIdStr = locationId.toString();
      setSelectedLocations(prev => {
        // Remove 'all' from selection when working with individual locations
        const currentSelection = prev.filter(id => id !== 'all');

        if (currentSelection.includes(locationIdStr)) {
          // Remove the location
          return currentSelection.filter(id => id !== locationIdStr);
        } else {
          // Add the location
          const updated = [...currentSelection, locationIdStr];
          // Auto-convert to "All" if all locations are selected
          if (updated.length === availableLocations.length) {
            return ['all'];
          }
          return updated;
        }
      });
    }
  };

  const isLocationSelected = (locationId) => {
    if (locationId === 'all') {
      return selectedLocations.includes('all');
    }
    // Only show individual location as checked if it's specifically selected (not when "all" is selected)
    return selectedLocations.includes(locationId.toString());
  };

  const getSelectedLocationText = () => {
    if (selectedLocations.includes('all')) {
      return 'All Locations';
    }
    if (selectedLocations.length === 0) {
      return 'Select Locations';
    }
    if (selectedLocations.length === 1) {
      const locationId = selectedLocations[0];
      return getLocationDisplayName(locationId, locationNames[parseInt(locationId)]);
    }
    return `${selectedLocations.length} Locations Selected`;
  };

  const comparisonOptions = getComparisonOptions(selectedQuickDate);
  const selectedComparisonOption =
    comparisonOptions.find(option => option.key === selectedComparisonMode) || comparisonOptions[0];
  const benchmarkRevenueLabel = selectedComparisonMode === 'budget'
    ? 'Budget Total Revenue'
    : `${selectedComparisonOption?.label || 'Benchmark'} Revenue`;
  const hasBudgetComparisonData = !!(
    calculatedMetrics?.aggregated?.totalRoomRevenueBudget ||
    calculatedMetrics?.aggregated?.totalRevenueBudget ||
    calculatedMetrics?.aggregated?.totalBudgetRevenue ||
    calculatedMetrics?.aggregated?.totalBudgetExpense ||
    calculatedMetrics?.aggregated?.totalBudgetedHours
  );
  const hasForecastComparisonData = !!(
    calculatedMetrics?.aggregated?.totalForecastRevenue ||
    calculatedMetrics?.aggregated?.totalRmsForecast
  );
  const isComparisonOptionDisabled = (option) => (
    (option.type === 'budget' && !hasBudgetComparisonData) ||
    (option.type === 'forecast' && !hasForecastComparisonData)
  );
  const dashboardQuickDateOptions = [
    { key: 'yesterday', label: 'Yesterday' },
    { key: 'last7', label: '7d' },
    { key: 'last30', label: '30d' },
    { key: 'last60', label: '60d' },
    { key: 'last90', label: '90d' },
    { key: 'thisMonth', label: 'MTD' },
    { key: 'lastMonth', label: 'Last month' }
  ];
  const FINANCIAL_PERIOD_OPTIONS = [
    { key: 'lastMonth', label: 'Last Month' },
    { key: 'ytd', label: 'YTD' },
    { key: 'trailing12', label: 'Trailing 12 Months' }
  ];
  const FORECAST_PERIOD_OPTIONS = [
    { key: 'thisMonth', label: 'This month' },
    { key: 'next30',    label: 'Next 30 days' },
    { key: 'next60',    label: 'Next 60 days' },
    { key: 'next90',    label: 'Next 90 days' },
    { key: 'thisYear',  label: 'This year' }
  ];
  const FORECAST_COMPARISON_OPTIONS = [
    { key: 'lastYear', label: 'Last year', longLabel: 'Last year pace', type: 'historical' },
    { key: 'budget',   label: 'Budget',    longLabel: 'OTB vs budget',  type: 'budget' },
    { key: 'pickup',   label: 'Pickup',    longLabel: 'Overnight pickup by stay date', type: 'pickup' },
    { key: 'none',     label: 'None',      longLabel: 'No comparison',  type: 'none' }
  ];
  const dashboardFloatingMenuStyle = {
    position: 'absolute',
    left: 0,
    bottom: 'calc(100% + 10px)',
    minWidth: '100%',
    width: 'max-content',
    maxWidth: 280,
    padding: 6,
    borderRadius: 14,
    border: '1px solid var(--lodge-onyx-200)',
    backgroundColor: 'rgba(255, 255, 255, 0.96)',
    boxShadow: '0 18px 50px rgba(15, 15, 17, 0.18)',
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    zIndex: 1300
  };
  const dashboardFloatingMenuItemStyle = (isActive, isDisabled = false) => ({
    width: '100%',
    minHeight: 38,
    padding: '8px 12px',
    border: 'none',
    borderRadius: 10,
    backgroundColor: isActive ? 'var(--lodge-orange-400)' : 'transparent',
    color: isDisabled ? 'var(--lodge-onyx-300)' : isActive ? '#ffffff' : 'var(--lodge-onyx-800)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    fontFamily: 'var(--font-sans)',
    fontSize: 14,
    fontWeight: isActive ? 800 : 700,
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    whiteSpace: 'nowrap',
    opacity: isDisabled ? 0.55 : 1
  });
  const dashboardLocationMenuItemStyle = (isSelected, isAllOption = false) => ({
    width: '100%',
    minHeight: isAllOption ? 44 : 42,
    padding: '10px 18px',
    border: 'none',
    borderRadius: 0,
    backgroundColor: 'transparent',
    color: isSelected ? 'var(--lodge-orange-700)' : 'var(--lodge-onyx-800)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 18,
    fontFamily: 'var(--font-sans)',
    fontSize: 14,
    fontWeight: isSelected ? 800 : 600,
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'background-color var(--duration-fast) var(--easing-standard), color var(--duration-fast) var(--easing-standard)'
  });
  const dashboardLocationCheckStyle = (isSelected) => ({
    width: 18,
    height: 18,
    color: isSelected ? 'var(--lodge-orange-500)' : 'transparent',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  });
  const comparisonIsNone = selectedComparisonOption?.type === 'none' || selectedComparisonMode === 'none';
  const comparisonSummaryText = comparisonIsNone
    ? `Showing ${getDateRangeLabel(selectedQuickDate).toLowerCase()}`
    : `Showing ${getDateRangeLabel(selectedQuickDate).toLowerCase()} ${
        selectedComparisonOption?.longLabel || selectedComparisonOption?.label
          ? `vs ${selectedComparisonOption.longLabel || selectedComparisonOption.label}`
          : ''
      }`;
  const dashboardTabs = [
    { key: 'revenue', label: 'Revenue', icon: DollarSign },
    { key: 'forecast', label: 'Forecast', icon: Activity },
    { key: 'operations', label: 'Operations', icon: Users },
    { key: 'financial', label: 'Financial', icon: TrendingUp }
  ].filter((tab) => canSeeDashboardSection(tab.key));
  const displayDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return parseLocalDate(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      return dateStr;
    }
  };
  const dashboardDateRangeLabel = dateRange?.start && dateRange?.end
    ? dateRange.start === dateRange.end
      ? displayDate(dateRange.start)
      : `${displayDate(dateRange.start)} - ${displayDate(dateRange.end)}`
    : getDateRangeLabel(selectedQuickDate);
  const selectedLocationCount = selectedLocations.includes('all')
    ? availableLocations.length
    : selectedLocations.length;
  const dashboardLocationSummary = selectedLocations.includes('all')
    ? `${selectedLocationCount || 'All'} ${selectedLocationCount === 1 ? 'property' : 'properties'}`
    : `${selectedLocationCount} selected ${selectedLocationCount === 1 ? 'property' : 'properties'}`;
  const handleActiveTabExport = () => {
    if (activeDashboardTab === 'financial') {
      exportFinancialToExcel(financialTableData, 'Financial_Performance_Summary', financialComparisonLabel);
      return;
    }

    if (activeDashboardTab === 'operations') {
      exportOperationsToExcel(operationsTableData, 'Operations_Performance_Summary');
      return;
    }

    exportToExcel(tableData, 'Performance_Summary', comparisonTableLabel);
  };

  const sortedAvailableLocations = [...availableLocations].sort((a, b) => {
    const nameA = (locationNames[a] || `Location ${a}`).toLowerCase();
    const nameB = (locationNames[b] || `Location ${b}`).toLowerCase();
    return nameA.localeCompare(nameB);
  });

  const renderLocationMenuItems = (itemClassName) => (
    <>
      <button
        type="button"
        className={itemClassName + (isLocationSelected('all') ? ' active' : '')}
        onClick={() => handleLocationToggle('all')}
      >
        <span className="label">All Locations</span>
        {isLocationSelected('all') && <Check size={12} color="var(--lodge-orange-500)" />}
      </button>
      <div className="dashboard-filter-location-divider" />
      {sortedAvailableLocations.map(locationId => {
        const isSelected = isLocationSelected(locationId);
        return (
          <button
            key={locationId}
            type="button"
            className={itemClassName + (isSelected ? ' active' : '')}
            onClick={() => handleLocationToggle(locationId)}
          >
            <span className="label">{getLocationDisplayName(locationId, locationNames[locationId])}</span>
            {isSelected && <Check size={12} color="var(--lodge-orange-500)" />}
          </button>
        );
      })}
    </>
  );

  const renderTopLocationFilter = () => (
    <div
      className="dashboard-filter-location"
      data-location-dropdown
    >
      <button
        type="button"
        className={'dashboard-filter-location-btn' + (showLocationDropdown ? ' open' : '')}
        onClick={() => setShowLocationDropdown(open => !open)}
      >
        <MapPin size={13} color="var(--lodge-onyx-500)" />
        <span className="label">{getSelectedLocationText()}</span>
        <ChevronDown size={11} color="var(--lodge-onyx-500)" />
      </button>

      {showLocationDropdown && (
        <div className="dashboard-filter-location-menu">
          {renderLocationMenuItems('dashboard-filter-location-item')}
        </div>
      )}
    </div>
  );

  const renderFloatLocationFilter = () => (
    <div className="dashboard-float-menu" data-float-location-dropdown>
      <button
        type="button"
        aria-label="Location"
        className={'dashboard-float-menu-btn' + (showFloatLocationDropdown ? ' open' : '')}
        onClick={() => {
          setShowFloatLocationDropdown(open => !open);
          setShowDashboardPeriodMenu(false);
          setShowDashboardComparisonMenu(false);
        }}
      >
        <MapPin size={13} color="var(--lodge-onyx-500)" />
        <span className="dashboard-float-menu-label">{getSelectedLocationText()}</span>
        <ChevronDown size={11} color="var(--lodge-onyx-500)" />
      </button>
      {showFloatLocationDropdown && (
        <div className="dashboard-float-menu-pop">
          {renderLocationMenuItems('dashboard-float-menu-item')}
        </div>
      )}
    </div>
  );

  // Render mobile-optimized dashboard on mobile devices
  if (isMobile) {
    return (
      <MobileDashboard
        dashboardData={dashboardData}
        calculatedMetrics={calculatedMetrics}
        comparisonMetrics={comparisonMetrics}
        isLoadingComparison={isLoadingComparison}
        financialData={financialData}
        financialMetrics={financialMetrics}
        currentOrganization={currentOrganization}
        lastUpdated={lastUpdated}
        dateRange={dateRange}
        selectedQuickDate={selectedQuickDate}
        selectedLocations={selectedLocations}
        availableLocations={availableLocations}
        locationNames={locationNames}
        onQuickDateChange={handleQuickDateRange}
        onLocationChange={setSelectedLocations}
        onRefresh={fetchDashboardData}
        formatCurrency={formatCurrency}
        formatCurrencyRounded={formatCurrencyRounded}
        formatPercentage={formatPercentage}
        calculatePercentChange={calculatePercentChange}
        calculateAbsoluteDelta={calculateAbsoluteDelta}
        formatAbsoluteDelta={formatAbsoluteDelta}
        formatComparisonValue={formatComparisonValue}
        chartData={chartData}
        financialChartData={financialDisplayChartData}
        selectedFinancialQuickDate={selectedFinancialQuickDate}
        onFinancialQuickDateChange={handleFinancialQuickDateRange}
        revenueTableData={tableData}
        operationsTableData={operationsTableData}
        financialTableData={financialTableData}
        aiAssistantEnabled={aiAssistantEnabled}
        briefingHotelId="all"
        briefingRefreshKey={lastUpdated ? lastUpdated.getTime() : 0}
        briefingComparisonMode={selectedComparisonMode}
        briefingComparisonLabel={selectedComparisonOption?.longLabel || selectedComparisonOption?.label}
        comparisonOptions={comparisonOptions}
        selectedComparisonMode={selectedComparisonMode}
        onComparisonModeChange={setSelectedComparisonMode}
        isComparisonOptionDisabled={isComparisonOptionDisabled}
        comparisonSummaryText={comparisonSummaryText}
        comparisonTableLabel={comparisonTableLabel}
        onSaveComparisonDefault={handleSaveDefaultComparison}
        hasUnsavedComparisonPreference={hasUnsavedComparisonPreference}
        isSavingComparisonPreference={isSavingComparisonPreference}
        comparisonSaveSuccess={comparisonSaveSuccess}
      />
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        padding: isMobile ? 'var(--sp-4)' : 'var(--sp-6) var(--sp-2)',
        // Reserve space so the bottom-floating filter bar never overlaps content.
        paddingBottom: 'calc(var(--sp-8, 32px) + 96px)',
        position: 'relative',
      }}
    >
      <div
        style={{
          // Matches ReportPageShell: wide enough for the dense performance
          // tables; below ~1550px viewports the window is the constraint.
          maxWidth: 1760,
          margin: '0 auto',
          position: 'relative',
        }}
      >
        <div className="dashboard-shell-head">
          <div>
            <div className="dashboard-shell-title-row">
              <h1 className="dashboard-shell-title">Dashboard</h1>
              <NewLookChip />
            </div>
            <div className="dashboard-shell-sub">
              {currentOrganization?.name || 'Portfolio'} &middot; {dashboardLocationSummary} &middot; {dashboardDateRangeLabel}
            </div>
          </div>

          {aiAssistantEnabled && (
            <div className="dashboard-briefing-strip" role="status" aria-label="AI Daily Briefing">
              <span className="dashboard-briefing-icon">
                <Sparkles size={14} />
              </span>
              <span className="dashboard-briefing-eyebrow">AI Daily Briefing</span>
              <p className="dashboard-briefing-headline">
                {comparisonIsNone
                  ? `${getDateRangeLabel(selectedQuickDate)} performance ready`
                  : `Performance vs ${selectedComparisonOption?.label || 'benchmark'} ready`}
              </p>
              <span className="dashboard-briefing-chip positive">
                Rev <span>{formatCurrencyRounded(calculatedMetrics?.aggregated?.totalRevenue || 0)}</span>
              </span>
              <span className="dashboard-briefing-chip warning">
                Labor <span>{formatPercentage(calculatedMetrics?.aggregated?.laborPercentage || 0)}</span>
              </span>
              <button
                type="button"
                className="dashboard-briefing-cta"
                onClick={() => setIsBriefingDrawerOpen(true)}
              >
                Read briefing
                <ChevronRight size={14} />
              </button>
            </div>
          )}
        </div>
        <NewLookPrompt />

        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ marginBottom: 24 }}>
            <div
              ref={dashboardDockRef}
              className="dashboard-filter-bar"
            >
              {activeDashboardTab === 'financial' ? (
                <>
                  <div className="dashboard-filter-row">
                    <div className="dashboard-filter-periods" role="tablist" aria-label="Financial period">
                      {FINANCIAL_PERIOD_OPTIONS.map(option => {
                        const isActive = selectedFinancialQuickDate === option.key;
                        return (
                          <button
                            key={option.key}
                            type="button"
                            role="tab"
                            aria-selected={isActive}
                            className={'dashboard-filter-period' + (isActive ? ' active' : '')}
                            onClick={() => handleFinancialQuickDateRange(option.key)}
                          >
                            {option.label}
                          </button>
                        );
                      })}
                    </div>

                    <div className="dashboard-filter-date-wrap">
                      <DateRangePicker
                        startDate={financialDateRange.start}
                        endDate={financialDateRange.end}
                        onDateChange={handleFinancialDateRangeChange}
                        maxDate={formatLocalDate(getFinancialAvailableThroughDate(currentOrganization?.financialCloseDay))}
                        placeholder="Select financial date range"
                      />
                    </div>

                    <div className="dashboard-filter-spacer" />
                  </div>

                  <div className="dashboard-filter-row">
                    <span className="dashboard-filter-compare-label">Compare</span>

                    <div className="dashboard-filter-compare" role="tablist" aria-label="Comparison">
                      {financialComparisonOptions.map(option => {
                        const isActive = selectedFinancialComparisonMode === option.key;
                        return (
                          <button
                            key={option.key}
                            type="button"
                            role="tab"
                            aria-selected={isActive}
                            title={option.longLabel}
                            className={'dashboard-filter-compare-btn' + (isActive ? ' active' : '')}
                            onClick={() => setSelectedFinancialComparisonMode(option.key)}
                          >
                            {option.label}
                          </button>
                        );
                      })}
                    </div>

                    <div className="dashboard-filter-spacer" />

                    <button
                      type="button"
                      className="dashboard-filter-refresh"
                      onClick={fetchFinancialData}
                      title="Refresh"
                    >
                      <RefreshCw size={13} />
                      Refresh
                    </button>
                  </div>
                </>
              ) : activeDashboardTab === 'forecast' ? (
                <>
                  <div className="dashboard-filter-row">
                    <div className="dashboard-filter-periods" role="tablist" aria-label="Forecast period">
                      {FORECAST_PERIOD_OPTIONS.map(option => {
                        const isActive = selectedForecastQuickDate === option.key;
                        return (
                          <button
                            key={option.key}
                            type="button"
                            role="tab"
                            aria-selected={isActive}
                            className={'dashboard-filter-period' + (isActive ? ' active' : '')}
                            onClick={() => setSelectedForecastQuickDate(option.key)}
                          >
                            {option.label}
                          </button>
                        );
                      })}
                    </div>

                    <div className="dashboard-filter-spacer" />

                    {renderTopLocationFilter()}
                  </div>

                  <div className="dashboard-filter-row">
                    <span className="dashboard-filter-compare-label">Compare</span>

                    <div className="dashboard-filter-compare" role="tablist" aria-label="Comparison">
                      {FORECAST_COMPARISON_OPTIONS.map(option => {
                        const isActive = selectedForecastComparisonMode === option.key;
                        return (
                          <button
                            key={option.key}
                            type="button"
                            role="tab"
                            aria-selected={isActive}
                            title={option.longLabel}
                            className={'dashboard-filter-compare-btn' + (isActive ? ' active' : '')}
                            onClick={() => setSelectedForecastComparisonMode(option.key)}
                          >
                            {option.label}
                          </button>
                        );
                      })}
                    </div>

                    <div className="dashboard-filter-spacer" />

                    <button
                      type="button"
                      className="dashboard-filter-refresh"
                      onClick={fetchDashboardData}
                      title="Refresh"
                    >
                      <RefreshCw size={13} />
                      Refresh
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="dashboard-filter-row">
                    <button
                      type="button"
                      className="dashboard-filter-save"
                      onClick={handleSaveDefaults}
                      disabled={!hasUnsavedDefaults || isSavingPreference}
                      title={saveSuccess ? `Defaults saved (${savedDateLabel})` : 'Save this date range and comparison as your default'}
                      style={{
                        color: saveSuccess ? '#15803d' : '#2E7D32',
                        opacity: saveFadingOut ? 0 : 1,
                        transition: saveFadingOut ? 'opacity 2s ease-out' : 'background 120ms, opacity 200ms'
                      }}
                    >
                      <Save size={14} />
                    </button>

                    <div className="dashboard-filter-periods" role="tablist" aria-label="Period">
                      {dashboardQuickDateOptions.map(option => {
                        const isActive = selectedQuickDate === option.key;
                        return (
                          <button
                            key={option.key}
                            type="button"
                            role="tab"
                            aria-selected={isActive}
                            className={'dashboard-filter-period' + (isActive ? ' active' : '')}
                            onClick={() => handleQuickDateRange(option.key)}
                          >
                            {option.label}
                          </button>
                        );
                      })}
                    </div>

                    <div className="dashboard-filter-date-wrap">
                      <DateRangePicker
                        startDate={dateRange.start}
                        endDate={dateRange.end}
                        onDateChange={handleDateRangeChange}
                        maxDate={formatLocalDate(new Date())}
                        placeholder="Select date range"
                      />
                    </div>

                    <div className="dashboard-filter-spacer" />

                    {renderTopLocationFilter()}
                  </div>

                  <div className="dashboard-filter-row">
                    <span className="dashboard-filter-compare-label">Compare</span>

                    <div className="dashboard-filter-compare" role="tablist" aria-label="Comparison">
                      {comparisonOptions.map(option => {
                        const isActive = selectedComparisonMode === option.key;
                        const isDisabled = isComparisonOptionDisabled(option);
                        return (
                          <button
                            key={option.key}
                            type="button"
                            role="tab"
                            aria-selected={isActive}
                            disabled={isDisabled}
                            title={option.longLabel}
                            className={'dashboard-filter-compare-btn' + (isActive ? ' active' : '')}
                            onClick={() => { if (!isDisabled) setSelectedComparisonMode(option.key); }}
                          >
                            {option.label}
                          </button>
                        );
                      })}
                    </div>

                    <span className="dashboard-filter-context">
                      <span className="summary">{comparisonSummaryText.trim()}</span>
                    </span>

                    <div className="dashboard-filter-spacer" />

                    <button
                      type="button"
                      className="dashboard-filter-refresh"
                      onClick={fetchDashboardData}
                      title="Refresh"
                    >
                      <RefreshCw size={13} />
                      Refresh
                    </button>
                  </div>
                </>
              )}
            </div>

            <div
              className={'dashboard-float-bar' + (isDashboardDockVisible ? ' visible' : '')}
              role="toolbar"
              aria-label="Quick filters"
            >
              {activeDashboardTab === 'financial' ? (
                <>
                  <div className="dashboard-float-menu" data-dashboard-period-menu>
                    <button
                      type="button"
                      aria-label="Financial period"
                      className={'dashboard-float-menu-btn' + (showDashboardPeriodMenu ? ' open' : '')}
                      onClick={() => {
                        setShowDashboardPeriodMenu(open => !open);
                        setShowDashboardComparisonMenu(false);
                        setShowFloatLocationDropdown(false);
                      }}
                    >
                      <span className="dashboard-float-menu-label">
                        {selectedFinancialQuickDate
                          ? (FINANCIAL_PERIOD_OPTIONS.find(o => o.key === selectedFinancialQuickDate)?.label || 'Custom')
                          : 'Custom'}
                      </span>
                      <ChevronDown size={11} color="var(--lodge-onyx-500)" />
                    </button>
                    {showDashboardPeriodMenu && (
                      <div className="dashboard-float-menu-pop">
                        {!selectedFinancialQuickDate && (
                          <button
                            type="button"
                            className="dashboard-float-menu-item active"
                            onClick={() => setShowDashboardPeriodMenu(false)}
                          >
                            <span className="label">Custom</span>
                            <Check size={12} color="var(--lodge-orange-500)" />
                          </button>
                        )}
                        {FINANCIAL_PERIOD_OPTIONS.map(option => {
                          const isActive = selectedFinancialQuickDate === option.key;
                          return (
                            <button
                              key={option.key}
                              type="button"
                              className={'dashboard-float-menu-item' + (isActive ? ' active' : '')}
                              onClick={() => {
                                handleFinancialQuickDateRange(option.key);
                                setShowDashboardPeriodMenu(false);
                              }}
                            >
                              <span className="label">{option.label}</span>
                              {isActive && <Check size={12} color="var(--lodge-orange-500)" />}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <span className="dashboard-float-sep" />

                  <div className="dashboard-float-menu" data-dashboard-comparison-menu>
                    <button
                      type="button"
                      aria-label="Financial comparison"
                      title={selectedFinancialComparisonOption?.longLabel || selectedFinancialComparisonOption?.label}
                      className={'dashboard-float-menu-btn accent' + (showDashboardComparisonMenu ? ' open' : '')}
                      onClick={() => {
                        setShowDashboardComparisonMenu(open => !open);
                        setShowDashboardPeriodMenu(false);
                        setShowFloatLocationDropdown(false);
                      }}
                    >
                      <span className="dashboard-float-menu-label">{selectedFinancialComparisonOption?.label || 'Compare'}</span>
                      <ChevronDown size={11} color="var(--lodge-orange-500)" />
                    </button>
                    {showDashboardComparisonMenu && (
                      <div className="dashboard-float-menu-pop align-right">
                        {financialComparisonOptions.map(option => {
                          const isActive = selectedFinancialComparisonMode === option.key;
                          return (
                            <button
                              key={option.key}
                              type="button"
                              title={option.longLabel}
                              className={'dashboard-float-menu-item' + (isActive ? ' active' : '')}
                              onClick={() => {
                                setSelectedFinancialComparisonMode(option.key);
                                setShowDashboardComparisonMenu(false);
                              }}
                            >
                              <span className="label">{option.label}</span>
                              {isActive && <Check size={12} color="var(--lodge-orange-500)" />}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    className="dashboard-float-refresh"
                    onClick={fetchFinancialData}
                    title="Refresh"
                    aria-label="Refresh"
                  >
                    <RefreshCw size={14} />
                  </button>
                </>
              ) : activeDashboardTab === 'forecast' ? (
                <>
                  <div className="dashboard-float-menu" data-dashboard-period-menu>
                    <button
                      type="button"
                      aria-label="Forecast period"
                      className={'dashboard-float-menu-btn' + (showDashboardPeriodMenu ? ' open' : '')}
                      onClick={() => {
                        setShowDashboardPeriodMenu(open => !open);
                        setShowDashboardComparisonMenu(false);
                        setShowFloatLocationDropdown(false);
                      }}
                    >
                      <span className="dashboard-float-menu-label">
                        {FORECAST_PERIOD_OPTIONS.find(o => o.key === selectedForecastQuickDate)?.label || 'This month'}
                      </span>
                      <ChevronDown size={11} color="var(--lodge-onyx-500)" />
                    </button>
                    {showDashboardPeriodMenu && (
                      <div className="dashboard-float-menu-pop">
                        {FORECAST_PERIOD_OPTIONS.map(option => {
                          const isActive = selectedForecastQuickDate === option.key;
                          return (
                            <button
                              key={option.key}
                              type="button"
                              className={'dashboard-float-menu-item' + (isActive ? ' active' : '')}
                              onClick={() => {
                                setSelectedForecastQuickDate(option.key);
                                setShowDashboardPeriodMenu(false);
                              }}
                            >
                              <span className="label">{option.label}</span>
                              {isActive && <Check size={12} color="var(--lodge-orange-500)" />}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <span className="dashboard-float-sep" />

                  {renderFloatLocationFilter()}

                  <span className="dashboard-float-sep" />

                  <div className="dashboard-float-menu" data-dashboard-comparison-menu>
                    <button
                      type="button"
                      aria-label="Forecast comparison"
                      title={FORECAST_COMPARISON_OPTIONS.find(o => o.key === selectedForecastComparisonMode)?.longLabel}
                      className={'dashboard-float-menu-btn accent' + (showDashboardComparisonMenu ? ' open' : '')}
                      onClick={() => {
                        setShowDashboardComparisonMenu(open => !open);
                        setShowDashboardPeriodMenu(false);
                        setShowFloatLocationDropdown(false);
                      }}
                    >
                      <span className="dashboard-float-menu-label">
                        {FORECAST_COMPARISON_OPTIONS.find(o => o.key === selectedForecastComparisonMode)?.label || 'Compare'}
                      </span>
                      <ChevronDown size={11} color="var(--lodge-orange-500)" />
                    </button>
                    {showDashboardComparisonMenu && (
                      <div className="dashboard-float-menu-pop align-right">
                        {FORECAST_COMPARISON_OPTIONS.map(option => {
                          const isActive = selectedForecastComparisonMode === option.key;
                          return (
                            <button
                              key={option.key}
                              type="button"
                              title={option.longLabel}
                              className={'dashboard-float-menu-item' + (isActive ? ' active' : '')}
                              onClick={() => {
                                setSelectedForecastComparisonMode(option.key);
                                setShowDashboardComparisonMenu(false);
                              }}
                            >
                              <span className="label">{option.label}</span>
                              {isActive && <Check size={12} color="var(--lodge-orange-500)" />}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    className="dashboard-float-refresh"
                    onClick={fetchDashboardData}
                    title="Refresh"
                    aria-label="Refresh"
                  >
                    <RefreshCw size={14} />
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="dashboard-float-save"
                    onClick={handleSaveDefaults}
                    disabled={!hasUnsavedDefaults || isSavingPreference}
                    title={saveSuccess ? `Defaults saved (${savedDateLabel})` : 'Save this date range and comparison as your default'}
                    style={{
                      color: saveSuccess ? '#15803d' : '#2E7D32',
                      opacity: saveFadingOut ? 0 : 1
                    }}
                  >
                    <Save size={13} />
                  </button>

                  <span className="dashboard-float-sep" />

                  <div className="dashboard-float-menu" data-dashboard-period-menu>
                    <button
                      type="button"
                      aria-label="Dashboard period"
                      className={'dashboard-float-menu-btn' + (showDashboardPeriodMenu ? ' open' : '')}
                      onClick={() => {
                        setShowDashboardPeriodMenu(open => !open);
                        setShowDashboardComparisonMenu(false);
                        setShowFloatLocationDropdown(false);
                      }}
                    >
                      <span className="dashboard-float-menu-label">
                        {selectedQuickDate ? getDateRangeLabel(selectedQuickDate) : 'Custom'}
                      </span>
                      <ChevronDown size={11} color="var(--lodge-onyx-500)" />
                    </button>
                    {showDashboardPeriodMenu && (
                      <div className="dashboard-float-menu-pop">
                        {!selectedQuickDate && (
                          <button
                            type="button"
                            className="dashboard-float-menu-item active"
                            onClick={() => setShowDashboardPeriodMenu(false)}
                          >
                            <span className="label">Custom</span>
                            <Check size={12} color="var(--lodge-orange-500)" />
                          </button>
                        )}
                        {dashboardQuickDateOptions.map(option => {
                          const isActive = selectedQuickDate === option.key;
                          return (
                            <button
                              key={option.key}
                              type="button"
                              className={'dashboard-float-menu-item' + (isActive ? ' active' : '')}
                              onClick={() => {
                                handleQuickDateRange(option.key);
                                setShowDashboardPeriodMenu(false);
                              }}
                            >
                              <span className="label">{option.label}</span>
                              {isActive && <Check size={12} color="var(--lodge-orange-500)" />}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <span className="dashboard-float-sep" />

                  {renderFloatLocationFilter()}

                  <span className="dashboard-float-sep" />

                  <div className="dashboard-float-menu" data-dashboard-comparison-menu>
                    <button
                      type="button"
                      aria-label="Dashboard comparison"
                      title={selectedComparisonOption?.longLabel || selectedComparisonOption?.label}
                      className={'dashboard-float-menu-btn accent' + (showDashboardComparisonMenu ? ' open' : '')}
                      onClick={() => {
                        setShowDashboardComparisonMenu(open => !open);
                        setShowDashboardPeriodMenu(false);
                        setShowFloatLocationDropdown(false);
                      }}
                    >
                      <span className="dashboard-float-menu-label">{selectedComparisonOption?.label || 'Compare'}</span>
                      <ChevronDown size={11} color="var(--lodge-orange-500)" />
                    </button>
                    {showDashboardComparisonMenu && (
                      <div className="dashboard-float-menu-pop align-right">
                        {comparisonOptions.map(option => {
                          const isActive = selectedComparisonMode === option.key;
                          const isDisabled = isComparisonOptionDisabled(option);
                          return (
                            <button
                              key={option.key}
                              type="button"
                              disabled={isDisabled}
                              title={option.longLabel}
                              className={'dashboard-float-menu-item' + (isActive ? ' active' : '')}
                              onClick={() => {
                                if (isDisabled) return;
                                setSelectedComparisonMode(option.key);
                                setShowDashboardComparisonMenu(false);
                              }}
                            >
                              <span className="label">{option.label}</span>
                              {isActive && <Check size={12} color="var(--lodge-orange-500)" />}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    className="dashboard-float-refresh"
                    onClick={fetchDashboardData}
                    title="Refresh"
                    aria-label="Refresh"
                  >
                    <RefreshCw size={14} />
                  </button>
                </>
              )}
            </div>
          </div>

        {/* AI Daily Briefing drawer — opened via the header pill */}
        {aiAssistantEnabled && (
          <>
            {isBriefingDrawerOpen && createPortal(
              <div
                className="dashboard-drawer-scrim"
                onClick={() => setIsBriefingDrawerOpen(false)}
              >
                <aside
                  className="dashboard-briefing-drawer"
                  onClick={(event) => event.stopPropagation()}
                  aria-label="AI Daily Briefing"
                >
                  <div className="dashboard-briefing-drawer-head">
                    <div>
                      <div className="dashboard-briefing-eyebrow">
                        <Activity size={13} />
                        AI Daily Briefing
                      </div>
                      <h2 className="dashboard-briefing-drawer-title">
                        {getDateRangeLabel(selectedQuickDate)} &middot; {dashboardDateRangeLabel}
                      </h2>
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsBriefingDrawerOpen(false)}
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: 10,
                        border: '1px solid var(--lodge-onyx-100)',
                        background: 'var(--lodge-white)',
                        color: 'var(--lodge-onyx-500)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer'
                      }}
                      aria-label="Close briefing"
                    >
                      <X size={17} />
                    </button>
                  </div>
                  <div className="dashboard-briefing-drawer-body">
                    <DashboardDailyBriefing
                      hotelId="all"
                      hotelName={currentOrganization?.name || 'Portfolio'}
                      currency={activeCurrency}
                      refreshKey={lastUpdated ? lastUpdated.getTime() : 0}
                      comparisonMode={selectedComparisonMode}
                      comparisonLabel={selectedComparisonOption?.longLabel || selectedComparisonOption?.label}
                    />
                  </div>
                </aside>
              </div>,
              document.body
            )}
          </>
        )}

        <div className="dashboard-tabs-row">
          <div className="dashboard-tabs" role="tablist" aria-label="Dashboard sections">
            {dashboardTabs.map(tab => {
              const Icon = tab.icon;
              const isActive = activeDashboardTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className={`dashboard-tab${isActive ? ' active' : ''}`}
                  onClick={() => handleDashboardTabChange(tab.key)}
                >
                  <Icon size={15} />
                  {tab.label}
                </button>
              );
            })}
          </div>
          <div className="dashboard-tabs-actions">
            {showDashboardCurrencySelector && (
              <select
                value={activeCurrency}
                onChange={(e) => handleDashboardTargetCurrencyChange(e.target.value)}
                aria-label="Dashboard currency"
                className="dashboard-tabs-action"
                style={{ minWidth: 92, background: 'var(--lodge-surface)', color: 'var(--lodge-text)' }}
              >
                {dashboardCurrencyOptions.map((currency) => (
                  <option key={currency} value={currency}>{currency}</option>
                ))}
              </select>
            )}
            <button
              type="button"
              className="dashboard-tabs-action"
              onClick={handleActiveTabExport}
            >
              <Download size={14} />
              Export
            </button>
          </div>
        </div>

        {reportingCurrency.kind === 'mixed' && (
          <div
            role="status"
            style={{
              margin: '0 0 16px',
              padding: '10px 14px',
              borderRadius: 8,
              border: '1px solid #F0E1A8',
              background: '#FFF8E5',
              color: '#5C4A00',
              fontFamily: 'var(--font-sans)',
              fontSize: 13,
            }}
          >
            Showing {reportingCurrency.currencies.length} currencies
            ({reportingCurrency.currencies.join(', ')}). Portfolio totals are converted to {activeCurrency}
            when rates are configured.
          </div>
        )}

        {dashboardData?.currency?.conversionStatus === 'incomplete' && (
          <div
            role="alert"
            style={{
              margin: '0 0 16px',
              padding: '10px 14px',
              borderRadius: 8,
              border: '1px solid #F0B4A8',
              background: '#FFF1EE',
              color: '#7A2418',
              fontFamily: 'var(--font-sans)',
              fontSize: 13,
            }}
          >
            Some currency rates are missing for {activeCurrency}. Converted totals may be incomplete until rates are configured.
          </div>
        )}

        {activeDashboardTab === 'revenue' && (
          <>
        {/* Revenue Section */}
        <div className="mb-8">
      {/* No section header — the active tab above already names the section. */}
      <div className="rn-sheet">
        {/* Revenue KPI Tiles */}
      <div className="rn-joinrow grid grid-cols-2 md:grid-cols-[repeat(auto-fit,minmax(220px,1fr))]">
        <KpiTile
          label="RevPAR"
          value={formatCurrency(calculatedMetrics?.aggregated?.revPAR || 0)}
          sub={`${Math.round(calculatedMetrics?.aggregated?.totalAvailableRooms || 0)} available rooms`}
          trend={kpiTrends.revPAR}
          icon={DollarSign}
          accentColor="#FF7A00"
          onNavigate={handleNavigateToRevenue}
          {...(dashboardAlertsEnabled && {
            onAlertClick: () => handleOpenAlertModal({
              metricType: 'revpar',
              metricLabel: 'RevPAR',
              unit: '$'
            })
          })}
          comparison={isLoadingComparison ? undefined : comparisonMetrics ? {
            value: comparisonMetrics.aggregated.revPAR,
            formattedValue: formatComparisonValue(comparisonMetrics.aggregated.revPAR, 'currency'),
            percentChange: calculatePercentChange(
              calculatedMetrics?.aggregated?.revPAR || 0,
              comparisonMetrics.aggregated.revPAR
            ),
            absoluteDelta: formatAbsoluteDelta(
              calculateAbsoluteDelta(
                calculatedMetrics?.aggregated?.revPAR || 0,
                comparisonMetrics.aggregated.revPAR
              ),
              'currency'
            ),
            label: comparisonMetrics.label,
            dateRange: comparisonMetrics.dateRange
          } : null}
        />
        <KpiTile
          label="ADR"
          value={formatCurrency(calculatedMetrics?.aggregated?.adr || 0)}
          sub={`${Math.round(calculatedMetrics?.aggregated?.totalOccupiedRooms || 0)} occupied rooms (${Math.round(calculatedMetrics?.aggregated?.totalCompRooms || 0)} comp)`}
          trend={kpiTrends.adr}
          icon={TrendingUp}
          accentColor="#FF7A00"
          onNavigate={handleNavigateToRevenue}
          {...(dashboardAlertsEnabled && {
            onAlertClick: () => handleOpenAlertModal({
              metricType: 'adr',
              metricLabel: 'ADR',
              unit: '$'
            })
          })}
          comparison={isLoadingComparison ? undefined : comparisonMetrics ? {
            value: comparisonMetrics.aggregated.adr,
            formattedValue: formatComparisonValue(comparisonMetrics.aggregated.adr, 'currency'),
            percentChange: calculatePercentChange(
              calculatedMetrics?.aggregated?.adr || 0,
              comparisonMetrics.aggregated.adr
            ),
            absoluteDelta: formatAbsoluteDelta(
              calculateAbsoluteDelta(
                calculatedMetrics?.aggregated?.adr || 0,
                comparisonMetrics.aggregated.adr
              ),
              'currency'
            ),
            label: comparisonMetrics.label,
            dateRange: comparisonMetrics.dateRange
          } : null}
        />
        <KpiTile
          label="Occupancy"
          value={formatPercentage(calculatedMetrics?.aggregated?.occupancyRate || 0)}
          sub={`${Math.round(calculatedMetrics?.aggregated?.paidOccupiedRooms || 0)} / ${Math.round(calculatedMetrics?.aggregated?.totalAvailableRooms || 0)} rooms (${Math.round(calculatedMetrics?.aggregated?.totalCompRooms || 0)} comp)`}
          trend={kpiTrends.occupancyRate}
          icon={Percent}
          accentColor="#FF7A00"
          onNavigate={handleNavigateToRevenue}
          {...(dashboardAlertsEnabled && {
            onAlertClick: () => handleOpenAlertModal({
              metricType: 'occupancy',
              metricLabel: 'Occupancy Rate',
              unit: '%'
            })
          })}
          comparison={isLoadingComparison ? undefined : comparisonMetrics ? {
            value: comparisonMetrics.aggregated.occupancyRate,
            formattedValue: formatComparisonValue(comparisonMetrics.aggregated.occupancyRate, 'percentage'),
            percentChange: calculatePercentChange(
              calculatedMetrics?.aggregated?.occupancyRate || 0,
              comparisonMetrics.aggregated.occupancyRate
            ),
            absoluteDelta: formatAbsoluteDelta(
              calculateAbsoluteDelta(
                calculatedMetrics?.aggregated?.occupancyRate || 0,
                comparisonMetrics.aggregated.occupancyRate
              ),
              'percentage'
            ),
            label: comparisonMetrics.label,
            dateRange: comparisonMetrics.dateRange
          } : null}
        />
        <KpiTile
          label="Room Revenue"
          value={formatCurrencyRounded(calculatedMetrics?.aggregated?.totalRoomRevenue || 0)}
          sub={`${((calculatedMetrics?.aggregated?.totalRoomRevenue || 0) / (calculatedMetrics?.aggregated?.totalRevenue || 1) * 100).toFixed(1)}% of total revenue`}
          trend={kpiTrends.roomRevenue}
          icon={Hotel}
          accentColor="#FF7A00"
          onNavigate={handleNavigateToRevenue}
          {...(dashboardAlertsEnabled && {
            onAlertClick: () => handleOpenAlertModal({
              metricType: 'roomRevenue',
              metricLabel: 'Room Revenue',
              unit: '$'
            })
          })}
          comparison={isLoadingComparison ? undefined : comparisonMetrics ? {
            value: comparisonMetrics.aggregated.totalRoomRevenue,
            formattedValue: formatComparisonValue(comparisonMetrics.aggregated.totalRoomRevenue, 'currency'),
            percentChange: calculatePercentChange(
              calculatedMetrics?.aggregated?.totalRoomRevenue || 0,
              comparisonMetrics.aggregated.totalRoomRevenue
            ),
            absoluteDelta: formatAbsoluteDelta(
              calculateAbsoluteDelta(
                calculatedMetrics?.aggregated?.totalRoomRevenue || 0,
                comparisonMetrics.aggregated.totalRoomRevenue
              ),
              'currency'
            ),
            label: comparisonMetrics.label,
            dateRange: comparisonMetrics.dateRange
          } : null}
        />
        <KpiTile
          label="Total Revenue"
          value={formatCurrencyRounded(calculatedMetrics?.aggregated?.totalRevenue || 0)}
          sub={`${dateRange.start} to ${dateRange.end}`}
          trend={kpiTrends.totalRevenue}
          icon={DollarSign}
          accentColor="#FF7A00"
          onNavigate={handleNavigateToRevenue}
          {...(dashboardAlertsEnabled && {
            onAlertClick: () => handleOpenAlertModal({
              metricType: 'totalRevenue',
              metricLabel: 'Total Revenue',
              unit: '$'
            })
          })}
          comparison={isLoadingComparison ? undefined : comparisonMetrics ? {
            value: comparisonMetrics.aggregated.totalRevenue,
            formattedValue: formatComparisonValue(comparisonMetrics.aggregated.totalRevenue, 'currency'),
            percentChange: calculatePercentChange(
              calculatedMetrics?.aggregated?.totalRevenue || 0,
              comparisonMetrics.aggregated.totalRevenue
            ),
            absoluteDelta: formatAbsoluteDelta(
              calculateAbsoluteDelta(
                calculatedMetrics?.aggregated?.totalRevenue || 0,
                comparisonMetrics.aggregated.totalRevenue
              ),
              'currency'
            ),
            label: comparisonMetrics.label,
            dateRange: comparisonMetrics.dateRange
          } : null}
        />
      </div>

      {/* Z-Scan Layout: Charts */}
      <div className="rn-chartgrid grid grid-cols-1 md:grid-cols-[2fr_1fr]">
        {/* Revenue Trend Chart */}
        <Card
          title="Revenue Trend"
          right={
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 12, color: 'var(--lodge-onyx-500)', fontFamily: 'var(--font-sans)' }}>View by:</span>
              {[
                { key: 'day', label: 'Day' },
                { key: 'month', label: 'Month' },
                { key: 'year', label: 'Year' }
              ].map(option => {
                const isActive = timeAggregation === option.key;
                return (
                  <button
                    key={option.key}
                    onClick={() => setTimeAggregation(option.key)}
                    style={{
                      padding: '5px 12px',
                      fontSize: 12,
                      fontFamily: 'var(--font-sans)',
                      fontWeight: 500,
                      borderRadius: 'var(--radius-md)',
                      border: isActive ? 'none' : '1.5px solid var(--lodge-onyx-200)',
                      background: isActive ? 'var(--lodge-onyx-900)' : 'var(--lodge-white)',
                      color: isActive ? 'var(--lodge-onyx-50)' : 'var(--lodge-onyx-500)',
                      cursor: 'pointer',
                      transition: 'all var(--duration-base) var(--easing-standard)',
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.borderColor = 'var(--lodge-onyx-400)';
                        e.currentTarget.style.background = 'var(--lodge-onyx-50)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.borderColor = 'var(--lodge-onyx-200)';
                        e.currentTarget.style.background = 'var(--lodge-white)';
                      }
                    }}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          }
        >
          <div style={{ padding: isMobile ? 12 : 16 }}>
            <ChartLegend
              series={[
                { key: 'roomRevenue', label: 'Room Revenue' },
                { key: 'totalRevenue', label: 'Total Revenue' },
                ...(showBenchmarkRevenue ? [{ key: 'benchmarkRevenue', label: benchmarkRevenueLabel, color: '#64748B' }] : [])
              ]}
            />
            <LineChart
              data={withSyntheticChartDates(revenueData)}
              xDataKey="xDate"
              xLabels={revenueData.map(item => item.date)}
              aspectRatio={null}
              margin={{ top: 12, right: 16, bottom: 34, left: 58 }}
              style={{ height: isMobile ? 240 : 320 }}
            >
              <Grid horizontal />
              <XAxis />
              <YAxis peak formatValue={(value) => `${sharedCurrencySymbol(activeCurrency)}${Math.round(value / 1000)}k`} />
              <Line
                dataKey="roomRevenue"
                stroke="#FF7A00"
                strokeWidth={2.5}
                curve={curveMonotoneX}
              />
              <Line
                dataKey="totalRevenue"
                stroke={CHART_COLORS.secondary}
                strokeWidth={2.5}
                curve={curveMonotoneX}
              />
              {showBenchmarkRevenue && (
                <Line
                  dataKey="benchmarkRevenue"
                  stroke="#64748B"
                  strokeWidth={2}
                  strokeDasharray="6 5"
                  curve={curveMonotoneX}
                />
              )}
              <ChartTooltip
                title={null}
                rows={(point) => {
                  const rows = [
                    { color: '#FF7A00', label: 'Room Revenue', value: sharedFormatCurrency(point.roomRevenue, activeCurrency) },
                    { color: CHART_COLORS.secondary, label: 'Total Revenue', value: sharedFormatCurrency(point.totalRevenue, activeCurrency) }
                  ];
                  if (showBenchmarkRevenue && point.benchmarkRevenue !== null && point.benchmarkRevenue !== undefined) {
                    rows.push({ color: '#64748B', label: benchmarkRevenueLabel, value: sharedFormatCurrency(point.benchmarkRevenue, activeCurrency) });
                  }
                  return rows;
                }}
              />
            </LineChart>
          </div>
        </Card>

        {/* Occupancy Bar Chart */}
        <Card title="Daily Occupancy">
          <div style={{ padding: isMobile ? 12 : 16 }}>
            <ComposedChart
              data={withSyntheticChartDates(occupancyData)}
              xDataKey="xDate"
              xLabels={occupancyData.map(item => item.day)}
              aspectRatio={null}
              margin={{ top: 12, right: 16, bottom: 34, left: 58 }}
              style={{ height: isMobile ? 240 : 320 }}
            >
              <Grid horizontal />
              <XAxis />
              <YAxis peak formatValue={(value) => `${value}%`} />
              <SeriesBar dataKey="occupancy" fill="#FF7A00" radius={4} />
              {showBenchmarkOccupancy && (
                <Line
                  dataKey="benchmarkOccupancy"
                  stroke="#64748B"
                  strokeWidth={2}
                  strokeDasharray="6 5"
                  curve={curveMonotoneX}
                />
              )}
              <ChartTooltip
                title={null}
                rows={(point) => {
                  const rows = [
                    { color: '#FF7A00', label: 'Occupancy', value: `${Number(point.occupancy).toFixed(2)}%` }
                  ];
                  if (point.benchmarkOccupancy !== null && point.benchmarkOccupancy !== undefined) {
                    rows.push({ color: '#64748B', label: `${comparisonTableLabel} Occupancy`, value: `${Number(point.benchmarkOccupancy).toFixed(2)}%` });
                  }
                  return rows;
                }}
              />
            </ComposedChart>
          </div>
        </Card>

      </div>

      {/* Revenue Performance Table - Full Width */}
      <PerformanceSummary
        title="Performance Summary"
        subtitle={comparisonIsNone ? 'Per-property revenue, occupancy, and rate' : `Per-property revenue, occupancy, and rate vs ${comparisonTableLabel}`}
        columns={REVENUE_COLUMNS}
        rows={buildPerformanceRows(tableData, REVENUE_COLUMNS, {
          primaryKey: 'revPAR',
          comparisonLabel: comparisonTableLabel,
          tableSource: 'revenue',
        })}
        viewMode={performanceDeltaMode === 'absolute' ? 'value' : 'pct'}
        onViewModeChange={(mode) => setPerformanceDeltaMode(mode === 'value' ? 'absolute' : 'percent')}
        expandable
        onExpand={() => setExpandedTable('revenue')}
        onExport={() => exportToExcel(tableData, 'Performance_Summary', comparisonTableLabel)}
        cellHover={performanceCellHover}
        currency={activeCurrency}
      />
      </div>

      {/* Night Audit Alerts */}
      {nightAuditRiskEnabled && (
        <div className="mt-5 md:mt-8">
          <NightAuditAlerts />
        </div>
      )}

      </div>

          </>
        )}

      {activeDashboardTab === 'operations' && (
        <>
      {/* Operations Section */}
      <div style={{ marginBottom: 32 }}>
        {/* No section header — the active tab above already names the section. */}
        <div className="rn-sheet">
        {/* Operations KPI Tiles */}
        <div
          className="rn-joinrow"
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile
              ? 'repeat(2, 1fr)'
              : 'repeat(auto-fit, minmax(220px, 1fr))',
          }}
        >
          <KpiTile
            label="Hours Per Occupied Room"
            value={calculatedMetrics?.aggregated?.hoursPerOccupiedRoom?.toFixed(2) || '0'}
            sub={`${calculatedMetrics?.aggregated?.totalHoursWorked?.toFixed(0) || 0} total hours worked`}
            trend={kpiTrends.hoursPerOccupiedRoom}
            icon={Clock}
            accentColor="#FF7A00"
            onNavigate={handleNavigateToOperations}
            {...(dashboardAlertsEnabled && {
              onAlertClick: () => handleOpenAlertModal({
                metricType: 'hoursPerOccupiedRoom',
                metricLabel: 'Hours Per Occupied Room',
                unit: 'hrs'
              })
            })}
            comparison={isLoadingComparison ? undefined : comparisonMetrics ? {
              value: comparisonMetrics.aggregated.hoursPerOccupiedRoom,
              formattedValue: formatComparisonValue(comparisonMetrics.aggregated.hoursPerOccupiedRoom, 'number'),
              percentChange: calculatePercentChange(
                calculatedMetrics?.aggregated?.hoursPerOccupiedRoom || 0,
                comparisonMetrics.aggregated.hoursPerOccupiedRoom
              ),
              absoluteDelta: formatAbsoluteDelta(
                calculateAbsoluteDelta(
                  calculatedMetrics?.aggregated?.hoursPerOccupiedRoom || 0,
                  comparisonMetrics.aggregated.hoursPerOccupiedRoom
                ),
                'number'
              ),
              label: comparisonMetrics.label,
              dateRange: comparisonMetrics.dateRange
            } : null}
          />
          <KpiTile
            label="$ Per Occupied Room"
            value={formatCurrency(calculatedMetrics?.aggregated?.dollarPerOccupiedRoom || 0)}
            sub={`${formatCurrency(calculatedMetrics?.aggregated?.totalWages || 0)} total wages`}
            trend={kpiTrends.dollarPerOccupiedRoom}
            icon={DollarSign}
            accentColor="#FF7A00"
            onNavigate={handleNavigateToOperations}
            {...(dashboardAlertsEnabled && {
              onAlertClick: () => handleOpenAlertModal({
                metricType: 'dollarsPerOccupiedRoom',
                metricLabel: '$ Per Occupied Room',
                unit: '$'
              })
            })}
            comparison={isLoadingComparison ? undefined : comparisonMetrics ? {
              value: comparisonMetrics.aggregated.dollarPerOccupiedRoom,
              formattedValue: formatComparisonValue(comparisonMetrics.aggregated.dollarPerOccupiedRoom, 'currency'),
              percentChange: calculatePercentChange(
                calculatedMetrics?.aggregated?.dollarPerOccupiedRoom || 0,
                comparisonMetrics.aggregated.dollarPerOccupiedRoom
              ),
              absoluteDelta: formatAbsoluteDelta(
                calculateAbsoluteDelta(
                  calculatedMetrics?.aggregated?.dollarPerOccupiedRoom || 0,
                  comparisonMetrics.aggregated.dollarPerOccupiedRoom
                ),
                'currency'
              ),
              label: comparisonMetrics.label,
              dateRange: comparisonMetrics.dateRange
            } : null}
          />
          <KpiTile
            label="OT Percentage"
            value={formatPercentage(calculatedMetrics?.aggregated?.otPercentage || 0)}
            sub={`${calculatedMetrics?.aggregated?.totalOTHours?.toFixed(1) || 0} OT hours`}
            trend={kpiTrends.otPercentage}
            icon={Activity}
            accentColor="#FF7A00"
            onNavigate={handleNavigateToOperations}
            {...(dashboardAlertsEnabled && {
              onAlertClick: () => handleOpenAlertModal({
                metricType: 'otPercentage',
                metricLabel: 'OT Percentage',
                unit: '%'
              })
            })}
            comparison={isLoadingComparison ? undefined : comparisonMetrics ? {
              value: comparisonMetrics.aggregated.otPercentage,
              formattedValue: formatComparisonValue(comparisonMetrics.aggregated.otPercentage, 'percentage'),
              percentChange: calculatePercentChange(
                calculatedMetrics?.aggregated?.otPercentage || 0,
                comparisonMetrics.aggregated.otPercentage
              ),
              absoluteDelta: formatAbsoluteDelta(
                calculateAbsoluteDelta(
                  calculatedMetrics?.aggregated?.otPercentage || 0,
                  comparisonMetrics.aggregated.otPercentage
                ),
                'percentage'
              ),
              label: comparisonMetrics.label,
              dateRange: comparisonMetrics.dateRange
            } : null}
          />
        </div>

        {/* Operations Chart */}
        <div>
          <Card title="Labor Hours Analysis">
            <div style={{ padding: 16 }}>
              <ChartLegend
                series={[
                  { key: 'hoursWorked', label: 'Hours Worked', color: isDarkMode ? '#ffffff' : '#fb923c' },
                  { key: 'budgetedHours', label: 'Budgeted Hours' }
                ]}
              />
              <ComposedChart
                data={withSyntheticChartDates(chartData)}
                xDataKey="xDate"
                xLabels={chartData.map(item => item.date)}
                aspectRatio={null}
                margin={{ top: 12, right: 16, bottom: 34, left: 58 }}
                style={{ height: 320 }}
              >
                <Grid horizontal />
                <XAxis />
                <YAxis peak />
                <SeriesBar dataKey="hoursWorked" fill="url(#hoursGradient)" radius={12} />
                <Line
                  dataKey="budgetedHours"
                  stroke={CHART_COLORS.secondary}
                  strokeWidth={2}
                  curve={curveMonotoneX}
                />
                <defs>
                  <linearGradient id="hoursGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CHART_COLORS.primary} />
                    <stop offset="100%" stopColor={CHART_COLORS.primaryLight} />
                  </linearGradient>
                </defs>
                <ChartTooltip
                  title={null}
                  rows={(point) => [
                    { color: isDarkMode ? '#ffffff' : '#fb923c', label: 'Hours Worked', value: `${Number(point.hoursWorked || 0).toFixed(2)} hours` },
                    { color: CHART_COLORS.secondary, label: 'Budgeted Hours', value: `${Number(point.budgetedHours || 0).toFixed(2)} hours` }
                  ]}
                />
              </ComposedChart>
          </div>
        </Card>
        </div>

        {/* Operations Performance Table */}
        <PerformanceSummary
          title="Operations Performance Summary"
          subtitle="Labor, OT, HPOR, and wages per property"
          columns={OPERATIONS_COLUMNS}
          rows={buildPerformanceRows(operationsTableData, OPERATIONS_COLUMNS, {
            primaryKey: 'otPercentage',
            primaryInverted: true,
            comparisonLabel: comparisonTableLabel,
            tableSource: 'operations',
          })}
          viewMode={performanceDeltaMode === 'absolute' ? 'value' : 'pct'}
          onViewModeChange={(mode) => setPerformanceDeltaMode(mode === 'value' ? 'absolute' : 'percent')}
          expandable
          onExpand={() => setExpandedTable('operations')}
          onExport={() => exportOperationsToExcel(operationsTableData)}
          cellHover={performanceCellHover}
        currency={activeCurrency}
        />
        </div>
      </div>
        </>
      )}

      {activeDashboardTab === 'financial' && (
        <>
      {/* Financial Section */}
      <div style={{ marginBottom: 32 }}>
        {/* No section header — the active tab above already names the section. */}
        <div className="rn-sheet">
        {/* Financial KPI Tiles */}
        <div
          className="rn-joinrow"
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile
              ? 'repeat(2, 1fr)'
              : 'repeat(3, 1fr)',
          }}
        >
          <KpiTile
            label="Total Revenue"
            value={formatCurrency(financialMetrics?.totalRevenue || 0)}
            sub={`${financialComparisonLabel}: ${formatCurrency(financialBenchmarkMetrics.revenue || 0)}`}
            trend={financialKpiTrends.revenue}
            icon={TrendingUp}
            accentColor="#FF7A00"
            onNavigate={handleNavigateToFinancial}
            comparison={financialKpiComparisons.revenue}
          />
          <KpiTile
            label="Total Expenses"
            value={formatCurrency(financialMetrics?.totalExpense || 0)}
            sub={`${financialComparisonLabel}: ${formatCurrency(financialBenchmarkMetrics.expense || 0)}`}
            trend={financialKpiTrends.expense}
            icon={TrendingDown}
            accentColor="#FF7A00"
            onNavigate={handleNavigateToFinancial}
            comparison={financialKpiComparisons.expense}
          />
          <KpiTile
            label="Net Income"
            value={formatCurrency(financialMetrics?.totalNetIncome || 0)}
            sub={`${financialComparisonLabel}: ${formatCurrency(financialBenchmarkMetrics.netIncome || 0)}`}
            trend={financialKpiTrends.netIncome}
            icon={DollarSign}
            accentColor="#FF7A00"
            onNavigate={handleNavigateToFinancial}
            comparison={financialKpiComparisons.netIncome}
          />
        </div>

        {/* Financial Charts Row */}
        <div
          className="rn-chartgrid"
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr',
          }}
        >
          {/* Revenue vs Expense Line Chart */}
          <Card title="Revenue vs Expenses">
            <div style={{ padding: 16 }}>
              <ChartLegend
                series={[
                  { key: 'revenue', label: 'Revenue', color: '#f97316' },
                  { key: 'expense', label: 'Expenses' },
                  { key: 'netIncome', label: 'Net Income', color: '#10b981' },
                  { key: 'comparisonRevenue', label: `${financialComparisonLabel} Revenue`, color: '#f97316' },
                  { key: 'comparisonExpense', label: `${financialComparisonLabel} Expenses` }
                ]}
              />
              <LineChart
                data={withSyntheticChartDates(financialDisplayChartData)}
                xDataKey="xDate"
                xLabels={financialDisplayChartData.map(item => item.date)}
                aspectRatio={null}
                margin={{ top: 12, right: 16, bottom: 34, left: 58 }}
                style={{ height: 320 }}
              >
                <Grid horizontal />
                <XAxis />
                <YAxis peak formatValue={(value) => `$${Math.round(value / 1000)}k`} />
                <Line
                  dataKey="revenue"
                  stroke="#f97316"
                  strokeWidth={3}
                  curve={curveMonotoneX}
                />
                <Line
                  dataKey="expense"
                  stroke={CHART_COLORS.secondary}
                  strokeWidth={3}
                  curve={curveMonotoneX}
                />
                <Line
                  dataKey="netIncome"
                  stroke="#10b981"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  curve={curveMonotoneX}
                />
                <Line
                  dataKey="comparisonRevenue"
                  stroke="#f97316"
                  strokeWidth={2}
                  strokeDasharray="4 6"
                  curve={curveMonotoneX}
                />
                <Line
                  dataKey="comparisonExpense"
                  stroke={CHART_COLORS.secondary}
                  strokeWidth={2}
                  strokeDasharray="4 6"
                  curve={curveMonotoneX}
                />
                <ChartTooltip
                  title={null}
                  rows={(point) => {
                    const rows = [
                      { color: '#f97316', label: 'Revenue', value: formatCurrency(point.revenue) },
                      { color: CHART_COLORS.secondary, label: 'Expenses', value: formatCurrency(point.expense) },
                      { color: '#10b981', label: 'Net Income', value: formatCurrency(point.netIncome) }
                    ];
                    if (point.comparisonRevenue !== null && point.comparisonRevenue !== undefined) {
                      rows.push({ color: '#f97316', label: `${financialComparisonLabel} Revenue`, value: formatCurrency(point.comparisonRevenue) });
                    }
                    if (point.comparisonExpense !== null && point.comparisonExpense !== undefined) {
                      rows.push({ color: CHART_COLORS.secondary, label: `${financialComparisonLabel} Expenses`, value: formatCurrency(point.comparisonExpense) });
                    }
                    return rows;
                  }}
                />
              </LineChart>
            </div>
          </Card>

          {/* Net Income Bar Chart */}
          <Card title="Net Income">
            <div style={{ padding: 16 }}>
              <BarChart
                data={financialDisplayChartData}
                xDataKey="date"
                aspectRatio={null}
                margin={{ top: 12, right: 16, bottom: 34, left: 58 }}
                style={{ height: 320 }}
              >
                <Grid horizontal />
                <BarXAxis />
                <YAxis peak formatValue={(value) => `$${Math.round(value / 1000)}k`} />
                <Bar dataKey="comparisonNetIncome" fill="#cbd5e1" lineCap={12} />
                <Bar dataKey="netIncome" fill="url(#netIncomeGradient)" lineCap={12} />
                <defs>
                  <linearGradient id="netIncomeGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CHART_COLORS.primary} />
                    <stop offset="100%" stopColor={CHART_COLORS.primaryLight} />
                  </linearGradient>
                </defs>
                <ChartTooltip
                  title={null}
                  rows={(point) => {
                    const rows = [];
                    if (point.comparisonNetIncome !== null && point.comparisonNetIncome !== undefined) {
                      rows.push({ color: '#cbd5e1', label: `${financialComparisonLabel} Net Income`, value: formatCurrency(point.comparisonNetIncome) });
                    }
                    rows.push({ color: isDarkMode ? '#ffffff' : '#fb923c', label: 'Net Income', value: formatCurrency(point.netIncome) });
                    return rows;
                  }}
                />
              </BarChart>
            </div>
          </Card>
        </div>

        {/* Financial Performance Table */}
        <PerformanceSummary
          title="Financial Performance Summary"
          subtitle={`Revenue, expenses, and net income vs ${financialComparisonLabel}`}
          columns={FINANCIAL_COLUMNS}
          rows={buildPerformanceRows(financialTableData, FINANCIAL_COLUMNS, {
            primaryKey: 'netIncomeVariance',
            primaryIsVariance: true,
            comparisonLabel: financialComparisonLabel,
            tableSource: 'financial',
          })}
          viewMode={performanceDeltaMode === 'absolute' ? 'value' : 'pct'}
          onViewModeChange={(mode) => setPerformanceDeltaMode(mode === 'value' ? 'absolute' : 'percent')}
          expandable
          onExpand={() => setExpandedTable('financial')}
          onExport={() => exportFinancialToExcel(financialTableData, 'Financial_Performance_Summary', financialComparisonLabel)}
          cellHover={performanceCellHover}
        currency={activeCurrency}
        />
        </div>
      </div>
        </>
      )}

      {activeDashboardTab === 'forecast' && (
        <>
          <TonightPickup
            forecastingData={forecastingData}
            comparisonData={forecastingComparisonData}
            dashboardData={dashboardData}
            forecastingMetrics={forecastingMetrics}
            calculatedMetrics={calculatedMetrics}
            locationNames={locationNames}
            selectedLocations={selectedLocations}
            lastUpdated={lastUpdated}
            referenceDate={forecastReferenceDate}
            forecastWindow={forecastWindow}
            forecastComparison={selectedForecastComparisonMode}
            currency={activeCurrency}
          >
            {/* Nested so the pickup table joins the same sheet as the
                tiles and pace chart. */}
            <ForecastBreakdown
              forecastingData={forecastingData}
              dashboardData={dashboardData}
              locationNames={locationNames}
              selectedLocations={selectedLocations}
              getLocationDisplayName={getLocationDisplayName}
              referenceDate={forecastReferenceDate}
              forecastWindow={forecastWindow}
              currency={activeCurrency}
            />
          </TonightPickup>
        </>
      )}
        </div>
      </div>

      {performanceTooltip && createPortal(
        <div
          style={{
            position: 'fixed',
            ...getPerformanceTooltipStyle(performanceTooltip),
            zIndex: 12000,
            pointerEvents: 'none',
            padding: '10px 12px',
            borderRadius: 10,
            border: '1px solid var(--lodge-onyx-200)',
            // theme-aware surface: the onyx text tokens invert in dark mode,
            // so a hardcoded white card left near-white text unreadable
            background: 'color-mix(in srgb, var(--lodge-white, #ffffff) 96%, transparent)',
            boxShadow: isDarkMode ? '0 12px 30px rgba(0, 0, 0, 0.5)' : '0 12px 30px rgba(15, 15, 17, 0.16)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            color: 'var(--lodge-onyx-800)',
            fontFamily: 'var(--font-sans)'
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
              marginBottom: 6
            }}
          >
            <div
              style={{
                fontSize: 12,
                fontWeight: 800,
                letterSpacing: 0.8,
                textTransform: 'uppercase',
                color: 'var(--lodge-onyx-500)'
              }}
            >
              {performanceTooltip.metric}
            </div>
            <div
              style={{
                padding: '2px 7px',
                borderRadius: 6,
                background: 'var(--lodge-orange-50)',
                color: isDarkMode ? 'var(--lodge-orange-400)' : 'var(--lodge-orange-600)',
                fontSize: 11,
                fontWeight: 800,
                whiteSpace: 'nowrap'
              }}
            >
              {performanceTooltip.comparisonLabel}
            </div>
          </div>
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: 'var(--lodge-onyx-900)',
              marginBottom: 8,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {performanceTooltip.location}
          </div>
          {[
            ['Actual', performanceTooltip.currentValue, 'var(--lodge-onyx-900)'],
            [performanceTooltip.comparisonLabel, performanceTooltip.comparisonValue, 'var(--lodge-onyx-700)'],
            performanceTooltip.hasBenchmark
              ? [performanceTooltip.deltaModeLabel, performanceTooltip.deltaLabel, performanceTooltip.deltaColor]
              : null
          ].filter(Boolean).map(([label, value, color]) => (
            <div
              key={label}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                paddingTop: 5,
                borderTop: label === 'Actual' ? 'none' : '1px solid var(--lodge-onyx-100)'
              }}
            >
              <span style={{ fontSize: 12, color: 'var(--lodge-onyx-500)', fontWeight: 700 }}>
                {label}
              </span>
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 13,
                  fontWeight: 800,
                  fontVariantNumeric: 'tabular-nums',
                  color
                }}
              >
                {value}
              </span>
            </div>
          ))}
          {(() => {
            const series = buildSparklineSeries(performanceTooltip);
            if (!series) return null;
            return (
              <div
                style={{
                  marginTop: 8,
                  paddingTop: 8,
                  borderTop: '1px solid var(--lodge-onyx-100)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                    marginBottom: 2,
                  }}
                >
                  <span style={{ fontSize: 11, color: 'var(--lodge-onyx-500)', fontWeight: 700 }}>
                    {performanceTooltip.tableSource === 'financial' ? 'Monthly trend' : 'Daily trend'}
                  </span>
                  <span style={{ fontSize: 10, color: 'var(--lodge-onyx-400)', fontWeight: 600 }}>
                    {series.length} pts
                  </span>
                </div>
                {renderSparklineSvg(series, { inverted: !!performanceTooltip.inverted })}
                {renderSparklineLegend()}
              </div>
            );
          })()}
        </div>,
        document.body
      )}

      {/* Expanded Table Fullscreen Overlay */}
      {expandedTable && createPortal(
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
          }}
          onClick={() => setExpandedTable(null)}
        >
          <div
            style={{
              background: 'var(--lodge-white, #fff)',
              borderRadius: 'var(--radius-lg, 12px)',
              width: '95vw',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 24px',
              borderBottom: '2px solid var(--lodge-onyx-200, #e2e8f0)',
              flexShrink: 0,
            }}>
              <h3 style={{
                margin: 0,
                fontFamily: 'var(--font-sans)',
                fontSize: 18,
                fontWeight: 700,
                color: 'var(--lodge-onyx-900)',
              }}>
                {expandedTable === 'revenue' && 'Performance Summary'}
                {expandedTable === 'operations' && 'Operations Performance Summary'}
                {expandedTable === 'financial' && 'Financial Performance Summary'}
              </h3>
              <button
                onClick={() => setExpandedTable(null)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 32,
                  height: 32,
                  borderRadius: 'var(--radius-md)',
                  border: 'none',
                  background: 'var(--lodge-onyx-100)',
                  cursor: 'pointer',
                  transition: 'background var(--duration-base) var(--easing-standard)',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--lodge-onyx-200)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'var(--lodge-onyx-100)'}
              >
                <X size={18} color="var(--lodge-onyx-600)" />
              </button>
            </div>
            <div
              style={{
                padding: '16px 24px 24px',
                flex: '1 1 auto',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
              onMouseLeave={clearPerformanceTooltip}
              onPointerLeave={clearPerformanceTooltip}
              onScroll={clearPerformanceTooltip}
            >
              {expandedTable === 'revenue' && (
                <PerformanceSummary
                  title="Performance Summary"
                  subtitle={comparisonIsNone ? 'Per-property revenue, occupancy, and rate' : `Per-property revenue, occupancy, and rate vs ${comparisonTableLabel}`}
                  columns={REVENUE_COLUMNS}
                  rows={buildPerformanceRows(tableData, REVENUE_COLUMNS, {
                    primaryKey: 'revPAR',
                    comparisonLabel: comparisonTableLabel,
                    tableSource: 'revenue',
                  })}
                  viewMode={performanceDeltaMode === 'absolute' ? 'value' : 'pct'}
                  onViewModeChange={(mode) => setPerformanceDeltaMode(mode === 'value' ? 'absolute' : 'percent')}
                  expanded
                  onExport={() => exportToExcel(tableData, 'Performance_Summary', comparisonTableLabel)}
                  cellHover={performanceCellHover}
        currency={activeCurrency}
                />
              )}
              {expandedTable === 'operations' && (
                <PerformanceSummary
                  title="Operations Performance Summary"
                  subtitle="Labor, OT, HPOR, and wages per property"
                  columns={OPERATIONS_COLUMNS}
                  rows={buildPerformanceRows(operationsTableData, OPERATIONS_COLUMNS, {
                    primaryKey: 'otPercentage',
                    primaryInverted: true,
                    comparisonLabel: comparisonTableLabel,
                    tableSource: 'operations',
                  })}
                  viewMode={performanceDeltaMode === 'absolute' ? 'value' : 'pct'}
                  onViewModeChange={(mode) => setPerformanceDeltaMode(mode === 'value' ? 'absolute' : 'percent')}
                  expanded
                  onExport={() => exportOperationsToExcel(operationsTableData)}
                  cellHover={performanceCellHover}
        currency={activeCurrency}
                />
              )}
              {expandedTable === 'financial' && (
                <PerformanceSummary
                  title="Financial Performance Summary"
                  subtitle={`Revenue, expenses, and net income vs ${financialComparisonLabel}`}
                  columns={FINANCIAL_COLUMNS}
                  rows={buildPerformanceRows(financialTableData, FINANCIAL_COLUMNS, {
                    primaryKey: 'netIncomeVariance',
                    primaryIsVariance: true,
                    comparisonLabel: financialComparisonLabel,
                    tableSource: 'financial',
                  })}
                  viewMode={performanceDeltaMode === 'absolute' ? 'value' : 'pct'}
                  onViewModeChange={(mode) => setPerformanceDeltaMode(mode === 'value' ? 'absolute' : 'percent')}
                  expanded
                  onExport={() => exportFinancialToExcel(financialTableData, 'Financial_Performance_Summary', financialComparisonLabel)}
                  cellHover={performanceCellHover}
        currency={activeCurrency}
                />
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={showAlertModal}
        onClose={() => setShowAlertModal(false)}
        metricConfig={currentAlertMetric}
        selectedLocations={selectedLocations}
        availableLocations={availableLocations}
        locationNames={locationNames}
        dateRange={dateRange}
        selectedQuickDate={selectedQuickDate}
        onSave={handleSaveAlert}
      />
    </div>
  );
}
